import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { LogOut, Users, Calendar, Package, MessageSquare, Plus, Clock, TrendingUp, Settings, FileText, HelpCircle, Edit, Trash2, RefreshCw, CheckSquare, ClipboardList, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MaterialOrderForm from '@/components/MaterialOrderForm';
import StockManagementForm from '@/components/StockManagementForm';
import ClientManagement from '@/components/ClientManagement';
import { hasFeature, getCurrentPlanName } from '@/utils/features';
import { LockedFeatureButton, LockedFeatureSection } from '@/components/LockedFeature';
import { PushNotificationManager } from '@/components/PushNotificationManager';
import { NotificationCenter } from '@/components/NotificationCenter';
import { PWAInstallBanner } from '@/components/PWAInstallBanner';
import { IOSInstallBanner } from '@/components/IOSInstallBanner';
import { ShiftReminderSettings } from '@/components/ShiftReminderSettings';
import { DrivingLicensePrompt } from '@/components/DrivingLicensePrompt';
import { OnboardingTour } from '@/components/OnboardingTour';
import { HelpCenter } from '@/components/HelpCenter';
import { WelcomeGuide } from '@/components/WelcomeGuide';
import { forceAppUpdate, getAppVersion } from '@/lib/appUpdate';
import ChangePasswordDialog from '@/components/employee/ChangePasswordDialog';
import { MyEarnings } from '@/components/MyEarnings';
import { InvoiceManagement } from '@/components/admin/InvoiceManagement';
import { ScheduleManagement } from '@/components/dashboard/ScheduleManagement';
import { CompanySettingsDialog } from '@/components/dashboard/CompanySettingsDialog';
import { StatsDetailsDialog } from '@/components/dashboard/StatsDetailsDialog';
import { MobileTileGrid } from '@/components/dashboard/MobileTileGrid';
import { MyClockStatusDialog } from '@/components/dashboard/MyClockStatusDialog';
import { ClockedInNowDialog } from '@/components/dashboard/ClockedInNowDialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import {
  LayoutGrid, Calendar as CalIcon, CheckSquare as TaskIcon, ClipboardList as ProgressIcon,
  Package as PackageIcon, Briefcase, Users as UsersIcon, UserCheck, UserPlus,
  MessageSquare as MsgIcon, DollarSign, FileSpreadsheet, RefreshCcw, AlertTriangle,
  Activity, Star, Building2,
} from 'lucide-react';
import { OrderDetailsDialog } from '@/components/dashboard/OrderDetailsDialog';
import { ProjectProgressViewer } from '@/components/ProjectProgressViewer';
import { TaskManagement } from '@/components/TaskManagement';
import { ProjectCombobox } from '@/components/dashboard/ProjectCombobox';
import { ProjectComparisonDialog } from '@/components/admin/ProjectComparisonDialog';

// Shared Styles
import '@/styles/companyHeader.css';
import '@/styles/companyDashboard.css';

// Extracted Admin Components
import { ClockInCard } from '@/components/admin';
import { 
  PayrollExportDialog, 
  ExpenseExportDialog, 
  MaterialOrdersExportDialog, 
  CommunicationsExportDialog, 
  StockExportDialog 
} from '@/components/admin';
import { 
  EmployeeProfileDialog, 
  AddCertificateDialog, 
  AddLicenseDialog, 
  AddCredentialDialog 
} from '@/components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EQUIPMENT_OPTIONS = [
  'Ladder',
  'Platform Ladder',
  'Welder Machine',
  'Welder Machine Wires',
  'Banding Sealer Tool',
  'Strapping Tool',
  'Tensioner Tool',
  'Sheet Metal Bending Brake',
  'Insulation Knife',
  'Propane Torch',
  'Propane Tank',
  'Other'
];

// Custom hook for sticky scrollbar
function useStickyScrollbar(tableRef) {
  const stickyScrollRef = useRef(null);
  const [showStickyScroll, setShowStickyScroll] = useState(false);

  useEffect(() => {
    const tableElement = tableRef.current;
    
    if (!tableElement) {
      return;
    }

    // Check if sticky scrollbar should be visible
    const checkScrollbarVisibility = () => {
      const tableRect = tableElement.getBoundingClientRect();
      const isScrollable = tableElement.scrollWidth > tableElement.clientWidth;
      const isTableBelowViewport = tableRect.bottom > window.innerHeight;
      const isTableAboveViewport = tableRect.top > window.innerHeight;
      
      // Show sticky scrollbar only if table is scrollable AND extends below viewport OR is completely below viewport
      const shouldShow = isScrollable && (isTableBelowViewport || isTableAboveViewport);
      
      setShowStickyScroll(shouldShow);
    };

    // Sync scroll positions
    const syncScroll = (source, target) => {
      if (target) {
        target.scrollLeft = source.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      const stickyScrollElement = stickyScrollRef.current;
      if (stickyScrollElement) {
        syncScroll(tableElement, stickyScrollElement);
      }
    };
    
    const handleStickyScroll = () => {
      const stickyScrollElement = stickyScrollRef.current;
      if (stickyScrollElement) {
        syncScroll(stickyScrollElement, tableElement);
      }
    };

    // Add event listeners
    tableElement.addEventListener('scroll', handleTableScroll);
    window.addEventListener('scroll', checkScrollbarVisibility);
    window.addEventListener('resize', checkScrollbarVisibility);
    
    // Initial check
    checkScrollbarVisibility();

    // Cleanup function
    return () => {
      tableElement.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('scroll', checkScrollbarVisibility);
      window.removeEventListener('resize', checkScrollbarVisibility);
    };
  }, [tableRef]);

  // Separate useEffect for sticky scroll element events
  useEffect(() => {
    const stickyScrollElement = stickyScrollRef.current;
    const tableElement = tableRef.current;
    
    if (!stickyScrollElement || !tableElement) return;

    const handleStickyScroll = () => {
      tableElement.scrollLeft = stickyScrollElement.scrollLeft;
    };

    stickyScrollElement.addEventListener('scroll', handleStickyScroll);
    
    return () => {
      stickyScrollElement.removeEventListener('scroll', handleStickyScroll);
    };
  }, [showStickyScroll, tableRef]); // Re-run when sticky scroll becomes visible

  return { stickyScrollRef, showStickyScroll };
}

export default function AdminDashboard({ user, setUser }) {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [subcontractorSearchQuery, setSubcontractorSearchQuery] = useState('');
  const [projects, setProjects] = useState([]);
  const [subProjects, setSubProjects] = useState([]);
  const [showSubProjectDialog, setShowSubProjectDialog] = useState(false);
  const [showEditSubProjectDialog, setShowEditSubProjectDialog] = useState(false);
  const [selectedProjectForSubProject, setSelectedProjectForSubProject] = useState(null);
  const [editingSubProject, setEditingSubProject] = useState(null);
  const [subProjectForm, setSubProjectForm] = useState({
    name: '',
    description: '',
    budget: '',
    start_date: '',
    end_date: '',
    team_members: []
  });
  const [clients, setClients] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [messages, setMessages] = useState([]);
  const [allMessages, setAllMessages] = useState([]); // Store all messages for filtering
  const [messageDateFilter, setMessageDateFilter] = useState({ startDate: '', endDate: '' });
  const [messageTypeFilter, setMessageTypeFilter] = useState('all');
  
  // Image Viewer
  const [viewerImages, setViewerImages] = useState([]);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [equipmentQuantities, setEquipmentQuantities] = useState({}); // Track quantities for each equipment type
  const [dashboardStats, setDashboardStats] = useState({
    activeProjects: 0,
    clockedInMembers: 0,
    weeklyHours: 0,
    biWeeklyHours: 0,
    biWeeklyPay: 0,
    biWeeklyPeriodStart: null,
    biWeeklyPeriodEnd: null
  });
  
  // Clock In/Out state for office staff
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [clockInTime, setClockInTime] = useState(null);
  const [todayHours, setTodayHours] = useState(0);
  
  // Onboarding and Help state
  const [showWelcomeGuide, setShowWelcomeGuide] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [teamMemberTimeWindows, setTeamMemberTimeWindows] = useState({});
  const [pollingStatus, setPollingStatus] = useState(false);

  const [employeeForm, setEmployeeForm] = useState({
    email: '', name: '', phone: '', position: '', hourly_rate: '', role: 'employee', password: '', employee_type: 'field_based', subcontractor_account_id: ''
  });
  
  // Subcontractor accounts state
  const [subcontractorAccounts, setSubcontractorAccounts] = useState([]);
  const [showSubcontractorDialog, setShowSubcontractorDialog] = useState(false);
  const [subcontractorForm, setSubcontractorForm] = useState({
    name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: ''
  });
  const [editingSubcontractor, setEditingSubcontractor] = useState(null);

  const [projectForm, setProjectForm] = useState({
    name: '', 
    address: '', 
    start_date: '', 
    client_id: null,
    status: 'active',
    stage: 'construction', // pre_construction or construction
    equipment: [], // Format: [{type: 'Ladder', quantity: 5, other_description: ''}]
    billing_date: '',
    billing_reminder_enabled: false
  });

  const [materialForm, setMaterialForm] = useState({
    pipe_insulation: [],
    pvc_sheets: [],
    pvc_rolls: [],
    pvc_fittings: {
      elbow_90: [],
      elbow_45: [],
      end_cap: [],
      tee: [],
      victaulic_90: [],
      victaulic_45: [],
      victaulic_tee: [],
      coupling_covers: []
    },
    duct_insulation: [],
    jacketing_items: [],
    aluminum_pipe_jackets: [],
    tape_items: {},
    equipment: [],
    others_notes: ''
  });

  const [equipment, setEquipment] = useState([]);
  const [equipmentLogs, setEquipmentLogs] = useState([]);
  const [showEquipmentDialog, setShowEquipmentDialog] = useState(false);
  const [showAddEquipmentDialog, setShowAddEquipmentDialog] = useState(false);
  const [equipmentForm, setEquipmentForm] = useState({ type: '', custom_description: '', quantity: 1 });
  
  // Equipment Management (Admin)
  const [equipmentList, setEquipmentList] = useState([]);
  const [equipmentSummary, setEquipmentSummary] = useState({ total_types: 0, total_units: 0, assigned_units: 0, available_units: 0 });
  const [showEquipmentManagementDialog, setShowEquipmentManagementDialog] = useState(false);
  const [showEditEquipmentDialog, setShowEditEquipmentDialog] = useState(false);
  const [showEquipmentAssignmentsDialog, setShowEquipmentAssignmentsDialog] = useState(false);
  const [showAssignEquipmentDialog, setShowAssignEquipmentDialog] = useState(false);
  const [equipmentManagementForm, setEquipmentManagementForm] = useState({ name: '', description: '', total_quantity: 1, unit: 'units' });
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState(null);
  const [equipmentAssignments, setEquipmentAssignments] = useState([]);
  const [assignEquipmentForm, setAssignEquipmentForm] = useState({ project_id: '', quantity: 1, notes: '' });
  const [showEditProjectDialog, setShowEditProjectDialog] = useState(false);
  const [editProjectForm, setEditProjectForm] = useState({ name: '', address: '', start_date: '', client_id: null, status: 'active', stage: 'construction', shift_start: '', shift_end: '', auto_clockout_hours: '', latitude: '', longitude: '', geofence_radius: '' });
  const [coordDiscrepancy, setCoordDiscrepancy] = useState(null);
  const [verifyingCoords, setVerifyingCoords] = useState(false);

  const verifyCoordinates = async () => {
    if (!editProjectForm.address || !editProjectForm.latitude || !editProjectForm.longitude) {
      setCoordDiscrepancy(null);
      return;
    }
    setVerifyingCoords(true);
    try {
      // Clean address of any embedded coordinates
      const cleanAddr = editProjectForm.address.replace(/-?\d+\.\d+\s*,\s*-?\d+\.\d+/g, '').trim();
      if (!cleanAddr) { setCoordDiscrepancy(null); setVerifyingCoords(false); return; }
      
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cleanAddr)}&limit=1`, {
        headers: { 'User-Agent': 'WorkForcePro/1.0' }
      });
      if (resp.ok) {
        const results = await resp.json();
        if (results.length > 0) {
          const geoLat = parseFloat(results[0].lat);
          const geoLng = parseFloat(results[0].lon);
          const manLat = parseFloat(editProjectForm.latitude);
          const manLng = parseFloat(editProjectForm.longitude);
          // Haversine distance
          const R = 6371000;
          const dLat = (geoLat - manLat) * Math.PI / 180;
          const dLon = (geoLng - manLng) * Math.PI / 180;
          const a = Math.sin(dLat/2)**2 + Math.cos(manLat*Math.PI/180) * Math.cos(geoLat*Math.PI/180) * Math.sin(dLon/2)**2;
          const dist = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
          
          if (dist > 150) {
            setCoordDiscrepancy({
              distance: Math.round(dist),
              geoLat: geoLat.toFixed(5),
              geoLng: geoLng.toFixed(5),
              geoDisplay: results[0].display_name
            });
          } else {
            setCoordDiscrepancy({ distance: Math.round(dist), match: true });
          }
        } else {
          setCoordDiscrepancy({ noResult: true });
        }
      }
    } catch {
      setCoordDiscrepancy(null);
    }
    setVerifyingCoords(false);
  };

  const [materialOrders, setMaterialOrders] = useState([]);
  const [showOrderDialog, setShowOrderDialog] = useState(false);
  const [showOrderDetailsDialog, setShowOrderDetailsDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderComment, setOrderComment] = useState('');
  const [showDeliveryPhotoDialog, setShowDeliveryPhotoDialog] = useState(false);
  const [deliveryPhotos, setDeliveryPhotos] = useState([]);
  const [uploadingDeliveryPhotos, setUploadingDeliveryPhotos] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [detailsType, setDetailsType] = useState('');
  const [detailsData, setDetailsData] = useState([]);

  // Refs for sticky scrollbar
  const employeesTableRef = useRef(null);
  const projectsTableRef = useRef(null);
  const clientsTableRef = useRef(null);
  const equipmentTableRef = useRef(null);
  const stockTableRef = useRef(null);
  const ordersTableRef = useRef(null);
  const returnsTableRef = useRef(null);
  const transfersTableRef = useRef(null);

  // Use sticky scrollbar hooks
  const employeesScroll = useStickyScrollbar(employeesTableRef);
  const projectsScroll = useStickyScrollbar(projectsTableRef);
  const clientsScroll = useStickyScrollbar(clientsTableRef);
  const equipmentScroll = useStickyScrollbar(equipmentTableRef);
  const stockScroll = useStickyScrollbar(stockTableRef);
  const ordersScroll = useStickyScrollbar(ordersTableRef);
  const returnsScroll = useStickyScrollbar(returnsTableRef);
  const transfersScroll = useStickyScrollbar(transfersTableRef);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [showEditStockDialog, setShowEditStockDialog] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [stockAdjustment, setStockAdjustment] = useState(0);
  const [showStockManagementDialog, setShowStockManagementDialog] = useState(false);
  const [stockMovements, setStockMovements] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exportText, setExportText] = useState('');
  
  const [showProjectDetailsDialog, setShowProjectDetailsDialog] = useState(false);
  const [selectedProjectDetails, setSelectedProjectDetails] = useState(null);
  const [projectMessages, setProjectMessages] = useState([]);
  const [projectMessageText, setProjectMessageText] = useState('');
  const [selectedImages, setSelectedImages] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null); // Message being replied to

  // Company Settings state
  const [showCompanySettingsDialog, setShowCompanySettingsDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('schedule');
  // When set on mobile, the dashboard renders the active Tabs section as a
  // fullscreen sheet (mimicking the Material Order wizard) so the user can
  // see the section instead of having it scroll silently below the tile grid.
  const [activeMobileSheet, setActiveMobileSheet] = useState(null);
  const [showClockStatusDialog, setShowClockStatusDialog] = useState(false);
  const [showClockedInNowDialog, setShowClockedInNowDialog] = useState(false);
  const [showPersonalStatsSection, setShowPersonalStatsSection] = useState(false);
  const [showCompanyStatsSection, setShowCompanyStatsSection] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const isMobile = useIsMobile();

  // Helper: switch to a tab AND, on mobile, open the fullscreen sheet for it.
  // Tab-switching tiles call this so phone users always get a dedicated page.
  const goToTab = useCallback((key) => {
    setActiveTab(key);
    setShowPersonalStatsSection(false);
    setShowCompanyStatsSection(false);
    if (isMobile) setActiveMobileSheet(key);
  }, [isMobile]);
  const closeMobileSheet = useCallback(() => setActiveMobileSheet(null), []);
  const [showMyProfileDialog, setShowMyProfileDialog] = useState(false);
  const [companySettings, setCompanySettings] = useState({ 
    name: '', 
    tagline: '',
    logo_url: '',
    logo_base64: '',
    name_animation: 'none',
    logo_animation: 'none',
    name_color: '#1e293b',
    name_font: 'Inter',
    timezone: 'America/Toronto',
    bi_weekly_start_date: '',
    default_shift_start: '07:00',
    default_shift_end: '15:00',
    auto_clockout_hours: 10
  });
  const [logoPreviewUrl, setLogoPreviewUrl] = useState('');
  const [assigningUser, setAssigningUser] = useState(null);

  // Employee admin controls state
  const [showEditEmployeeDialog, setShowEditEmployeeDialog] = useState(false);
  const [showChangeRoleDialog, setShowChangeRoleDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editEmployeeForm, setEditEmployeeForm] = useState({ name: '', position: '', hourly_rate: '', employee_type: 'field_based', subcontractor_account_id: 'direct_hire' });
  const [newRole, setNewRole] = useState('');
  const [selectedProjectsForAssignment, setSelectedProjectsForAssignment] = useState([]);
  
  // Additional Access Management
  const [showAdditionalAccessDialog, setShowAdditionalAccessDialog] = useState(false);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);
  const [additionalAccessForm, setAdditionalAccessForm] = useState([]);
  const AVAILABLE_PERMISSIONS = [
    { key: 'view_accounts_payroll', label: 'View Accounts & Payroll' },
    { key: 'manage_projects', label: 'Manage Projects' },
    { key: 'view_all_employees', label: 'View All Employees' },
    { key: 'manage_schedules', label: 'Manage Schedules' },
    { key: 'view_reports', label: 'View Reports' },
    { key: 'manage_orders', label: 'Manage Orders' },
    { key: 'view_stock_material', label: 'View Stock Material' },
    { key: 'manage_equipment', label: 'Manage Equipment' },
    { key: 'view_pre_construction', label: 'View Pre-Construction Projects', description: 'Allow access to pre-construction stage projects' },
    { key: 'view_project_progress', label: 'View Project Progress', description: 'Allow viewing project progress reports and photos' }
  ];
  
  // Time management state
  const [showTimeResetDialog, setShowTimeResetDialog] = useState(false);
  const [showTimeAdjustDialog, setShowTimeAdjustDialog] = useState(false);
  const [timeAdjustForm, setTimeAdjustForm] = useState({ adjustment_date: '', hours: '', reason: '' });
  const [timeAdjustments, setTimeAdjustments] = useState([]);
  const [showAdjustmentsHistory, setShowAdjustmentsHistory] = useState(false);
  const [employeeDailyHours, setEmployeeDailyHours] = useState([]);
  const [loadingDailyHours, setLoadingDailyHours] = useState(false);

  const [messageText, setMessageText] = useState('');

  // Long shift alerts state
  const [longShiftAlerts, setLongShiftAlerts] = useState([]);
  const [longShiftMaxHours, setLongShiftMaxHours] = useState(10);
  const [showLongShiftAlertsDialog, setShowLongShiftAlertsDialog] = useState(false);

  // Time tracking state for purchasers
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeSummary, setTimeSummary] = useState({
    today_hours: 0,
    week_hours: 0,
    pay_period_hours: 0,
    is_clocked_in: false,
    current_shift_start: null
  });
  const [clockingIn, setClockingIn] = useState(false);

  // Cost management state
  const [showAddCostDialog, setShowAddCostDialog] = useState(false);
  const [costForm, setCostForm] = useState({
    order_cost: '',
    delivery_charges: ''
  });

  // Material Returns & Transfers
  const [materialReturns, setMaterialReturns] = useState([]);
  const [materialTransfers, setMaterialTransfers] = useState([]);
  
  // Employee Database State
  const [selectedEmployeeForProfile, setSelectedEmployeeForProfile] = useState(null);
  const [showEmployeeProfileDialog, setShowEmployeeProfileDialog] = useState(false);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [showAddCertificateDialog, setShowAddCertificateDialog] = useState(false);
  const [showAddLicenseDialog, setShowAddLicenseDialog] = useState(false);
  const [showAddCredentialDialog, setShowAddCredentialDialog] = useState(false);
  const [showVisibilitySettingsDialog, setShowVisibilitySettingsDialog] = useState(false);
  const [visibilitySettings, setVisibilitySettings] = useState({
    show_certificates_to_managers: false,
    show_licenses_to_managers: false,
    show_credentials_to_managers: false,
    show_hourly_rate_to_managers: false,
    show_ytd_hours_to_managers: true,
    show_contact_info_to_managers: true
  });
  const [newCertificate, setNewCertificate] = useState({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '', document_url: '' });
  const [newLicense, setNewLicense] = useState({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '', document_url: '' });
  const [newCredential, setNewCredential] = useState({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  
  // Excel Export Dialogs
  const [showPayrollExportDialog, setShowPayrollExportDialog] = useState(false);
  const [showProjectComparisonDialog, setShowProjectComparisonDialog] = useState(false);
  const [showExpenseExportDialog, setShowExpenseExportDialog] = useState(false);
  const [showMaterialOrdersExportDialog, setShowMaterialOrdersExportDialog] = useState(false);
  const [showCommunicationsExportDialog, setShowCommunicationsExportDialog] = useState(false);
  const [showStockExportDialog, setShowStockExportDialog] = useState(false);
  const [exportForm, setExportForm] = useState({
    start_date: '',
    end_date: '',
    project_id: ''
  });

  // Material Reconciliation State
  const [reconciliationData, setReconciliationData] = useState(null);
  const [selectedReconciliationProject, setSelectedReconciliationProject] = useState('');
  const [loadingReconciliation, setLoadingReconciliation] = useState(false);

  useEffect(() => {
    fetchAllData();
    fetchCompanySettings();
    fetchReturnsAndTransfers();
    
    // Check if we should show welcome guide (after registration)
    const shouldShowGuide = localStorage.getItem('show_welcome_guide');
    const companyName = localStorage.getItem('new_company_name');
    if (shouldShowGuide === 'true') {
      setNewCompanyName(companyName || '');
      setShowWelcomeGuide(true);
      localStorage.removeItem('show_welcome_guide');
      localStorage.removeItem('new_company_name');
    }
    
    // Fetch equipment list for company_admin
    if (user && ['company_admin', 'general_manager', 'manager'].includes(user.role)) {
      fetchEquipmentList();
    }
    
    // Fetch time tracking data and clock status for ALL users who can clock in
    if (user && ['company_admin', 'purchaser', 'accounts', 'general_manager', 'project_coordinator', 'manager', 'director', 'estimator'].includes(user.role)) {
      fetchTimeEntries();
      fetchTimeSummary();
      checkClockStatus();
    }
  }, []);

  // Sorted and filtered employees list
  const filteredEmployees = useMemo(() => {
    let result = [...employees];
    
    // Filter by search query
    if (employeeSearchQuery.trim()) {
      const query = employeeSearchQuery.toLowerCase();
      result = result.filter(emp => 
        (emp.user?.name || emp.name || '').toLowerCase().includes(query) ||
        (emp.user?.email || '').toLowerCase().includes(query) ||
        (emp.position || '').toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => {
      const nameA = (a.user?.name || a.name || '').toLowerCase();
      const nameB = (b.user?.name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return result;
  }, [employees, employeeSearchQuery]);

  // Sorted and filtered subcontractor accounts list
  const filteredSubcontractors = useMemo(() => {
    let result = [...subcontractorAccounts];
    
    // Filter by search query
    if (subcontractorSearchQuery.trim()) {
      const query = subcontractorSearchQuery.toLowerCase();
      result = result.filter(sub => 
        (sub.name || '').toLowerCase().includes(query) ||
        (sub.contact_name || '').toLowerCase().includes(query) ||
        (sub.contact_email || '').toLowerCase().includes(query)
      );
    }
    
    // Sort alphabetically by name
    result.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    
    return result;
  }, [subcontractorAccounts, subcontractorSearchQuery]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && !pollingStatus) {
      setPollingStatus(true);
      pollPaymentStatus(sessionId);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          toast.success('Payment successful! Subscription activated.');
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      const [empRes, projRes, clientsRes, matRes, msgRes, ordersRes, statsRes, equipRes, expenseRes, subcontractorRes] = await Promise.all([
        fetch(`${API_URL}/api/employees`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/projects`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/clients`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/materials`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/messages`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-orders`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/dashboard/stats`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/equipment`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/expenses`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/subcontractor-accounts`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (empRes.ok) setEmployees(await empRes.json());
      if (projRes.ok) setProjects(await projRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
      if (equipRes.ok) setEquipment(await equipRes.json());
      if (matRes.ok) setMaterials(await matRes.json());
      if (msgRes.ok) {
        const msgs = await msgRes.json();
        setMessages(msgs);
        setAllMessages(msgs); // Store for filtering
      }
      if (ordersRes.ok) setMaterialOrders(await ordersRes.json());
      if (expenseRes.ok) setExpenses(await expenseRes.json());
      if (subcontractorRes.ok) setSubcontractorAccounts(await subcontractorRes.json());
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setDashboardStats({
          activeProjects: stats.active_projects,
          clockedInMembers: stats.clocked_in_members,
          // Company totals
          weeklyHours: stats.weekly_hours,
          biWeeklyHours: stats.bi_weekly_hours || 0,
          biWeeklyPay: stats.bi_weekly_pay || 0,
          // User's own stats
          userWeeklyHours: stats.user_weekly_hours || 0,
          userBiWeeklyHours: stats.user_bi_weekly_hours || 0,
          userBiWeeklyPay: stats.user_bi_weekly_pay || 0,
          // Period info
          biWeeklyPeriodStart: stats.bi_weekly_period_start,
          biWeeklyPeriodEnd: stats.bi_weekly_period_end
        });
      }
      
      // Fetch long shift alerts (admin only)
      if (['company_admin', 'general_manager'].includes(user?.role)) {
        const longShiftRes = await fetch(`${API_URL}/api/time/long-shifts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (longShiftRes.ok) {
          const alertData = await longShiftRes.json();
          setLongShiftAlerts(alertData.alerts || []);
          if (alertData.max_hours) setLongShiftMaxHours(alertData.max_hours);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const fetchMessagesWithFilter = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/messages`;
      const params = new URLSearchParams();
      
      if (messageDateFilter.startDate) {
        params.append('start_date', messageDateFilter.startDate);
      }
      if (messageDateFilter.endDate) {
        params.append('end_date', messageDateFilter.endDate);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
        setAllMessages(data);
      }
    } catch (error) {
      console.error('Error fetching messages with filter:', error);
    }
  };

  const fetchEquipmentList = async () => {
    const token = localStorage.getItem('token');
    try {
      const [equipmentRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/api/equipment`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/equipment/summary`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (equipmentRes.ok) {
        const data = await equipmentRes.json();
        setEquipmentList(data);
      }
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setEquipmentSummary(summaryData);
      }
    } catch (error) {
      console.error('Error fetching equipment list:', error);
    }
  };

  const fetchEquipmentAssignments = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/equipment/${equipmentId}/assignments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEquipmentAssignments(data.assignments || []);
      }
    } catch (error) {
      console.error('Error fetching equipment assignments:', error);
    }
  };

  const fetchReturnsAndTransfers = async () => {
    const token = localStorage.getItem('token');
    try {
      const [returnsRes, transfersRes] = await Promise.all([
        fetch(`${API_URL}/api/material-returns`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/material-transfers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (returnsRes.ok) setMaterialReturns(await returnsRes.json());
      if (transfersRes.ok) setMaterialTransfers(await transfersRes.json());
    } catch (error) {
      console.error('Error fetching returns/transfers:', error);
    }
  };

  // Fetch Material Reconciliation data for a project
  const fetchMaterialReconciliation = async (projectId) => {
    if (!projectId) {
      setReconciliationData(null);
      return;
    }
    
    const token = localStorage.getItem('token');
    setLoadingReconciliation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/material-reconciliation`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setReconciliationData(data);
      } else {
        toast.error('Failed to load reconciliation data');
        setReconciliationData(null);
      }
    } catch (error) {
      console.error('Error fetching reconciliation:', error);
      toast.error('Connection error');
    } finally {
      setLoadingReconciliation(false);
    }
  };

  // Export reconciliation as Excel
  const handleExportReconciliation = async (projectId) => {
    if (!projectId) {
      toast.error('Please select a project first');
      return;
    }
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-reconciliation/export/${projectId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `material_reconciliation_${projectId.substring(0, 8)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Excel file downloaded');
      } else {
        toast.error('Failed to export reconciliation');
      }
    } catch (error) {
      console.error('Error exporting reconciliation:', error);
      toast.error('Connection error');
    }
  };

  const handleReviewReturn = async (returnId, status, rejectionReason = null) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-returns/${returnId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, rejection_reason: rejectionReason })
      });

      if (response.ok) {
        toast.success(`Return request ${status}`);
        fetchReturnsAndTransfers();
        fetchAllData(); // Refresh stock data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to review return');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleReviewTransfer = async (transferId, status, rejectionReason = null) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-transfers/${transferId}/review`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status, rejection_reason: rejectionReason })
      });

      if (response.ok) {
        toast.success(`Transfer request ${status}`);
        fetchReturnsAndTransfers();
        fetchAllData(); // Refresh stock data
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to review transfer');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Clock In/Out Functions for Office Staff
  const checkClockStatus = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const entries = await response.json();
        // For admins, filter to only check their own entry for clock status
        // Only entries explicitly marked as is_own_entry: true are the current user's
        const ownEntries = entries.filter(entry => entry.is_own_entry === true);
        const activeEntry = ownEntries.find(entry => !entry.clock_out);
        
        console.log('[ClockStatus] Total entries:', entries.length, 'Own entries:', ownEntries.length, 'Active:', !!activeEntry);
        
        setIsClockedIn(!!activeEntry);
        if (activeEntry) {
          setClockInTime(new Date(activeEntry.clock_in));
        } else {
          setClockInTime(null);
        }
        
        // Fetch today's hours from backend (uses company timezone)
        try {
          const summaryRes = await fetch(`${API_URL}/api/time/summary`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (summaryRes.ok) {
            const summary = await summaryRes.json();
            // Add current running time if clocked in
            let todayTotal = summary.today_hours || 0;
            if (activeEntry) {
              const clockIn = new Date(activeEntry.clock_in);
              const now = new Date();
              todayTotal += (now - clockIn) / (1000 * 60 * 60);
            }
            setTodayHours(todayTotal);
          }
        } catch (err) {
          console.error('Error fetching time summary:', err);
        }
      }
    } catch (error) {
      console.error('Error checking clock status:', error);
    }
  };

  const handleClockIn = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-in`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: null, // Office work - no project
          latitude: null,
          longitude: null,
          address: 'Office'
        })
      });

      if (response.ok) {
        toast.success('Clocked in successfully');
        setIsClockedIn(true);
        setClockInTime(new Date());
        checkClockStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClockOut = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          latitude: null,
          longitude: null,
          address: 'Office',
          work_description: 'Office work'
        })
      });

      if (response.ok) {
        toast.success('Clocked out successfully');
        setIsClockedIn(false);
        setClockInTime(null);
        checkClockStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportPayroll = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(
        `${API_URL}/api/export/payroll?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Payroll exported successfully');
        setShowPayrollExportDialog(false);
      } else {
        toast.error('Failed to export payroll');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportProjectExpenses = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/project-expenses?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `project_expenses_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Project expenses exported successfully');
        setShowExpenseExportDialog(false);
      } else {
        toast.error('Failed to export project expenses');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportMaterialOrders = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/material-orders?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `material_orders_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Material orders exported successfully');
        setShowMaterialOrdersExportDialog(false);
      } else {
        toast.error('Failed to export material orders');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportCommunications = async () => {
    const token = localStorage.getItem('token');
    try {
      let url = `${API_URL}/api/export/project-communications?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;
      if (exportForm.project_id && exportForm.project_id !== 'all') {
        url += `&project_id=${exportForm.project_id}`;
      }

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `project_communications_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Communications exported successfully');
        setShowCommunicationsExportDialog(false);
      } else {
        toast.error('Failed to export communications');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleExportStockMovements = async () => {
    const token = localStorage.getItem('token');
    try {
      const url = `${API_URL}/api/export/stock-movements?start_date=${exportForm.start_date}&end_date=${exportForm.end_date}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `stock_movements_${exportForm.start_date}_to_${exportForm.end_date}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Stock movements exported successfully');
        setShowStockExportDialog(false);
      } else {
        toast.error('Failed to export stock movements');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEquipmentExport = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/export/equipment?include_logs=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `equipment_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Equipment report exported successfully');
      } else {
        toast.error('Failed to export equipment report');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleClientExport = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/export/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const urlBlob = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = urlBlob;
        a.download = `client_projects_report.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(urlBlob);
        toast.success('Client report exported successfully');
      } else {
        toast.error('Failed to export client report');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Employee Database Functions
  const fetchEmployeeProfile = async (employeeId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const profile = await response.json();
        setEmployeeProfile(profile);
        setShowEmployeeProfileDialog(true);
      } else {
        toast.error('Failed to fetch employee profile');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchVisibilitySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/employee-visibility`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const settings = await response.json();
        setVisibilitySettings(settings);
      }
    } catch (error) {
      console.error('Error fetching visibility settings:', error);
    }
  };

  const saveVisibilitySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/company/employee-visibility`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(visibilitySettings)
      });
      if (response.ok) {
        toast.success('Visibility settings saved');
        setShowVisibilitySettingsDialog(false);
      } else {
        toast.error('Failed to save visibility settings');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Document Upload Handler for Certificates and Licenses
  const handleDocumentUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setIsUploadingDocument(true);
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/document`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        const documentUrl = data.url || data.file_url;
        
        if (type === 'certificate') {
          setNewCertificate(prev => ({ ...prev, document_url: documentUrl }));
        } else if (type === 'license') {
          setNewLicense(prev => ({ ...prev, document_url: documentUrl }));
        }
        toast.success('Document uploaded');
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('Upload error');
    } finally {
      setIsUploadingDocument(false);
    }
  };

  const addCertificate = async () => {
    if (!selectedEmployeeForProfile || !newCertificate.name || !newCertificate.document_url) {
      toast.error('Please fill required fields and upload document');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/certificates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCertificate)
      });
      if (response.ok) {
        toast.success('Certificate added');
        setShowAddCertificateDialog(false);
        setNewCertificate({ name: '', issuing_authority: '', issue_date: '', expiry_date: '', certificate_number: '', document_url: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add certificate');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteCertificate = async (employeeId, certId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/certificates/${certId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Certificate deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addLicense = async () => {
    if (!selectedEmployeeForProfile || !newLicense.license_type || !newLicense.document_url) {
      toast.error('Please fill required fields and upload document');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/licenses`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newLicense)
      });
      if (response.ok) {
        toast.success('License added');
        setShowAddLicenseDialog(false);
        setNewLicense({ license_type: '', license_number: '', state: '', issue_date: '', expiry_date: '', document_url: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add license');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteLicense = async (employeeId, licenseId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/licenses/${licenseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('License deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const addCredential = async () => {
    if (!selectedEmployeeForProfile || !newCredential.credential_type) return;
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${selectedEmployeeForProfile}/credentials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newCredential)
      });
      if (response.ok) {
        toast.success('Credential added');
        setShowAddCredentialDialog(false);
        setNewCredential({ credential_type: '', credential_number: '', issue_date: '', expiry_date: '', notes: '' });
        fetchEmployeeProfile(selectedEmployeeForProfile);
      } else {
        toast.error('Failed to add credential');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const deleteCredential = async (employeeId, credId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/employees/${employeeId}/credentials/${credId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Credential deleted');
        fetchEmployeeProfile(employeeId);
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  
  // Helper to format date string without timezone conversion
  // When date is stored as "2026-04-08", display it as "4/8/2026" without shifting
  const formatDateDisplay = (dateString, options = {}) => {
    if (!dateString) return '';
    // Parse the date string directly without timezone conversion
    const parts = dateString.split('T')[0].split('-');
    if (parts.length !== 3) return dateString;
    const [year, month, day] = parts;
    
    // If options include weekday, format as "Wednesday, Apr 8, 2026"
    if (options.weekday) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      // Create date at noon UTC to avoid timezone shift
      const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
      const weekday = weekdays[date.getUTCDay()];
      const monthName = months[parseInt(month) - 1];
      return `${weekday}, ${monthName} ${parseInt(day)}, ${year}`;
    }
    
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const fetchTimeEntries = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeEntries(data);
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
    }
  };

  const fetchTimeSummary = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/time/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTimeSummary(data);
      }
    } catch (error) {
      console.error('Error fetching time summary:', error);
    }
  };


  const handleAddEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Format the data properly for the backend
      const employeeData = {
        email: employeeForm.email,
        name: employeeForm.name,
        phone: employeeForm.phone || null,
        position: employeeForm.position,
        hourly_rate: employeeForm.hourly_rate ? parseFloat(employeeForm.hourly_rate) : null,
        role: employeeForm.role,
        password: employeeForm.password || null,
        employee_type: employeeForm.employee_type || 'field_based',
        subcontractor_account_id: employeeForm.subcontractor_account_id && employeeForm.subcontractor_account_id !== 'direct_hire' ? employeeForm.subcontractor_account_id : null
      };

      const response = await fetch(`${API_URL}/api/employees`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(employeeData)
      });

      if (response.ok) {
        toast.success('Employee added successfully');
        setShowEmployeeDialog(false);
        setEmployeeForm({ email: '', name: '', phone: '', position: '', hourly_rate: '', role: 'employee', password: '', employee_type: 'field_based', subcontractor_account_id: '' });
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Admin employee controls handlers
  const handleOpenEditEmployee = (emp) => {
    setSelectedEmployee(emp);
    setEditEmployeeForm({
      name: emp.user?.name || '',
      position: emp.position || '',
      hourly_rate: emp.hourly_rate || '',
      employee_type: emp.employee_type || 'field_based',
      subcontractor_account_id: emp.subcontractor_account_id || 'direct_hire'
    });
    setShowEditEmployeeDialog(true);
  };

  const handleEditEmployee = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Check if editing company admin's own account (only update hourly rate)
      const isEditingOwnAdminAccount = selectedEmployee?.user?.role === 'company_admin' && selectedEmployee?.user_id === user.id;
      
      // Build the update payload
      const updatePayload = isEditingOwnAdminAccount 
        ? { hourly_rate: parseFloat(editEmployeeForm.hourly_rate) || 0 }
        : {
            name: editEmployeeForm.name,
            position: editEmployeeForm.position,
            hourly_rate: parseFloat(editEmployeeForm.hourly_rate) || 0
          };
      
      // Update basic employee details
      const response = await fetch(`${API_URL}/api/admin/employees/${selectedEmployee.id}/details`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        // Only update employee type and subcontractor account if NOT editing own admin account
        if (!isEditingOwnAdminAccount) {
          // Also update employee type if changed
          if (editEmployeeForm.employee_type !== selectedEmployee.employee_type) {
            const typeResponse = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/type?employee_type=${editEmployeeForm.employee_type}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (!typeResponse.ok) {
              const error = await typeResponse.json();
              toast.error(error.detail || 'Failed to update employee type');
            }
          }
          
          // Update subcontractor account if changed
          const newAccountId = editEmployeeForm.subcontractor_account_id === 'direct_hire' ? null : editEmployeeForm.subcontractor_account_id;
          if (newAccountId !== selectedEmployee.subcontractor_account_id) {
            const accountResponse = await fetch(`${API_URL}/api/employees/${selectedEmployee.id}/subcontractor-account${newAccountId ? `?subcontractor_account_id=${newAccountId}` : ''}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            
            if (!accountResponse.ok) {
              const error = await accountResponse.json();
              toast.error(error.detail || 'Failed to update subcontractor account');
            }
          }
        }
        
        toast.success(isEditingOwnAdminAccount ? 'Pay rate updated successfully' : 'Employee updated successfully');
        setShowEditEmployeeDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Additional Access Management handlers
  const handleOpenAdditionalAccess = async (emp) => {
    setSelectedUserForAccess(emp);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/users/${emp.user_id}/additional-access`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAdditionalAccessForm(data.additional_access || []);
      }
    } catch (error) {
      console.error('Error fetching additional access:', error);
      setAdditionalAccessForm([]);
    }
    
    setShowAdditionalAccessDialog(true);
  };

  const handleSaveAdditionalAccess = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/users/${selectedUserForAccess.user_id}/additional-access`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ additional_access: additionalAccessForm })
      });
      
      if (response.ok) {
        toast.success('Additional access updated successfully');
        setShowAdditionalAccessDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update additional access');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permission) => {
    if (additionalAccessForm.includes(permission)) {
      setAdditionalAccessForm(additionalAccessForm.filter(p => p !== permission));
    } else {
      setAdditionalAccessForm([...additionalAccessForm, permission]);
    }
  };

  const handleOpenChangeRole = (emp) => {
    setSelectedEmployee(emp);
    setNewRole(emp.user?.role || 'employee');
    setShowChangeRoleDialog(true);
  };

  const handleChangeRole = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${selectedEmployee.id}/role`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role: newRole })
      });

      if (response.ok) {
        toast.success('Role updated successfully');
        setShowChangeRoleDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmployeeStatus = async (emp) => {
    const token = localStorage.getItem('token');
    const isPausing = emp.user?.is_active !== false;
    const action = isPausing ? 'pause' : 'activate';

    if (!confirm(`Are you sure you want to ${action} ${emp.user?.name}?`)) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${emp.id}/${action}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success(`Employee ${action}d successfully`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || `Failed to ${action} employee`);
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEmployee = async (emp) => {
    if (!confirm(`Are you sure you want to DELETE ${emp.user?.name}? This action cannot be undone.`)) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${emp.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Employee deleted successfully');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete employee');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Force Clock Out Handler
  const handleForceClockOut = async (emp) => {
    if (!confirm(`Are you sure you want to force clock out ${emp.user?.name || emp.name}? This will end their current shift.`)) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/employees/${emp.id}/force-clock-out`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`${emp.user?.name || emp.name} has been clocked out. Shift duration: ${result.hours_worked?.toFixed(2) || 'N/A'} hours`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to force clock out');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Time Management Handlers
  const handleOpenTimeReset = (emp) => {
    setSelectedEmployee(emp);
    setShowTimeResetDialog(true);
  };

  const handleResetEmployeeTime = async () => {
    if (!selectedEmployee) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/time/reset/${selectedEmployee.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTimeResetDialog(false);
        setSelectedEmployee(null);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reset time');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTimeAdjust = async (emp) => {
    setSelectedEmployee(emp);
    setTimeAdjustForm({ adjustment_date: '', hours: '', reason: '' });
    setEmployeeDailyHours([]);
    setShowTimeAdjustDialog(true);
    
    // Fetch daily hours for this employee
    const token = localStorage.getItem('token');
    setLoadingDailyHours(true);
    try {
      const response = await fetch(`${API_URL}/api/time/daily-hours/${emp.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEmployeeDailyHours(data.days || []);
      }
    } catch (error) {
      console.error('Error fetching daily hours:', error);
    } finally {
      setLoadingDailyHours(false);
    }
  };

  const handleAdjustEmployeeTime = async (e) => {
    e.preventDefault();
    if (!selectedEmployee || !timeAdjustForm.adjustment_date) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/time/adjust/${selectedEmployee.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adjustment_date: timeAdjustForm.adjustment_date,
          hours: parseFloat(timeAdjustForm.hours),
          reason: timeAdjustForm.reason
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowTimeAdjustDialog(false);
        setSelectedEmployee(null);
        setTimeAdjustForm({ adjustment_date: '', hours: '', reason: '' });
        setEmployeeDailyHours([]);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to adjust time');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAdjustmentsHistory = async (emp) => {
    setSelectedEmployee(emp);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API_URL}/api/time/adjustments/${emp.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTimeAdjustments(data);
        setShowAdjustmentsHistory(true);
      } else {
        toast.error('Failed to load adjustment history');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Equipment Management Handlers
  const handleAddEquipmentItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: equipmentManagementForm.name,
          description: equipmentManagementForm.description,
          total_quantity: parseInt(equipmentManagementForm.total_quantity) || 1,
          unit: equipmentManagementForm.unit || 'units'
        })
      });

      if (response.ok) {
        toast.success('Equipment added to pool');
        setShowEquipmentManagementDialog(false);
        setEquipmentManagementForm({ name: '', description: '', total_quantity: 1, unit: 'units' });
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditEquipment = (item) => {
    setSelectedEquipmentItem(item);
    setEquipmentManagementForm({
      name: item.name || item.type || '',
      description: item.description || item.custom_description || '',
      total_quantity: item.total_quantity || item.quantity || 1,
      unit: item.unit || 'units'
    });
    setShowEditEquipmentDialog(true);
  };

  const handleEditEquipmentItem = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${selectedEquipmentItem.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: equipmentManagementForm.name,
          description: equipmentManagementForm.description,
          total_quantity: parseInt(equipmentManagementForm.total_quantity) || 1,
          unit: equipmentManagementForm.unit || 'units'
        })
      });

      if (response.ok) {
        toast.success('Equipment updated successfully');
        setShowEditEquipmentDialog(false);
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteEquipmentItem = async (item) => {
    if (item.assigned_quantity > 0) {
      toast.error(`Cannot delete. ${item.assigned_quantity} units are currently assigned to projects.`);
      return;
    }
    if (!confirm(`Are you sure you want to delete "${item.name || item.type}"? This action cannot be undone.`)) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${item.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment deleted successfully');
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewEquipmentAssignments = async (item) => {
    setSelectedEquipmentItem(item);
    await fetchEquipmentAssignments(item.id);
    setShowEquipmentAssignmentsDialog(true);
  };

  const handleOpenAssignEquipment = (item) => {
    setSelectedEquipmentItem(item);
    setAssignEquipmentForm({ project_id: '', quantity: 1, notes: '' });
    setShowAssignEquipmentDialog(true);
  };

  const handleAssignEquipmentToProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/projects/${assignEquipmentForm.project_id}/equipment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          equipment_id: selectedEquipmentItem.id,
          quantity: parseInt(assignEquipmentForm.quantity) || 1,
          notes: assignEquipmentForm.notes
        })
      });

      if (response.ok) {
        toast.success('Equipment assigned to project');
        setShowAssignEquipmentDialog(false);
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignEquipment = async (assignmentId, projectId) => {
    if (!confirm('Return this equipment to the pool?')) return;
    
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/equipment/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Equipment returned to pool');
        if (selectedEquipmentItem) {
          await fetchEquipmentAssignments(selectedEquipmentItem.id);
        }
        fetchEquipmentList();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to return equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Format equipment with quantities for backend
      const formattedEquipment = projectForm.equipment.map(eq => ({
        type: eq.type,
        quantity: eq.quantity || 1,
        other_description: eq.other_description || ''
      }));

      const projectPayload = {
        name: projectForm.name,
        address: projectForm.address,
        start_date: projectForm.start_date,
        client_id: projectForm.client_id || null,
        status: projectForm.status || 'active',
        stage: projectForm.stage || 'construction',
        equipment: formattedEquipment,
        billing_date: projectForm.billing_date || null,
        billing_reminder_enabled: projectForm.billing_reminder_enabled || false
      };

      const response = await fetch(`${API_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectPayload)
      });

      if (response.ok) {
        toast.success('Project created successfully');
        setShowProjectDialog(false);
        setProjectForm({ 
          name: '', 
          address: '', 
          start_date: '', 
          client_id: null,
          status: 'active',
          stage: 'construction',
          equipment: [],
          billing_date: '',
          billing_reminder_enabled: false
        });
        setEquipmentQuantities({});
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignTeam = async (projectId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Build assignments with time windows
      const assignments = selectedTeamMembers.map(userId => ({
        user_id: userId,
        start_time: teamMemberTimeWindows[userId]?.start_time || null,
        end_time: teamMemberTimeWindows[userId]?.end_time || null
      }));

      const response = await fetch(`${API_URL}/api/projects/${projectId}/assign`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ assignments })
      });

      if (response.ok) {
        toast.success('Team assigned successfully');
        setShowAssignDialog(false);
        setSelectedProject(null);
        setSelectedTeamMembers([]);
        setTeamMemberTimeWindows({});
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to assign team');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const toggleTeamMember = (empId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(empId) 
        ? prev.filter(id => id !== empId)
        : [...prev, empId]
    );
  };

  const handleCreateMaterialOrder = async (orderData) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        const newOrder = await response.json();
        toast.success('Material order created successfully');
        
        // Send notification to admins and purchasers
        const project = projects.find(p => p.id === orderData.project_id);
        const notificationMessage = `New material order created for project: ${project?.name || 'Unknown'}`;
        
        try {
          await fetch(`${API_URL}/api/messages`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ message: notificationMessage })
          });
        } catch (err) {
          console.error('Failed to send notification:', err);
        }
        
        setShowOrderDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderDetailsDialog(true);
  };
  const handleUpdateOrderStatusWithPhotos = async (orderId, newStatus, photoUrls = []) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          delivery_photos: photoUrls
        })
      });

      if (response.ok) {
        toast.success('Order status updated');
        setShowDeliveryPhotoDialog(false);
        setDeliveryPhotos([]);
        fetchAllData();
        if (selectedOrder) {
          const updatedOrder = await fetch(`${API_URL}/api/material-orders/${orderId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (updatedOrder.ok) {
            setSelectedOrder(await updatedOrder.json());
          }
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleMarkReceivedAtSite = async () => {
    if (!selectedOrder) return;
    
    // If photos are selected, upload them first
    if (deliveryPhotos.length > 0) {
      setUploadingDeliveryPhotos(true);
      const token = localStorage.getItem('token');
      
      try {
        const formData = new FormData();
        deliveryPhotos.forEach(file => {
          formData.append('files', file);
        });
        
        const uploadResponse = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          const photoUrls = uploadResult.image_urls;
          await handleUpdateOrderStatusWithPhotos(selectedOrder.id, 'received_at_site', photoUrls);
        } else {
          toast.error('Failed to upload photos');
        }
      } catch (error) {
        toast.error('Upload error');
      } finally {
        setUploadingDeliveryPhotos(false);
      }
    } else {
      // No photos, just update status
      await handleUpdateOrderStatusWithPhotos(selectedOrder.id, 'received_at_site', []);
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        toast.success(`Order status updated to ${newStatus}`);
        
        // Update local state
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
        
        // Refresh orders list
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update status');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Cancel order - available to accounts and purchaser roles
  const handleCancelOrder = async (orderId) => {
    if (!confirm('Are you sure you want to cancel this order?')) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: 'cancelled' })
      });

      if (response.ok) {
        toast.success('Order cancelled');
        setShowOrderDetailsDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  // Delete order - only company_admin
  const handleDeleteOrder = async (orderId) => {
    if (!confirm('Are you sure you want to permanently delete this order? This action cannot be undone.')) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Order deleted');
        setShowOrderDetailsDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrderComment = async (orderId) => {
    if (!orderComment.trim()) return;

    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: orderComment })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Comment added successfully');
        
        // Update local state with new comment
        setSelectedOrder(prev => ({
          ...prev,
          comments: [...(prev.comments || []), result.comment]
        }));
        
        setOrderComment('');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add comment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCost = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    const orderCost = parseFloat(costForm.order_cost) || 0;
    const deliveryCharges = parseFloat(costForm.delivery_charges) || 0;

    if (orderCost < 0 || deliveryCharges < 0) {
      toast.error('Cost values must be positive');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${selectedOrder.id}/cost`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          order_cost: orderCost,
          delivery_charges: deliveryCharges
        })
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Cost added successfully');
        
        // Update local state
        setSelectedOrder(prev => ({
          ...prev,
          order_cost: orderCost,
          delivery_charges: deliveryCharges,
          total_cost: result.total_cost
        }));
        
        setShowAddCostDialog(false);
        setCostForm({ order_cost: '', delivery_charges: '' });
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add cost');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/materials`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(materialForm)
      });

      if (response.ok) {
        toast.success('Material added successfully');
        
        // If equipment type is selected, also create equipment item
        if (materialForm.equipment_type && materialForm.equipment_type !== '') {
          try {
            const equipResponse = await fetch(`${API_URL}/api/equipment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                type: materialForm.equipment_type,
                custom_description: materialForm.equipment_other || ''
              })
            });
            
            if (equipResponse.ok) {
              toast.success('Equipment also added to inventory');
            }
          } catch (error) {
            console.error('Error creating equipment:', error);
          }
        }
        
        setShowMaterialDialog(false);
        setMaterialForm({ name: '', category: 'HVAC', unit: 'piece', price: '', stock_quantity: '', equipment_type: '', equipment_other: '' });
        fetchAllData();
      } else {
        toast.error('Failed to add material');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipment = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const quantity = parseInt(equipmentForm.quantity) || 1;
      let successCount = 0;

      // Create the specified quantity of equipment items
      for (let i = 0; i < quantity; i++) {
        const response = await fetch(`${API_URL}/api/equipment`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: equipmentForm.type,
            custom_description: equipmentForm.type === 'Other' ? equipmentForm.custom_description : ''
          })
        });

        if (response.ok) {
          successCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} equipment item(s) added to inventory`);
        setShowAddEquipmentDialog(false);
        setEquipmentForm({ type: '', custom_description: '', quantity: 1 });
        fetchAllData();
      } else {
        toast.error('Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnEquipment = async (equipmentId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/equipment/${equipmentId}/return`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Equipment returned to inventory');
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to return equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStock = async () => {
    if (!editingMaterial || stockAdjustment === 0) return;
    
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const newStock = editingMaterial.stock_quantity + stockAdjustment;
      const response = await fetch(`${API_URL}/api/materials/${editingMaterial.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ stock_quantity: newStock })
      });

      if (response.ok) {
        toast.success(`Stock ${stockAdjustment > 0 ? 'increased' : 'decreased'} successfully`);
        setShowEditStockDialog(false);
        setEditingMaterial(null);
        setStockAdjustment(0);
        fetchAllData();
      } else {
        toast.error('Failed to update stock');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitStockMovement = async (stockData) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/stock-movements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(stockData)
      });

      if (response.ok) {
        const movementType = stockData.movement_type === 'new_arrival' ? 'New stock added' : 'Returns processed';
        toast.success(`${movementType} successfully`);
        setShowStockManagementDialog(false);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to process stock movement');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleExpenseStatusUpdate = async (expenseId, status) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/expenses/${expenseId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Expense ${status}`);
        fetchAllData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update expense');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportOrder = async (orderId) => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/material-orders/${orderId}/export`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setExportText(data.export_text);
        setShowExportDialog(true);
      } else {
        toast.error('Failed to export order');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyExport = async () => {
    try {
      // Try modern clipboard API first
      await navigator.clipboard.writeText(exportText);
      toast.success('Copied to clipboard!');
    } catch (err) {
      // Fallback: create a temporary textarea
      try {
        const textArea = document.createElement('textarea');
        textArea.value = exportText;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        toast.success('Copied to clipboard!');
      } catch (fallbackErr) {
        // If both methods fail, just show success and let user manually copy
        toast.info('Please manually copy the text below');
      }
    }
  };

  const handleOpenAssignDialog = (employee) => {
    setAssigningUser(employee.user);
    // Load current assignments
    setSelectedProjectsForAssignment(employee.user.assigned_projects || []);
    setSelectedTeamMembers(employee.user.team_members || []);
    setShowAssignDialog(true);
  };

  const handleSaveAssignments = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      // Assign projects if applicable
      if (assigningUser.role === 'project_manager' || assigningUser.role === 'team_lead' || assigningUser.role === 'project_coordinator') {
        await fetch(`${API_URL}/api/users/${assigningUser.id}/assign-projects`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(selectedProjectsForAssignment)
        });
      }

      // Assign team members if Team Lead
      if (assigningUser.role === 'team_lead') {
        await fetch(`${API_URL}/api/users/${assigningUser.id}/assign-team`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(selectedTeamMembers)
        });
      }

      toast.success('Assignments saved successfully');
      setShowAssignDialog(false);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save assignments');
    } finally {
      setLoading(false);
    }
  };

  const handleViewProjectDetails = async (project) => {
    setSelectedProjectDetails(project);
    setSelectedProjectForSubProject(project);
    setShowProjectDetailsDialog(true);
    
    // Fetch sub-projects for this project
    fetchSubProjects(project.id);
    
    // Fetch project-specific messages
    const token = localStorage.getItem('token');
    try {
      console.log('Fetching messages for project:', project.id);
      const response = await fetch(`${API_URL}/api/projects/${project.id}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const messages = await response.json();
        console.log('Project messages loaded:', messages.length, messages);
        setProjectMessages(messages);
      } else {
        console.error('Failed to fetch messages:', response.status);
      }
    } catch (error) {
      console.error('Error fetching project messages:', error);
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editProjectForm,
          auto_clockout_hours: editProjectForm.auto_clockout_hours ? parseFloat(editProjectForm.auto_clockout_hours) : null,
          shift_start: editProjectForm.shift_start || null,
          shift_end: editProjectForm.shift_end || null,
          latitude: editProjectForm.latitude ? parseFloat(editProjectForm.latitude) : null,
          longitude: editProjectForm.longitude ? parseFloat(editProjectForm.longitude) : null,
          geofence_radius: editProjectForm.geofence_radius ? parseInt(editProjectForm.geofence_radius) : null
        })
      });

      if (response.ok) {
        toast.success('Project updated successfully');
        setShowEditProjectDialog(false);
        fetchAllData();
        // Update selected project details
        setSelectedProjectDetails({
          ...selectedProjectDetails,
          ...editProjectForm
        });
      } else {
        toast.error('Failed to update project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddEquipmentToProject = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/equipment/${equipmentId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment added to project');
        fetchAllData();
        handleViewProjectDetails(selectedProjectDetails); // Refresh project details
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  // Sub-project Management Functions
  const fetchSubProjects = async (projectId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${projectId}/sub-projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubProjects(data);
      }
    } catch (error) {
      console.error('Error fetching sub-projects:', error);
    }
  };

  const handleAddSubProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/sub-projects`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: selectedProjectForSubProject.id,
          name: subProjectForm.name,
          description: subProjectForm.description,
          budget: subProjectForm.budget ? parseFloat(subProjectForm.budget) : null,
          start_date: subProjectForm.start_date || null,
          end_date: subProjectForm.end_date || null,
          team_members: subProjectForm.team_members
        })
      });

      if (response.ok) {
        toast.success('Sub-project created successfully');
        setShowSubProjectDialog(false);
        setSubProjectForm({
          name: '',
          description: '',
          budget: '',
          start_date: '',
          end_date: '',
          team_members: []
        });
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubProject = async (subProjectId) => {
    if (!confirm('Are you sure you want to delete this sub-project?')) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/sub-projects/${subProjectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Sub-project deleted successfully');
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleEditSubProject = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/sub-projects/${editingSubProject.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: subProjectForm.name,
          description: subProjectForm.description,
          budget: subProjectForm.budget ? parseFloat(subProjectForm.budget) : null,
          start_date: subProjectForm.start_date || null,
          end_date: subProjectForm.end_date || null,
          team_members: subProjectForm.team_members
        })
      });

      if (response.ok) {
        toast.success('Sub-project updated successfully');
        setShowEditSubProjectDialog(false);
        setEditingSubProject(null);
        setSubProjectForm({
          name: '',
          description: '',
          budget: '',
          start_date: '',
          end_date: '',
          team_members: []
        });
        fetchSubProjects(selectedProjectForSubProject.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update sub-project');
      }
    } catch (error) {
      toast.error('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const openEditSubProjectDialog = (subProject) => {
    setEditingSubProject(subProject);
    setSubProjectForm({
      name: subProject.name || '',
      description: subProject.description || '',
      budget: subProject.budget || '',
      start_date: subProject.start_date || '',
      end_date: subProject.end_date || '',
      team_members: subProject.team_members || []
    });
    setShowEditSubProjectDialog(true);
  };

  const handleRemoveEquipmentFromProject = async (equipmentId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/equipment/${equipmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Equipment removed and returned to stock');
        fetchAllData();
        handleViewProjectDetails(selectedProjectDetails); // Refresh project details
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove equipment');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const fetchEquipmentLogs = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/equipment/logs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        setEquipmentLogs(await response.json());
      }
    } catch (error) {
      console.error('Error fetching equipment logs:', error);
    }
  };

  const handleSendProjectMessage = async (e) => {
    e.preventDefault();
    if (!projectMessageText.trim() || !selectedProjectDetails) return;

    const token = localStorage.getItem('token');
    setUploadingImages(true);
    
    try {
      let imageUrls = [];
      let videoUrl = null;
      
      // Upload images if any are selected
      if (selectedImages.length > 0) {
        const formData = new FormData();
        selectedImages.forEach(file => {
          formData.append('files', file);
        });
        
        const uploadResponse = await fetch(`${API_URL}/api/upload-images`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });
        
        if (uploadResponse.ok) {
          const uploadResult = await uploadResponse.json();
          imageUrls = uploadResult.image_urls;
        } else {
          toast.error('Failed to upload images');
          return;
        }
      }
      
      // Upload video if selected
      if (selectedVideo) {
        setUploadingVideo(true);
        const videoFormData = new FormData();
        videoFormData.append('file', selectedVideo);
        
        const videoUploadResponse = await fetch(`${API_URL}/api/upload-video`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: videoFormData
        });
        
        if (videoUploadResponse.ok) {
          const videoResult = await videoUploadResponse.json();
          videoUrl = videoResult.video_url;
        } else {
          const error = await videoUploadResponse.json();
          toast.error(error.detail || 'Failed to upload video');
          setUploadingVideo(false);
          return;
        }
        setUploadingVideo(false);
      }
      
      // Send message with image URLs and video URL in body
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          message: projectMessageText,
          message_type: 'project',
          project_id: selectedProjectDetails.id,
          reply_to_id: replyingTo?.id || null,
          image_urls: imageUrls,
          video_url: videoUrl
        })
      });

      if (response.ok) {
        setProjectMessageText('');
        setSelectedImages([]);
        setSelectedVideo(null);
        setReplyingTo(null);
        toast.success('Message sent');
        
        // Refresh project messages immediately
        const refreshResponse = await fetch(`${API_URL}/api/projects/${selectedProjectDetails.id}/messages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (refreshResponse.ok) {
          const updatedMessages = await refreshResponse.json();
          setProjectMessages(updatedMessages);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Connection error');
    } finally {
      setUploadingImages(false);
      setUploadingVideo(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: messageText })
      });

      if (response.ok) {
        setMessageText('');
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const handleDashboardCardClick = async (type, isPersonal = false) => {
    const token = localStorage.getItem('token');
    setDetailsLoading(true);
    setShowDetailsDialog(true);
    setDetailsType(isPersonal ? `personal-${type}` : type);
    
    try {
      let endpoint = '';
      
      if (type === 'clocked-in') {
        endpoint = '/api/dashboard/clocked-in';
      } else if (type === 'weekly-hours') {
        endpoint = isPersonal ? '/api/time/personal-weekly-breakdown' : '/api/dashboard/weekly-hours';
      } else if (type === 'biweekly-hours') {
        endpoint = isPersonal ? '/api/time/personal-weekly-breakdown?weeks=2' : '/api/dashboard/biweekly-hours';
      } else if (type === 'biweekly-payroll') {
        endpoint = isPersonal ? '/api/time/personal-weekly-breakdown?weeks=2' : '/api/dashboard/biweekly-payroll';
      }
      
      if (endpoint) {
        const response = await fetch(`${API_URL}${endpoint}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setDetailsData(data);
        } else {
          toast.error('Failed to fetch details');
          setShowDetailsDialog(false);
        }
      }
    } catch (error) {
      console.error('Dashboard card click error:', error);
      toast.error('Connection error');
      setShowDetailsDialog(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleSubscription = async (planType) => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_type: planType,
          origin_url: window.location.origin
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const fetchCompanySettings = async () => {
    const token = localStorage.getItem('token');
    try {
      // Use /api/company/branding for all users (accessible by everyone)
      const brandingResponse = await fetch(`${API_URL}/api/company/branding`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (brandingResponse.ok) {
        const branding = await brandingResponse.json();
        
        // Use base64 logo if available (persists across deployments), otherwise construct URL
        let logoUrl = branding.logo_base64 || '';
        if (!logoUrl && branding.logo_url) {
          logoUrl = branding.logo_url;
          if (logoUrl.startsWith('/uploads/') || logoUrl.startsWith('/api/uploads/')) {
            logoUrl = `${API_URL}${logoUrl}`;
          }
        }
        
        setCompanySettings({
          name: branding.name || '',
          tagline: branding.tagline || '',
          logo_url: logoUrl,
          logo_base64: branding.logo_base64 || '',
          name_animation: branding.name_animation || 'none',
          logo_animation: branding.logo_animation || 'none',
          name_color: branding.name_color || '#1e293b',
          name_font: branding.name_font || 'Inter',
          timezone: branding.timezone || 'America/Toronto'
        });
      }
      
      // Try to get full settings (only admins/managers can access this)
      const settingsResponse = await fetch(`${API_URL}/api/company/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (settingsResponse.ok) {
        const settings = await settingsResponse.json();
        setCompanySettings(prev => ({
          ...prev,
          timezone: settings.timezone || prev.timezone,
          bi_weekly_start_date: settings.bi_weekly_start_date || '',
          default_shift_start: settings.default_shift_start || '07:00',
          default_shift_end: settings.default_shift_end || '15:00',
          auto_clockout_hours: settings.auto_clockout_hours || 10
        }));
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  };

  // Calendar helper functions

  return (
    <div className="admin-dashboard modern-bg" data-testid="admin-dashboard">
      {/* Company Branding Header */}
      <div className="company-header">
        <div className="company-branding">
          <div className={`company-logo-circle logo-animation-${companySettings.logo_animation || 'none'}`}>
            {companySettings.logo_url ? (
              <img 
                src={companySettings.logo_url} 
                alt="Company logo" 
                className="company-logo-img" 
                onError={(e) => {
                  console.error('Logo failed to load:', companySettings.logo_url);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <span className="company-initials">
                {companySettings.name ? companySettings.name.substring(0, 2).toUpperCase() : 'MI'}
              </span>
            )}
          </div>
          <div className="company-info">
            <h2 
              className={`company-name name-animation-${companySettings.name_animation || 'none'}`}
              style={{ 
                color: companySettings.name_color || '#1e293b',
                fontFamily: companySettings.name_font || 'Inter',
                whiteSpace: 'nowrap'
              }}
            >
              {companySettings.name || 'Mechanical Insulation Co.'}
            </h2>
            <p className="company-tagline">
              {companySettings.tagline || 'Admin Control Center'}
            </p>
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setShowHelpCenter(true)}
            className="text-gray-600 hover:text-blue-600"
            data-testid="help-btn"
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          <NotificationCenter />
          <PushNotificationManager authToken={localStorage.getItem('token')} compact user={user} />
          <Button onClick={handleLogout} variant="outline" className="logout-btn" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* PWA Install Banner */}
      <PWAInstallBanner user={user} />

      {/* iOS Install Instructions Banner */}
      <IOSInstallBanner />

      {/* Personalized Dashboard Header */}
      <header className="dashboard-header-modern">
        <div>
          <h1 className="personalized-title" data-testid="dashboard-title">
            {user.name}'s Control Center
          </h1>
          <p className="welcome-text">
            Manage your workforce, projects, and operations.
          </p>
          <Badge variant="secondary" className="ml-0 mt-1" data-testid="plan-badge">
            {getCurrentPlanName(user)} Plan
          </Badge>
        </div>
        <div className="header-actions">
          <Button
            variant="outline"
            size="sm"
            className="action-btn-small"
            onClick={() => setShowMyProfileDialog(true)}
            data-testid="my-profile-btn"
          >
            <Users className="w-4 h-4 mr-2" />
            My Profile
          </Button>
          {user.role === 'company_admin' && (
          <Button 
            variant="outline" 
            size="sm"
            className="action-btn-small"
            onClick={() => {
              fetchCompanySettings();
              setLogoPreviewUrl(companySettings.logo_url || '');
              setShowCompanySettingsDialog(true);
            }}
          >
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </Button>
          )}
        </div>
      </header>

      {/* Long Shift Alert Banner - Admin Only */}
      {longShiftAlerts.length > 0 && ['company_admin', 'general_manager'].includes(user?.role) && (
        <div 
          className="mx-4 md:mx-8 mb-4 p-4 bg-orange-50 border-2 border-orange-300 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors"
          onClick={() => setShowLongShiftAlertsDialog(true)}
          data-testid="long-shift-alert-banner"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-orange-800">
                  ⚠️ {longShiftAlerts.length} Employee{longShiftAlerts.length > 1 ? 's' : ''} with Long Shifts
                </p>
                <p className="text-sm text-orange-600">
                  {longShiftAlerts.filter(a => a.is_critical).length > 0 
                    ? `${longShiftAlerts.filter(a => a.is_critical).length} critical (approaching ${longShiftMaxHours}hr auto clock-out)`
                    : 'Click to view details and take action'}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="border-orange-400 text-orange-700 hover:bg-orange-200">
              View All
            </Button>
          </div>
        </div>
      )}

      {/* Dashboard Overview Grid */}
      <div className="dashboard-overview" data-testid="dashboard-overview">
        {/* On mobile we hide the Active Projects, Clocked In Now and Open Orders
            cards — those tiles already live in the MobileTileGrid below.
            Only the personal Clock In/Out card stays at the top. */}
        {!isMobile && (
          <Card 
            className="stat-card clickable" 
            data-testid="active-projects-card"
            onClick={() => {
              setDetailsType('projects');
              setDetailsData(projects.filter(p => p.is_active && p.status !== 'completed'));
              setShowDetailsDialog(true);
            }}
          >
            <CardHeader>
              <CardTitle className="stat-title">Active Projects</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value">{dashboardStats.activeProjects}</div>
              <p className="stat-label">Currently Running</p>
            </CardContent>
          </Card>
        )}

        {/* Hide team hours for purchaser - show their own hours instead */}
        {!isMobile && !['purchaser', 'accounts'].includes(user.role) && (
          <>
            <Card 
              className="stat-card clickable" 
              data-testid="clocked-in-card"
              onClick={() => handleDashboardCardClick('clocked-in')}
            >
              <CardHeader>
                <CardTitle className="stat-title">Clocked In Now</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="stat-value">{dashboardStats.clockedInMembers}</div>
                <p className="stat-label">Team Members Working</p>
              </CardContent>
            </Card>
        </>
        )}

        {/* Clock In/Out Card for All Staff — kept on both desktop and mobile.
            On mobile we render a more compact variant via the `compact` prop. */}
        <ClockInCard
          isClockedIn={isClockedIn}
          clockInTime={clockInTime}
          todayHours={todayHours}
          onClockIn={handleClockIn}
          onClockOut={handleClockOut}
          compact={isMobile}
        />

        {!isMobile && (
          <Card 
            className="stat-card clickable" 
            data-testid="open-orders-card"
            onClick={() => {
              setDetailsType('open-orders');
              setDetailsLoading(false);
              setShowDetailsDialog(true);
            }}
          >
            <CardHeader>
              <CardTitle className="stat-title">Open Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="stat-value">
                {materialOrders.filter(o => o.status !== 'complete').length}
              </div>
              <p className="stat-label">
                Pending & In Progress
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Personal & Company Stats Section
          - Desktop: always visible
          - Mobile: only when user taps "Your Personal Stats" or "Company Total Stats" tile */}
      {!['purchaser', 'accounts'].includes(user.role) && (!isMobile || showPersonalStatsSection || showCompanyStatsSection) && (
      <div className="stats-comparison-section" style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
        {isMobile && (
          <div className="flex justify-end mb-2 px-3">
            <button
              onClick={() => { setShowPersonalStatsSection(false); setShowCompanyStatsSection(false); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 text-sm font-semibold rounded-full border border-purple-200"
              data-testid="stats-back-btn"
            >
              ✕ Back to menu
            </button>
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1.5rem' }}>
          
          {/* Personal Stats Group - shown on desktop always, or mobile when Personal tile tapped */}
          {(!isMobile || showPersonalStatsSection) && (
          <div className="personal-stats-group" style={{ 
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)', 
            borderRadius: '1rem', 
            padding: '1.25rem',
            border: '2px solid #10b981'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#065f46', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              👤 Your Personal Stats
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
              {/* Your Weekly Hours */}
              <Card 
                className="stat-card clickable" 
                data-testid="your-weekly-hours-card"
                onClick={() => handleDashboardCardClick('weekly-hours', true)}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#065f46', fontSize: '0.875rem' }}>Your Weekly Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#059669', fontSize: '2rem' }}>{dashboardStats.userWeeklyHours || 0}</div>
                  <p className="stat-label" style={{ color: '#047857' }}>Hours You Worked</p>
                </CardContent>
              </Card>

              {/* Your Bi-Weekly Hours */}
              {hasFeature(user, 'payroll_summary') && (
              <Card 
                className="stat-card clickable" 
                data-testid="your-bi-weekly-hours-card"
                onClick={() => handleDashboardCardClick('biweekly-hours', true)}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#065f46', fontSize: '0.875rem' }}>Your Bi-Weekly Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#059669', fontSize: '2rem' }}>{dashboardStats.userBiWeeklyHours || 0}</div>
                  <p className="stat-label" style={{ color: '#047857', fontSize: '0.75rem' }}>
                    {dashboardStats.biWeeklyPeriodStart && dashboardStats.biWeeklyPeriodEnd ? (
                      <>{formatDateDisplay(dashboardStats.biWeeklyPeriodStart)} - {formatDateDisplay(dashboardStats.biWeeklyPeriodEnd)}</>
                    ) : 'This Pay Period'}
                  </p>
                </CardContent>
              </Card>
              )}

              {/* Your Bi-Weekly Pay */}
              {hasFeature(user, 'payroll_summary') && (
              <Card 
                className="stat-card clickable" 
                data-testid="your-bi-weekly-pay-card"
                onClick={() => handleDashboardCardClick('biweekly-payroll', true)}
                style={{ background: 'white', border: '1px solid #a7f3d0' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#065f46', fontSize: '0.875rem' }}>Your Bi-Weekly Pay</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#059669', fontSize: '2rem' }}>${(dashboardStats.userBiWeeklyPay || 0).toFixed(2)}</div>
                  <p className="stat-label" style={{ color: '#047857' }}>Your Earnings</p>
                </CardContent>
              </Card>
              )}
            </div>
          </div>
          )}

          {/* Company Stats Group - shown on desktop always, or mobile when Company tile tapped */}
          {(!isMobile || showCompanyStatsSection) && (
          <div className="company-stats-group" style={{ 
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)', 
            borderRadius: '1rem', 
            padding: '1.25rem',
            border: '2px solid #3b82f6'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#1e40af', 
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              🏢 Company Total Stats
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
              {/* Company Weekly Hours */}
              <Card 
                className="stat-card clickable" 
                data-testid="weekly-hours-card"
                onClick={() => handleDashboardCardClick('weekly-hours')}
                style={{ background: 'white', border: '1px solid #93c5fd' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#1e40af', fontSize: '0.875rem' }}>Company Weekly Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#2563eb', fontSize: '2rem' }}>{dashboardStats.weeklyHours}</div>
                  <p className="stat-label" style={{ color: '#1d4ed8' }}>Total Team Hours</p>
                </CardContent>
              </Card>

              {/* Company Bi-Weekly Hours */}
              {hasFeature(user, 'payroll_summary') && (
              <Card 
                className="stat-card clickable" 
                data-testid="bi-weekly-hours-card"
                onClick={() => handleDashboardCardClick('biweekly-hours')}
                style={{ background: 'white', border: '1px solid #93c5fd' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#1e40af', fontSize: '0.875rem' }}>Company Bi-Weekly Hours</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#2563eb', fontSize: '2rem' }}>{dashboardStats.biWeeklyHours}</div>
                  <p className="stat-label" style={{ color: '#1d4ed8', fontSize: '0.75rem' }}>
                    {dashboardStats.biWeeklyPeriodStart && dashboardStats.biWeeklyPeriodEnd ? (
                      <>{formatDateDisplay(dashboardStats.biWeeklyPeriodStart)} - {formatDateDisplay(dashboardStats.biWeeklyPeriodEnd)}</>
                    ) : 'This Pay Period'}
                  </p>
                </CardContent>
              </Card>
              )}

              {/* Company Bi-Weekly Payroll */}
              {hasFeature(user, 'payroll_summary') && (
              <Card 
                className="stat-card clickable" 
                data-testid="bi-weekly-pay-card"
                onClick={() => handleDashboardCardClick('biweekly-payroll')}
                style={{ background: 'white', border: '1px solid #93c5fd' }}
              >
                <CardHeader style={{ paddingBottom: '0.5rem' }}>
                  <CardTitle className="stat-title" style={{ color: '#1e40af', fontSize: '0.875rem' }}>Company Bi-Weekly Payroll</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="stat-value" style={{ color: '#2563eb', fontSize: '2rem' }}>${dashboardStats.biWeeklyPay.toFixed(2)}</div>
                  <p className="stat-label" style={{ color: '#1d4ed8' }}>Total Payroll Cost</p>
                </CardContent>
              </Card>
              )}
            </div>
          </div>
          )}
        </div>
      </div>
      )}

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className={`dashboard-tabs ${isMobile && activeMobileSheet ? 'mobile-sheet-active' : ''}`}
      >
        {/* Fullscreen mobile sheet header — only visible when a tile has been
            tapped on mobile. Mimics the Material Order wizard back-bar. */}
        {isMobile && activeMobileSheet && (
          <div
            className="sticky top-0 z-30 bg-white border-b border-gray-200 flex items-center gap-3 px-3 py-3 shadow-sm"
            data-testid="mobile-sheet-header"
          >
            <button
              type="button"
              onClick={closeMobileSheet}
              className="inline-flex items-center gap-1 px-2 py-1.5 -ml-1 text-purple-700 active:scale-95 transition-transform"
              data-testid="mobile-sheet-back"
              aria-label="Back to menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              <span className="text-sm font-semibold">Menu</span>
            </button>
            <div className="text-base font-semibold text-gray-900 truncate flex-1">
              {(() => {
                const labels = {
                  schedule: 'Schedule', tasks: 'Tasks', 'project-progress': 'Project Progress',
                  projects: 'Projects', subcontractors: 'Subcontractors', employees: 'Employees',
                  clients: 'Clients', equipment: 'Equipment', stock: 'Stock Material',
                  communication: 'Project Messages', expenses: 'Expenses',
                  'excel-reports': 'Excel Exports', orders: 'Open Orders',
                };
                return labels[activeMobileSheet] || activeMobileSheet;
              })()}
            </div>
          </div>
        )}

        {/* Mobile Tile Grid — hidden when a sheet is open OR when one of the
            stat detail pages is showing. */}
        {isMobile && !activeMobileSheet && !showPersonalStatsSection && !showCompanyStatsSection && (
          <MobileTileGrid
            storageScope={`company-${user.role}`}
            defaultPins={['your-stats', 'quick-builder', 'clocked-in-now', 'open-orders', 'company-stats', 'schedule']}
            tiles={[
              // ROW 1 — YOU
              {
                key: 'your-stats',
                label: 'Your Personal Stats',
                hint: `${dashboardStats.userWeeklyHours || 0} hrs this week`,
                icon: <Activity className="w-5 h-5" />,
                iconBg: '#d1fae5',
                iconColor: '#047857',
                onClick: () => { setShowPersonalStatsSection(true); setShowCompanyStatsSection(false); window.scrollTo({ top: 0, behavior: 'smooth' }); },
              },
              // ROW 2 — NOW
              {
                key: 'clocked-in-now',
                label: 'Clocked In Now',
                hint: 'Live company roster',
                icon: <Users className="w-5 h-5" />,
                iconBg: '#dcfce7',
                iconColor: '#15803d',
                onClick: () => setShowClockedInNowDialog(true),
              },
              {
                key: 'open-orders',
                label: 'Open Orders',
                hint: 'Pending materials',
                badge: (materialOrders || []).filter((o) => o.status === 'pending').length || null,
                badgeColor: 'amber',
                icon: <PackageIcon className="w-5 h-5" />,
                iconBg: '#fef3c7',
                iconColor: '#b45309',
                onClick: () => goToTab('orders'),
              },
              {
                key: 'company-stats',
                label: 'Company Total Stats',
                hint: `${dashboardStats.weeklyHours || 0} hrs this week`,
                icon: <Building2 className="w-5 h-5" />,
                iconBg: '#dbeafe',
                iconColor: '#1e40af',
                onClick: () => { setShowCompanyStatsSection(true); setShowPersonalStatsSection(false); window.scrollTo({ top: 0, behavior: 'smooth' }); },
              },
              // ROW 3+ — follow original order
              {
                key: 'schedule',
                label: 'Schedule',
                icon: <CalIcon className="w-5 h-5" />,
                iconBg: '#dbeafe',
                iconColor: '#1d4ed8',
                onClick: () => goToTab('schedule'),
              },
              {
                key: 'tasks',
                label: 'Tasks',
                icon: <TaskIcon className="w-5 h-5" />,
                iconBg: '#fef3c7',
                iconColor: '#b45309',
                onClick: () => goToTab('tasks'),
              },
              ...(['company_admin', 'general_manager', 'director', 'project_manager'].includes(user.role) ? [{
                key: 'project-progress',
                label: 'Project Progress',
                icon: <ProgressIcon className="w-5 h-5" />,
                iconBg: '#e0e7ff',
                iconColor: '#4338ca',
                onClick: () => goToTab('project-progress'),
              }] : []),
              {
                key: 'projects',
                label: 'Projects',
                icon: <Briefcase className="w-5 h-5" />,
                iconBg: '#f3e8ff',
                iconColor: '#6d28d9',
                onClick: () => goToTab('projects'),
              },
              {
                key: 'subcontractors',
                label: 'Subcontractors',
                icon: <UserPlus className="w-5 h-5" />,
                iconBg: '#ccfbf1',
                iconColor: '#0f766e',
                onClick: () => goToTab('subcontractors'),
              },
              {
                key: 'employees',
                label: 'Employees',
                icon: <UsersIcon className="w-5 h-5" />,
                iconBg: '#dbeafe',
                iconColor: '#1d4ed8',
                onClick: () => goToTab('employees'),
              },
              {
                key: 'clients',
                label: 'Clients',
                icon: <Star className="w-5 h-5" />,
                iconBg: '#fce7f3',
                iconColor: '#be185d',
                onClick: () => goToTab('clients'),
              },
              ...(['company_admin', 'general_manager', 'manager'].includes(user.role) ? [{
                key: 'equipment',
                label: 'Equipment',
                icon: <PackageIcon className="w-5 h-5" />,
                iconBg: '#fee2e2',
                iconColor: '#b91c1c',
                onClick: () => goToTab('equipment'),
              }] : []),
              {
                key: 'stock',
                label: 'Stock Material',
                icon: <LayoutGrid className="w-5 h-5" />,
                iconBg: '#d1fae5',
                iconColor: '#065f46',
                onClick: () => goToTab('stock'),
              },
              {
                key: 'communication',
                label: 'Project Messages',
                icon: <MsgIcon className="w-5 h-5" />,
                iconBg: '#ede9fe',
                iconColor: '#6d28d9',
                onClick: () => goToTab('communication'),
              },
              {
                key: 'expenses',
                label: 'Expenses',
                icon: <DollarSign className="w-5 h-5" />,
                iconBg: '#fef9c3',
                iconColor: '#a16207',
                onClick: () => goToTab('expenses'),
              },
              {
                key: 'excel-reports',
                label: 'Excel Exports',
                icon: <FileSpreadsheet className="w-5 h-5" />,
                iconBg: '#dcfce7',
                iconColor: '#15803d',
                onClick: () => goToTab('excel-reports'),
              },
            ]}
          />
        )}

        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', marginBottom: '20px', display: isMobile ? 'none' : 'block' }}>
          <TabsList data-testid="tabs-list" className="tabs-list" style={{ display: 'inline-flex', minWidth: 'max-content', width: 'auto' }}>
            {/* Show for Admin, General Manager only */}
            {!['purchaser', 'accounts'].includes(user.role) && (
              <>
              <TabsTrigger value="schedule" data-testid="schedule-tab">
                <Clock className="w-4 h-4 mr-2" />
                Schedule
              </TabsTrigger>
            </>
          )}

          {/* Task Management tab */}
          {!['purchaser', 'accounts', 'accounts_purchaser'].includes(user.role) && (
            <TabsTrigger value="tasks" data-testid="tasks-tab">
              <CheckSquare className="w-4 h-4 mr-2" />
              Tasks
            </TabsTrigger>
          )}

          {/* Project Progress tab */}
          {['company_admin', 'general_manager', 'director', 'project_manager'].includes(user.role) && (
            <TabsTrigger value="project-progress" data-testid="project-progress-tab">
              <ClipboardList className="w-4 h-4 mr-2" />
              Project Progress
            </TabsTrigger>
          )}

          {(['purchaser', 'accounts', 'admin', 'company_admin', 'director', 'project_manager', 'general_manager'].includes(user.role)) && (
            <TabsTrigger value="orders" data-testid="orders-tab" disabled={!hasFeature(user, 'material_ordering')}>
              <Package className="w-4 h-4 mr-2" />
              Material Orders
              {!hasFeature(user, 'material_ordering') && <span className="ml-1">🔒</span>}
            </TabsTrigger>
          )}

          {!['purchaser', 'accounts'].includes(user.role) && (
            <>
              <TabsTrigger value="projects" data-testid="projects-tab">
                <Calendar className="w-4 h-4 mr-2" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="subcontractors" data-testid="subcontractors-tab">
                <Users className="w-4 h-4 mr-2" />
                Subcontractors
              </TabsTrigger>
              <TabsTrigger value="employees" data-testid="employees-tab">
                <Users className="w-4 h-4 mr-2" />
                Employees
              </TabsTrigger>
              <TabsTrigger value="clients" data-testid="clients-tab">
                <Users className="w-4 h-4 mr-2" />
                Clients
              </TabsTrigger>
            </>
          )}

          {/* Equipment Management - Company Admin only */}
          {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
            <TabsTrigger value="equipment" data-testid="equipment-tab" disabled={!hasFeature(user, 'equipment_management')}>
              <Package className="w-4 h-4 mr-2" />
              Equipment
              {!hasFeature(user, 'equipment_management') && <span className="ml-1">🔒</span>}
            </TabsTrigger>
          )}
          
          {/* Show for all roles (including purchaser) */}
          <TabsTrigger value="stock" data-testid="stock-tab">
            <Package className="w-4 h-4 mr-2" />
            Stock Material
          </TabsTrigger>

          {/* Show Time Tracking tab only for purchaser roles */}
          {['purchaser', 'accounts'].includes(user.role) && (
            <>
              <TabsTrigger value="time-tracking" data-testid="time-tracking-tab">
                <Clock className="w-4 h-4 mr-2" />
                Time Tracking
              </TabsTrigger>
              <TabsTrigger value="view-clients" data-testid="view-clients-tab">
                <Users className="w-4 h-4 mr-2" />
                Clients
              </TabsTrigger>
              <TabsTrigger value="view-projects" data-testid="view-projects-tab">
                <Calendar className="w-4 h-4 mr-2" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="view-employees" data-testid="view-employees-tab">
                <Users className="w-4 h-4 mr-2" />
                Employees
              </TabsTrigger>
            </>
          )}
          
          {/* Communication tab - Available for all roles including purchasers */}
          <TabsTrigger value="communication" data-testid="communication-tab">
            <MessageSquare className="w-4 h-4 mr-2" />
            Project Messages
          </TabsTrigger>

          {/* Show Expenses for Admin, Director, General Manager */}
          {!['purchaser', 'accounts'].includes(user.role) && (
            <TabsTrigger value="expenses" data-testid="expenses-tab">
              <TrendingUp className="w-4 h-4 mr-2" />
              Expenses
            </TabsTrigger>
          )}

          {/* Show Excel Exports ONLY for company_admin and accounts */}
          {(['company_admin', 'accounts', 'admin'].includes(user.role)) && (
            <TabsTrigger value="exports" data-testid="exports-tab">
              <Package className="w-4 h-4 mr-2" />
              Excel Exports
            </TabsTrigger>
          )}

          {/* Material Reconciliation - For Admin, Accounts and Purchaser roles */}
          {(['company_admin', 'accounts', 'accounts', 'admin'].includes(user.role)) && (
            <TabsTrigger value="reconciliation" data-testid="reconciliation-tab">
              <FileText className="w-4 h-4 mr-2" />
              Material Reconciliation
            </TabsTrigger>
          )}

          {/* Invoice Management - Behind invoice_ocr feature flag, for Company Admin + Accounts */}
          {(['company_admin', 'accounts', 'admin'].includes(user.role)) && hasFeature(user, 'invoice_ocr') && (
            <TabsTrigger value="invoices" data-testid="invoices-tab">
              <Receipt className="w-4 h-4 mr-2" />
              Invoice Management
            </TabsTrigger>
          )}

          {/* Employee Database - Company Admin only */}
          {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
            <TabsTrigger value="employee-database" data-testid="employee-database-tab">
              <Users className="w-4 h-4 mr-2" />
              Employee Database
            </TabsTrigger>
          )}

          {/* Material Returns & Transfers - For Purchasers & Managers */}
          {(['purchaser', 'accounts', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
            <>
              <TabsTrigger value="returns" data-testid="returns-tab" disabled={!hasFeature(user, 'material_returns')}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Return Requests
                {!hasFeature(user, 'material_returns') && <span className="ml-1">🔒</span>}
              </TabsTrigger>
              <TabsTrigger value="transfers" data-testid="transfers-tab" disabled={!hasFeature(user, 'material_transfers')}>
                <TrendingUp className="w-4 h-4 mr-2" />
                Transfer Requests
                {!hasFeature(user, 'material_transfers') && <span className="ml-1">🔒</span>}
              </TabsTrigger>
              <TabsTrigger value="notification-settings" data-testid="notification-settings-tab">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </TabsTrigger>
            </>
          )}
        </TabsList>
        </div>

        {/* Clients Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="clients" data-testid="clients-content">
          <ClientManagement />
        </TabsContent>
        )}

        {/* Employees Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="employees" data-testid="employees-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <CardTitle>Employees</CardTitle>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, email, position..."
                      value={employeeSearchQuery}
                      onChange={(e) => setEmployeeSearchQuery(e.target.value)}
                      className="w-64 pl-8"
                      data-testid="employee-search-input"
                    />
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  </div>
                  {employeeSearchQuery && (
                    <span className="text-sm text-gray-500">
                      {filteredEmployees.length} of {employees.length} employees
                    </span>
                  )}
                </div>
                <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-employee-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Employee
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-employee-dialog">
                    <DialogHeader>
                      <DialogTitle>Add New Employee</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddEmployee} className="form-grid">
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={employeeForm.name}
                          onChange={(e) => {
                            const name = e.target.value;
                            const firstName = name.split(' ')[0];
                            const autoPassword = firstName ? `${firstName}123` : '';
                            setEmployeeForm({ 
                              ...employeeForm, 
                              name: name,
                              password: autoPassword
                            });
                          }}
                          required
                          data-testid="employee-name-input"
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={employeeForm.email}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                          required
                          data-testid="employee-email-input"
                        />
                      </div>
                      <div>
                        <Label>Position</Label>
                        <Input
                          value={employeeForm.position}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                          required
                          data-testid="employee-position-input"
                        />
                      </div>
                      <div>
                        <Label>Hourly Rate</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={employeeForm.hourly_rate}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, hourly_rate: e.target.value })}
                          data-testid="employee-rate-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Login Password</Label>
                        <Input
                          type="text"
                          value={employeeForm.password}
                          onChange={(e) => setEmployeeForm({ ...employeeForm, password: e.target.value })}
                          required
                          placeholder="Auto-generated from name"
                          data-testid="employee-password-input"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Default: FirstName123 (e.g., John123). You can edit this.
                        </p>
                      </div>
                      <div className="col-span-2">
                        <Label>Role</Label>
                        <Select 
                          value={employeeForm.role} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, role: val })}
                        >
                          <SelectTrigger data-testid="employee-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general_manager">General Manager (1 per company)</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="project_manager">Project Manager</SelectItem>
                            <SelectItem value="accounts">Accounts</SelectItem>
                            <SelectItem value="purchaser">Purchaser</SelectItem>
                            <SelectItem value="team_lead">Team Lead</SelectItem>
                            <SelectItem value="project_coordinator">Project Coordinator</SelectItem>
                            <SelectItem value="employee">Employee</SelectItem>
                            <SelectItem value="subcontractor">Subcontractor</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Employee Type</Label>
                        <Select 
                          value={employeeForm.employee_type} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, employee_type: val })}
                        >
                          <SelectTrigger data-testid="new-employee-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="field_based">
                              <div className="flex items-center gap-2">
                                <span>🏗️</span>
                                <span>Field-Based (requires work progress on clock-out)</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="office_based">
                              <div className="flex items-center gap-2">
                                <span>🏢</span>
                                <span>Office-Based (no work progress required)</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label>Subcontractor Account</Label>
                        <Select 
                          value={employeeForm.subcontractor_account_id || 'direct_hire'} 
                          onValueChange={(val) => setEmployeeForm({ ...employeeForm, subcontractor_account_id: val === 'direct_hire' ? '' : val })}
                        >
                          <SelectTrigger data-testid="new-employee-account-select">
                            <SelectValue placeholder="Select account..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="direct_hire">
                              <div className="flex items-center gap-2">
                                <span>🏠</span>
                                <span>Direct Hire (No Subcontractor)</span>
                              </div>
                            </SelectItem>
                            {subcontractorAccounts.filter(a => a.is_active !== false).map(account => (
                              <SelectItem key={account.id} value={account.id}>
                                <div className="flex items-center gap-2">
                                  <span>🏢</span>
                                  <span>{account.name}</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="submit" className="col-span-2" disabled={loading} data-testid="submit-employee-btn">
                        Add Employee
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-container-with-sticky-scroll">
                <div className="table-responsive" ref={employeesTableRef}>
                  <table className="data-table" data-testid="employees-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Position</th>
                      <th>Account</th>
                      <th>Type</th>
                      <th>Role</th>
                      <th>Hourly Rate</th>
                      <th>Password</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, index) => (
                      <tr key={emp.id} data-testid={`employee-row-${index}`} className={emp.user?.is_active === false ? 'opacity-60 bg-red-50' : ''}>
                        <td>
                          <div className="flex items-center gap-2">
                            {emp.user?.name}
                            {emp.user?.is_active === false && (
                              <Badge variant="destructive" className="text-xs px-1.5 py-0">Paused</Badge>
                            )}
                          </div>
                        </td>
                        <td>{emp.user?.email}</td>
                        <td>{emp.position}</td>
                        <td>
                          <Badge 
                            variant={emp.account_name === 'Direct Hire' ? 'outline' : 'secondary'}
                            className={emp.account_name === 'Direct Hire' ? 'bg-gray-100 text-gray-700' : 'bg-purple-100 text-purple-800'}
                          >
                            {emp.account_name || 'Direct Hire'}
                          </Badge>
                        </td>
                        <td>
                          <Badge 
                            variant={emp.employee_type === 'office_based' ? 'secondary' : 'default'}
                            className={emp.employee_type === 'office_based' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}
                          >
                            {emp.employee_type === 'office_based' ? '🏢 Office' : '🏗️ Field'}
                          </Badge>
                        </td>
                        <td className="capitalize">{emp.user?.role?.replace('_', ' ')}</td>
                        <td>${emp.hourly_rate || 0}/hr</td>
                        <td>
                          {user.role === 'company_admin' ? (
                            <Select
                              value=""
                              onValueChange={async (value) => {
                                const token = localStorage.getItem('token');
                                
                                if (value === 'reset') {
                                  // Reset password - only company admin can do this
                                  if (!confirm(`Reset password for ${emp.user?.name}?\n\nA new default password will be generated.`)) {
                                    return;
                                  }
                                  
                                  try {
                                    const response = await fetch(`${API_URL}/api/users/${emp.user_id}/reset-password`, {
                                      method: 'PUT',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    if (response.ok) {
                                      const data = await response.json();
                                      alert(`Password reset successful!\n\nNew Password: ${data.new_password}\n\nPlease share this with the employee securely.`);
                                      toast.success(`Password reset completed`);
                                      fetchAllData();
                                    } else {
                                      toast.error('Failed to reset password');
                                    }
                                  } catch (error) {
                                    toast.error('Connection error');
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="w-[180px] h-9">
                                <SelectValue placeholder="••••••" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="reset">🔄 Request Password Reset</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-sm text-gray-500 italic">Contact company admin</span>
                          )}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            {emp.user && ['project_manager', 'team_lead', 'project_coordinator'].includes(emp.user.role) && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleOpenAssignDialog(emp)}
                              >
                                Assign
                              </Button>
                            )}
                            {['company_admin', 'general_manager', 'manager', 'accounts', 'accounts'].includes(user.role) && (
                              <Select onValueChange={(action) => {
                                if (action === 'edit') handleOpenEditEmployee(emp);
                                else if (action === 'role') handleOpenChangeRole(emp);
                                else if (action === 'access') handleOpenAdditionalAccess(emp);
                                else if (action === 'toggle') handleToggleEmployeeStatus(emp);
                                else if (action === 'delete') handleDeleteEmployee(emp);
                                else if (action === 'time-reset') handleOpenTimeReset(emp);
                                else if (action === 'time-adjust') handleOpenTimeAdjust(emp);
                                else if (action === 'time-history') handleViewAdjustmentsHistory(emp);
                                else if (action === 'force-clock-out') handleForceClockOut(emp);
                              }}>
                                <SelectTrigger className="w-[140px] h-9">
                                  <SelectValue placeholder="Manage..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* Edit - For all employees including company_admin (their own account) */}
                                  {(emp.user?.role !== 'company_admin' || emp.user_id === user.id) && (
                                    <SelectItem value="edit">✏️ Edit Details</SelectItem>
                                  )}
                                  {/* Change Role - Only for company_admin, general_manager, manager - NOT for company_admin employees */}
                                  {['company_admin', 'general_manager', 'manager'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="role">🔄 Change Role</SelectItem>
                                  )}
                                  {/* Manage Additional Access - Only for company_admin - NOT for company_admin employees */}
                                  {user.role === 'company_admin' && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="access">🔑 Manage Access</SelectItem>
                                  )}
                                  {/* Force Clock Out - Only for company_admin when employee is clocked in */}
                                  {user.role === 'company_admin' && emp.is_clocked_in && (
                                    <SelectItem value="force-clock-out">🚪 Force Clock Out</SelectItem>
                                  )}
                                  {/* Time Reset - Only for company_admin - NOT for company_admin employees */}
                                  {user.role === 'company_admin' && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="time-reset">🔄 Reset Hours</SelectItem>
                                  )}
                                  {/* Time Adjust - For accounts roles AND company_admin's own account */}
                                  {(['accounts', 'accounts', 'company_admin'].includes(user.role) && emp.user?.role !== 'company_admin') || 
                                   (user.role === 'company_admin' && emp.user_id === user.id) ? (
                                    <SelectItem value="time-adjust">⏱️ Adjust Hours</SelectItem>
                                  ) : null}
                                  {/* View Time Adjustment History */}
                                  {['accounts', 'accounts', 'company_admin', 'general_manager'].includes(user.role) && (
                                    <SelectItem value="time-history">📋 Hour Adjustments</SelectItem>
                                  )}
                                  {/* Pause/Activate - Only for company_admin - NOT for their own account */}
                                  {user.role === 'company_admin' && emp.user_id !== user.id && (
                                    <SelectItem value="toggle">
                                      {emp.user?.is_active ? '⏸️ Pause Account' : '▶️ Activate Account'}
                                    </SelectItem>
                                  )}
                                  {/* Delete - Only for company_admin, general_manager, manager - NOT for company_admin employees */}
                                  {['company_admin', 'general_manager', 'manager'].includes(user.role) && emp.user?.role !== 'company_admin' && (
                                    <SelectItem value="delete">🗑️ Delete</SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Sticky scrollbar at bottom of viewport */}
                {employeesScroll.showStickyScroll && (
                  <div className="sticky-scrollbar-container">
                    <div 
                      className="sticky-scrollbar-track" 
                      ref={employeesScroll.stickyScrollRef}
                    >
                      <div 
                        className="sticky-scrollbar-content"
                        style={{ width: employeesTableRef.current?.scrollWidth + 'px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Edit Employee Dialog */}
          <Dialog open={showEditEmployeeDialog} onOpenChange={setShowEditEmployeeDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {selectedEmployee?.user?.role === 'company_admin' && selectedEmployee?.user_id === user.id 
                    ? 'Edit Pay Rate' 
                    : 'Edit Employee Details'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditEmployee} className="space-y-4">
                {/* Only show Name, Position, Account, Type for non-admin employees */}
                {!(selectedEmployee?.user?.role === 'company_admin' && selectedEmployee?.user_id === user.id) && (
                  <>
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editEmployeeForm.name}
                        onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Position</Label>
                      <Input
                        value={editEmployeeForm.position}
                        onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, position: e.target.value })}
                        required
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label>Hourly Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editEmployeeForm.hourly_rate}
                    onChange={(e) => setEditEmployeeForm({ ...editEmployeeForm, hourly_rate: e.target.value })}
                  />
                  {selectedEmployee?.user?.role === 'company_admin' && selectedEmployee?.user_id === user.id && (
                    <p className="text-xs text-gray-500 mt-1">
                      Update your hourly rate for payroll calculations.
                    </p>
                  )}
                </div>
                {/* Only show Subcontractor Account and Employee Type for non-admin employees */}
                {!(selectedEmployee?.user?.role === 'company_admin' && selectedEmployee?.user_id === user.id) && (
                  <>
                    <div>
                      <Label>Subcontractor Account</Label>
                      <Select 
                        value={editEmployeeForm.subcontractor_account_id || 'direct_hire'} 
                        onValueChange={(val) => setEditEmployeeForm({ ...editEmployeeForm, subcontractor_account_id: val })}
                      >
                        <SelectTrigger data-testid="edit-employee-account-select">
                          <SelectValue placeholder="Select account..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="direct_hire">
                            <div className="flex items-center gap-2">
                              <span>🏠</span>
                              <span>Direct Hire (No Subcontractor)</span>
                            </div>
                          </SelectItem>
                          {subcontractorAccounts.filter(a => a.is_active !== false).map(account => (
                            <SelectItem key={account.id} value={account.id}>
                              <div className="flex items-center gap-2">
                                <span>🏢</span>
                                <span>{account.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Employee Type</Label>
                      <Select 
                        value={editEmployeeForm.employee_type} 
                        onValueChange={(val) => setEditEmployeeForm({ ...editEmployeeForm, employee_type: val })}
                      >
                        <SelectTrigger data-testid="employee-type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="field_based">
                            <div className="flex items-center gap-2">
                              <span>🏗️</span>
                              <span>Field-Based</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="office_based">
                            <div className="flex items-center gap-2">
                              <span>🏢</span>
                              <span>Office-Based</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">
                        {editEmployeeForm.employee_type === 'office_based' 
                          ? 'Office-based employees do not need to submit work progress when clocking out.' 
                          : 'Field-based employees must submit work progress when clocking out.'}
                      </p>
                    </div>
                  </>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditEmployeeDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Change Role Dialog */}
          <Dialog open={showChangeRoleDialog} onOpenChange={setShowChangeRoleDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Change Employee Role</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Employee: {selectedEmployee?.user?.name}</Label>
                </div>
                <div>
                  <Label>New Role</Label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general_manager">General Manager (1 per company)</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="project_manager">Project Manager</SelectItem>
                      <SelectItem value="accounts">Accounts</SelectItem>
                      <SelectItem value="purchaser">Purchaser</SelectItem>
                      <SelectItem value="team_lead">Team Lead</SelectItem>
                      <SelectItem value="project_coordinator">Project Coordinator</SelectItem>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="subcontractor">Subcontractor</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowChangeRoleDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleChangeRole} disabled={loading}>
                    Change Role
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Additional Access Dialog - Company Admin Only */}
          <Dialog open={showAdditionalAccessDialog} onOpenChange={setShowAdditionalAccessDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Manage Additional Access</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>{selectedUserForAccess?.user?.name}</strong>
                  </p>
                  <p className="text-xs text-blue-600 mt-1">
                    Primary Role: {selectedUserForAccess?.user?.role?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-3 block">Grant Additional Permissions:</Label>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {AVAILABLE_PERMISSIONS.map(perm => (
                      <label 
                        key={perm.key} 
                        className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={additionalAccessForm.includes(perm.key)}
                          onChange={() => togglePermission(perm.key)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <span className="text-sm">{perm.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => setShowAdditionalAccessDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAdditionalAccess} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Access'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Time Reset Dialog - Company Admin Only */}
          <Dialog open={showTimeResetDialog} onOpenChange={setShowTimeResetDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Reset Employee Hours</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">⚠️ Warning: This action cannot be undone!</p>
                  <p className="text-red-700 text-sm mt-2">
                    You are about to delete ALL time entries for <strong>{selectedEmployee?.user?.name}</strong>.
                    This will permanently remove their clock-in/out history.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowTimeResetDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    onClick={handleResetEmployeeTime} 
                    disabled={loading}
                    data-testid="confirm-time-reset-btn"
                  >
                    {loading ? 'Resetting...' : 'Reset All Hours'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Time Adjust Dialog - Accounts Role */}
          <Dialog open={showTimeAdjustDialog} onOpenChange={setShowTimeAdjustDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Adjust Employee Hours</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAdjustEmployeeTime} className="space-y-4">
                <div>
                  <Label>Employee</Label>
                  <p className="text-lg font-medium">{selectedEmployee?.user?.name}</p>
                </div>
                
                {/* Day Selection - Last 30 Days */}
                <div>
                  <Label className="mb-2 block">Select Day to Adjust *</Label>
                  {loadingDailyHours ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-500">Loading daily hours...</span>
                    </div>
                  ) : (
                    <div className="max-h-64 overflow-y-auto border rounded-lg">
                      {employeeDailyHours.map((day) => (
                        <div 
                          key={day.date}
                          onClick={() => setTimeAdjustForm({ ...timeAdjustForm, adjustment_date: day.date })}
                          className={`flex items-center justify-between p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                            timeAdjustForm.adjustment_date === day.date ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                          } ${day.is_today ? 'bg-green-50' : ''}`}
                          data-testid={`day-select-${day.date}`}
                        >
                          <div className="flex items-center gap-3">
                            <input 
                              type="radio" 
                              name="adjustment_date"
                              checked={timeAdjustForm.adjustment_date === day.date}
                              onChange={() => setTimeAdjustForm({ ...timeAdjustForm, adjustment_date: day.date })}
                              className="w-4 h-4 text-blue-600"
                            />
                            <div>
                              <p className="font-medium">
                                {day.day_name}
                                {day.is_today && <Badge variant="success" className="ml-2 text-xs">Today</Badge>}
                              </p>
                              <p className="text-sm text-gray-500">{formatDateDisplay(day.date)}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${day.logged_hours > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              {day.logged_hours} hrs logged
                            </p>
                            {day.adjustment_total !== 0 && (
                              <p className={`text-xs ${day.adjustment_total > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                {day.adjustment_total > 0 ? '+' : ''}{day.adjustment_total} hrs adjusted
                              </p>
                            )}
                            {day.entry_count > 0 && (
                              <p className="text-xs text-gray-400">{day.entry_count} time entries</p>
                            )}
                          </div>
                        </div>
                      ))}
                      {employeeDailyHours.length === 0 && (
                        <p className="text-center text-gray-500 py-8">No data available for the last 30 days</p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="adjust-hours">Hours to Add/Subtract *</Label>
                  <Input
                    id="adjust-hours"
                    type="number"
                    step="0.25"
                    placeholder="e.g., 2.5 to add, -1.5 to subtract"
                    value={timeAdjustForm.hours}
                    onChange={(e) => setTimeAdjustForm({ ...timeAdjustForm, hours: e.target.value })}
                    required
                    data-testid="adjust-hours-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Use positive numbers to add hours, negative to subtract
                  </p>
                </div>
                <div>
                  <Label htmlFor="adjust-reason">Reason *</Label>
                  <Textarea
                    id="adjust-reason"
                    placeholder="Explain why this adjustment is being made..."
                    value={timeAdjustForm.reason}
                    onChange={(e) => setTimeAdjustForm({ ...timeAdjustForm, reason: e.target.value })}
                    required
                    minLength={3}
                    data-testid="adjust-reason-input"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowTimeAdjustDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading || !timeAdjustForm.adjustment_date || !timeAdjustForm.hours || !timeAdjustForm.reason}
                    data-testid="submit-time-adjust-btn"
                  >
                    {loading ? 'Saving...' : 'Save Adjustment'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Time Adjustments History Dialog */}
          <Dialog open={showAdjustmentsHistory} onOpenChange={setShowAdjustmentsHistory}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Hour Adjustments History - {selectedEmployee?.user?.name}</DialogTitle>
              </DialogHeader>
              <div className="max-h-[60vh] overflow-y-auto">
                {timeAdjustments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No adjustments recorded for this employee</p>
                ) : (
                  <div className="space-y-3">
                    {timeAdjustments.map((adj) => (
                      <div key={adj.id} className="p-3 border rounded-lg">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className={`font-semibold ${adj.adjustment_hours > 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {adj.adjustment_hours > 0 ? '+' : ''}{adj.adjustment_hours} hours
                            </span>
                            {adj.adjustment_date && (
                              <Badge variant="outline" className="ml-2">
                                {formatDateDisplay(adj.adjustment_date)}
                              </Badge>
                            )}
                            <p className="text-sm text-gray-600 mt-1">{adj.reason}</p>
                          </div>
                          <div className="text-right text-sm text-gray-500">
                            <p>By: {adj.adjusted_by_name}</p>
                            <p>{new Date(adj.created_at).toLocaleDateString()} {new Date(adj.created_at).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Long Shift Alerts Dialog */}
          <Dialog open={showLongShiftAlertsDialog} onOpenChange={setShowLongShiftAlertsDialog}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  Employees with Long Active Shifts
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  These employees have been clocked in for extended hours. Employees reaching {longShiftMaxHours} hours will be automatically clocked out.
                </p>
                {longShiftAlerts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No long shifts detected</p>
                ) : (
                  <div className="space-y-2">
                    {longShiftAlerts.map((alert) => (
                      <div 
                        key={alert.time_entry_id} 
                        className={`p-4 rounded-lg border ${
                          alert.is_critical 
                            ? 'bg-red-50 border-red-300' 
                            : 'bg-orange-50 border-orange-200'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-semibold text-gray-800">{alert.employee_name}</p>
                            <p className="text-sm text-gray-600">
                              Clocked in: {new Date(alert.clock_in).toLocaleString()}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant={alert.is_critical ? "destructive" : "secondary"}>
                                {alert.hours_worked} hours
                              </Badge>
                              <Badge variant="outline">
                                {alert.work_type === 'site_visit' ? 'Site Visit' : 
                                 alert.work_type === 'work_from_home' ? 'WFH' : 'Office'}
                              </Badge>
                              {alert.is_critical && (
                                <Badge variant="destructive">Auto clock-out soon</Badge>
                              )}
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              const token = localStorage.getItem('token');
                              try {
                                const response = await fetch(`${API_URL}/api/admin/employees/${alert.employee_id}/force-clock-out`, {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ reason: 'Manual clock-out by admin due to long shift' })
                                });
                                if (response.ok) {
                                  toast.success(`${alert.employee_name} clocked out successfully`);
                                  fetchAllData();
                                  setShowLongShiftAlertsDialog(false);
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || 'Failed to clock out');
                                }
                              } catch (error) {
                                toast.error('Connection error');
                              }
                            }}
                          >
                            Force Clock Out
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={async () => {
                      const token = localStorage.getItem('token');
                      try {
                        const response = await fetch(`${API_URL}/api/time/process-auto-clockouts`, {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        if (response.ok) {
                          const result = await response.json();
                          toast.success(`Auto clock-out processed: ${result.auto_clocked_out_count} employees clocked out`);
                          fetchAllData();
                          setShowLongShiftAlertsDialog(false);
                        }
                      } catch (error) {
                        toast.error('Connection error');
                      }
                    }}
                  >
                    Process All Auto Clock-Outs Now
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Subcontractors Tab - Company Admin and Accounts roles */}
        {['company_admin', 'general_manager', 'manager', 'accounts', 'accounts'].includes(user.role) && (
        <TabsContent value="subcontractors" data-testid="subcontractors-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <CardTitle>Subcontractor Accounts</CardTitle>
                  <div className="relative">
                    <Input
                      placeholder="Search by name, contact..."
                      value={subcontractorSearchQuery}
                      onChange={(e) => setSubcontractorSearchQuery(e.target.value)}
                      className="w-64 pl-8"
                      data-testid="subcontractor-search-input"
                    />
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                  </div>
                  {subcontractorSearchQuery && (
                    <span className="text-sm text-gray-500">
                      {filteredSubcontractors.length} of {subcontractorAccounts.length} subcontractors
                    </span>
                  )}
                </div>
                <Dialog open={showSubcontractorDialog} onOpenChange={(open) => {
                  setShowSubcontractorDialog(open);
                  if (!open) {
                    setEditingSubcontractor(null);
                    setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-subcontractor-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Subcontractor
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{editingSubcontractor ? 'Edit Subcontractor' : 'Add Subcontractor Account'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      const token = localStorage.getItem('token');
                      setLoading(true);
                      try {
                        const url = editingSubcontractor 
                          ? `${API_URL}/api/subcontractor-accounts/${editingSubcontractor.id}`
                          : `${API_URL}/api/subcontractor-accounts`;
                        const method = editingSubcontractor ? 'PUT' : 'POST';
                        
                        const response = await fetch(url, {
                          method,
                          headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify(subcontractorForm)
                        });
                        
                        if (response.ok) {
                          toast.success(editingSubcontractor ? 'Subcontractor updated' : 'Subcontractor added');
                          setShowSubcontractorDialog(false);
                          setEditingSubcontractor(null);
                          setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                          fetchAllData();
                        } else {
                          const error = await response.json();
                          toast.error(error.detail || 'Failed to save subcontractor');
                        }
                      } catch (error) {
                        toast.error('Connection error');
                      } finally {
                        setLoading(false);
                      }
                    }} className="space-y-4">
                      <div>
                        <Label>Company Name *</Label>
                        <Input
                          value={subcontractorForm.name}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, name: e.target.value })}
                          placeholder="e.g., ABC Contracting Inc."
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Name</Label>
                          <Input
                            value={subcontractorForm.contact_name}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_name: e.target.value })}
                            placeholder="John Smith"
                          />
                        </div>
                        <div>
                          <Label>Contact Email</Label>
                          <Input
                            type="email"
                            value={subcontractorForm.contact_email}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_email: e.target.value })}
                            placeholder="contact@example.com"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Phone</Label>
                          <Input
                            value={subcontractorForm.contact_phone}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, contact_phone: e.target.value })}
                            placeholder="416-555-1234"
                          />
                        </div>
                        <div>
                          <Label>HST Number</Label>
                          <Input
                            value={subcontractorForm.hst_number}
                            onChange={(e) => setSubcontractorForm({ ...subcontractorForm, hst_number: e.target.value })}
                            placeholder="123456789RT0001"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Address</Label>
                        <Input
                          value={subcontractorForm.address}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, address: e.target.value })}
                          placeholder="123 Main St, Toronto, ON"
                        />
                      </div>
                      <div>
                        <Label>Notes</Label>
                        <textarea
                          className="w-full border rounded-md p-2 text-sm"
                          rows={2}
                          value={subcontractorForm.notes}
                          onChange={(e) => setSubcontractorForm({ ...subcontractorForm, notes: e.target.value })}
                          placeholder="Additional notes..."
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => {
                          setShowSubcontractorDialog(false);
                          setEditingSubcontractor(null);
                          setSubcontractorForm({ name: '', contact_name: '', contact_email: '', contact_phone: '', hst_number: '', address: '', notes: '' });
                        }}>
                          Cancel
                        </Button>
                        <Button type="submit" disabled={loading}>
                          {editingSubcontractor ? 'Update' : 'Add'} Subcontractor
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Manage subcontractor accounts. Employees can be assigned to a subcontractor for payroll tracking.
              </p>
              {subcontractorAccounts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No subcontractor accounts yet</p>
                  <p className="text-sm">Add a subcontractor account to assign employees for payment tracking</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="data-table" data-testid="subcontractors-table">
                    <thead>
                      <tr>
                        <th>Company Name</th>
                        <th>Contact</th>
                        <th>Phone</th>
                        <th>HST Number</th>
                        <th>Employees</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSubcontractors.map((account, index) => (
                        <tr key={account.id} data-testid={`subcontractor-row-${index}`}>
                          <td className="font-medium">{account.name}</td>
                          <td>
                            {account.contact_name && <div>{account.contact_name}</div>}
                            {account.contact_email && <div className="text-sm text-gray-500">{account.contact_email}</div>}
                          </td>
                          <td>{account.contact_phone || '-'}</td>
                          <td>{account.hst_number || '-'}</td>
                          <td>
                            <Badge variant="secondary">
                              {account.employee_count || 0} employee{(account.employee_count || 0) !== 1 ? 's' : ''}
                            </Badge>
                          </td>
                          <td>
                            <Badge variant={account.is_active !== false ? 'default' : 'secondary'} className={account.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {account.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingSubcontractor(account);
                                  setSubcontractorForm({
                                    name: account.name || '',
                                    contact_name: account.contact_name || '',
                                    contact_email: account.contact_email || '',
                                    contact_phone: account.contact_phone || '',
                                    hst_number: account.hst_number || '',
                                    address: account.address || '',
                                    notes: account.notes || ''
                                  });
                                  setShowSubcontractorDialog(true);
                                }}
                              >
                                Edit
                              </Button>
                              {account.employee_count === 0 && (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={async () => {
                                    if (!confirm(`Delete ${account.name}? This cannot be undone.`)) return;
                                    const token = localStorage.getItem('token');
                                    try {
                                      const response = await fetch(`${API_URL}/api/subcontractor-accounts/${account.id}`, {
                                        method: 'DELETE',
                                        headers: { 'Authorization': `Bearer ${token}` }
                                      });
                                      if (response.ok) {
                                        toast.success('Subcontractor deleted');
                                        fetchAllData();
                                      } else {
                                        const error = await response.json();
                                        toast.error(error.detail || 'Failed to delete');
                                      }
                                    } catch (error) {
                                      toast.error('Connection error');
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Projects Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="projects" data-testid="projects-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Projects</CardTitle>
                <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-project-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Project
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="add-project-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Project</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddProject} className="form-grid">
                      <div className="col-span-2">
                        <Label>Project Name</Label>
                        <Input
                          value={projectForm.name}
                          onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })}
                          required
                          placeholder="Enter project name"
                          data-testid="project-name-input"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Project Address</Label>
                        <Input
                          value={projectForm.address}
                          onChange={(e) => setProjectForm({ ...projectForm, address: e.target.value })}
                          required
                          placeholder="Enter project address"
                          data-testid="project-address-input"
                        />
                      </div>
                      
                      {/* Client Selection */}
                      <div className="col-span-2">
                        <Label>Client (Optional)</Label>
                        <Select
                          value={projectForm.client_id || "none"}
                          onValueChange={(value) => setProjectForm({ ...projectForm, client_id: value === "none" ? null : value })}
                        >
                          <SelectTrigger data-testid="project-client-select">
                            <SelectValue placeholder="Select a client" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {clients.map((client) => (
                              <SelectItem key={client.id} value={client.id}>
                                {client.client_name} ({client.client_code})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="col-span-2">
                        <Label>Start Date</Label>
                        <Input
                          type="date"
                          value={projectForm.start_date}
                          onChange={(e) => setProjectForm({ ...projectForm, start_date: e.target.value })}
                          required
                          data-testid="project-start-date-input"
                        />
                      </div>
                      
                      {/* Project Status */}
                      <div className="col-span-2">
                        <Label>Project Status</Label>
                        <Select
                          value={projectForm.status}
                          onValueChange={(value) => setProjectForm({ ...projectForm, status: value })}
                        >
                          <SelectTrigger data-testid="project-status-select">
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="future">Future</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Project Stage */}
                      <div className="col-span-2">
                        <Label>Project Stage</Label>
                        <Select
                          value={projectForm.stage || 'construction'}
                          onValueChange={(value) => setProjectForm({ ...projectForm, stage: value })}
                        >
                          <SelectTrigger data-testid="project-stage-select">
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pre_construction">
                              <div className="flex items-center gap-2">
                                <span>📋</span>
                                <span>Pre-Construction</span>
                              </div>
                            </SelectItem>
                            <SelectItem value="construction">
                              <div className="flex items-center gap-2">
                                <span>🏗️</span>
                                <span>Construction</span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-sm text-gray-500 mt-1">
                          Pre-construction projects are only visible to office staff (estimators, coordinators)
                        </p>
                      </div>

                      {/* Billing Date */}
                      <div className="col-span-2">
                        <Label>Client Billing Date</Label>
                        <Input
                          type="date"
                          value={projectForm.billing_date || ''}
                          onChange={(e) => setProjectForm({ ...projectForm, billing_date: e.target.value })}
                          data-testid="project-billing-date-input"
                        />
                      </div>

                      {/* Billing Reminder */}
                      <div className="col-span-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="billing-reminder"
                            checked={projectForm.billing_reminder_enabled || false}
                            onChange={(e) => setProjectForm({ ...projectForm, billing_reminder_enabled: e.target.checked })}
                            data-testid="billing-reminder-checkbox"
                          />
                          <Label htmlFor="billing-reminder" className="cursor-pointer">
                            Enable Billing Reminder (4 days before)
                          </Label>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Get email & dashboard notifications 4 days before the billing date
                        </p>
                      </div>
                      
                      <div className="col-span-2">
                        <Label>Equipment (Optional)</Label>
                        <p className="text-sm text-gray-500 mb-2">Select equipment and specify quantities</p>
                        {EQUIPMENT_OPTIONS.map((equip) => (
                          <div key={equip} className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              id={`equip-${equip}`}
                              checked={projectForm.equipment.some(e => e.type === equip)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setProjectForm({
                                    ...projectForm,
                                    equipment: [...projectForm.equipment, { type: equip, quantity: 1, other_description: '' }]
                                  });
                                } else {
                                  setProjectForm({
                                    ...projectForm,
                                    equipment: projectForm.equipment.filter(eq => eq.type !== equip)
                                  });
                                }
                              }}
                            />
                            <Label htmlFor={`equip-${equip}`} className="cursor-pointer w-48">{equip}</Label>
                            
                            {/* Quantity Input */}
                            {projectForm.equipment.some(e => e.type === equip) && (
                              <>
                                <Input
                                  type="number"
                                  min="1"
                                  placeholder="Qty"
                                  className="w-20"
                                  value={projectForm.equipment.find(e => e.type === equip)?.quantity || 1}
                                  onChange={(e) => {
                                    const qty = parseInt(e.target.value) || 1;
                                    setProjectForm({
                                      ...projectForm,
                                      equipment: projectForm.equipment.map(eq =>
                                        eq.type === equip ? { ...eq, quantity: qty } : eq
                                      )
                                    });
                                  }}
                                  data-testid={`equip-qty-${equip}`}
                                />
                                
                                {/* Other description for 'Other' type */}
                                {equip === 'Other' && (
                                  <Input
                                    placeholder="Specify other equipment"
                                    className="flex-1"
                                    value={projectForm.equipment.find(e => e.type === 'Other')?.other_description || ''}
                                    onChange={(e) => {
                                      setProjectForm({
                                        ...projectForm,
                                        equipment: projectForm.equipment.map(eq =>
                                          eq.type === 'Other' ? { ...eq, other_description: e.target.value } : eq
                                        )
                                      });
                                    }}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      <Button type="submit" className="col-span-2" disabled={loading} data-testid="submit-project-btn">
                        Create Project
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {/* Search filter */}
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[260px] max-w-md">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <Input
                    type="text"
                    placeholder="Search by project name, client, or address…"
                    value={projectSearchQuery}
                    onChange={(e) => setProjectSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="projects-search-input"
                  />
                  {projectSearchQuery && (
                    <button
                      onClick={() => setProjectSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 px-1"
                      data-testid="projects-search-clear"
                      aria-label="Clear search"
                      type="button"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
              <div className="table-container-with-sticky-scroll">
                <div className="table-responsive" ref={projectsTableRef}>
                  <table className="data-table" data-testid="projects-table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Client</th>
                      <th>Address</th>
                      <th>Team Members</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...projects]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .filter((p) => {
                        if (!projectSearchQuery) return true;
                        const q = projectSearchQuery.toLowerCase();
                        return (
                          (p.name || '').toLowerCase().includes(q) ||
                          (p.client_name || '').toLowerCase().includes(q) ||
                          (p.client_code || '').toLowerCase().includes(q) ||
                          (p.address || '').toLowerCase().includes(q)
                        );
                      })
                      .map((project, index) => (
                      <tr key={project.id} data-testid={`project-row-${index}`}>
                        <td>
                          <strong 
                            className="text-blue-600 hover:text-blue-800 cursor-pointer hover:underline"
                            onClick={() => handleViewProjectDetails(project)}
                            data-testid={`project-name-${index}`}
                          >
                            {project.name}
                          </strong>
                        </td>
                        <td>
                          {project.client_name ? (
                            <div>
                              <div className="font-medium">{project.client_name}</div>
                              <div className="text-sm text-gray-500">{project.client_code}</div>
                            </div>
                          ) : project.client_code ? (
                            <div className="text-sm text-gray-600">{project.client_code}</div>
                          ) : (
                            <span className="text-gray-400">No client</span>
                          )}
                        </td>
                        <td>{project.address}</td>
                        <td>{project.team_members?.length || 0} members</td>
                        <td>
                          <div className="flex flex-col gap-1">
                            <Badge 
                              variant={
                                project.status === 'active' ? 'success' : 
                                project.status === 'completed' ? 'default' : 
                                'secondary'
                              }
                            >
                              {project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : 'Active'}
                            </Badge>
                            <Badge 
                              variant="outline"
                              className={project.stage === 'pre_construction' ? 'text-blue-600 border-blue-300' : 'text-orange-600 border-orange-300'}
                            >
                              {project.stage === 'pre_construction' ? '📋 Pre-Con' : '🏗️ Construction'}
                            </Badge>
                          </div>
                        </td>
                        <td>
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedProject(project);
                              setSelectedTeamMembers(project.team_members || []);
                              setShowAssignDialog(true);
                            }}
                            data-testid={`assign-team-${index}`}
                          >
                            Assign Team
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {projectSearchQuery &&
                  projects.filter((p) => {
                    const q = projectSearchQuery.toLowerCase();
                    return (
                      (p.name || '').toLowerCase().includes(q) ||
                      (p.client_name || '').toLowerCase().includes(q) ||
                      (p.client_code || '').toLowerCase().includes(q) ||
                      (p.address || '').toLowerCase().includes(q)
                    );
                  }).length === 0 && (
                    <div className="py-8 text-center text-gray-500" data-testid="projects-search-no-results">
                      No projects match <strong>"{projectSearchQuery}"</strong>.
                      <button
                        onClick={() => setProjectSearchQuery('')}
                        className="ml-2 text-purple-600 hover:underline"
                        type="button"
                      >
                        Clear search
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Sticky scrollbar at bottom of viewport */}
                {projectsScroll.showStickyScroll && (
                  <div className="sticky-scrollbar-container">
                    <div 
                      className="sticky-scrollbar-track" 
                      ref={projectsScroll.stickyScrollRef}
                    >
                      <div 
                        className="sticky-scrollbar-content"
                        style={{ width: projectsTableRef.current?.scrollWidth + 'px' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Assign Team Dialog */}
          <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
            <DialogContent data-testid="assign-team-dialog" className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Assign Team to {selectedProject?.name}</DialogTitle>
              </DialogHeader>
              <div className="team-assignment">
                <Label>Select Team Members & Time Windows (Optional)</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Add time windows to prevent overlapping assignments
                </p>
                <div className="team-list space-y-4">
                  {employees.map((emp) => (
                    <div key={emp.id} className="team-item-with-time border p-3 rounded">
                      <div className="flex items-center mb-2">
                        <input
                          type="checkbox"
                          checked={selectedTeamMembers.includes(emp.user_id)}
                          onChange={() => toggleTeamMember(emp.user_id)}
                          data-testid={`team-member-${emp.id}`}
                        />
                        <label className="ml-2 font-medium">
                          {emp.user?.name} - {emp.position}
                        </label>
                      </div>
                      
                      {selectedTeamMembers.includes(emp.user_id) && (
                        <div className="grid grid-cols-2 gap-2 ml-6">
                          <div>
                            <Label className="text-xs">Start Time</Label>
                            <Input
                              type="time"
                              value={teamMemberTimeWindows[emp.user_id]?.start_time || ''}
                              onChange={(e) => setTeamMemberTimeWindows({
                                ...teamMemberTimeWindows,
                                [emp.user_id]: {
                                  ...teamMemberTimeWindows[emp.user_id],
                                  start_time: e.target.value
                                }
                              })}
                              placeholder="09:00"
                              className="text-sm"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">End Time</Label>
                            <Input
                              type="time"
                              value={teamMemberTimeWindows[emp.user_id]?.end_time || ''}
                              onChange={(e) => setTeamMemberTimeWindows({
                                ...teamMemberTimeWindows,
                                [emp.user_id]: {
                                  ...teamMemberTimeWindows[emp.user_id],
                                  end_time: e.target.value
                                }
                              })}
                              placeholder="17:00"
                              className="text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  onClick={() => handleAssignTeam(selectedProject?.id)}
                  disabled={loading || selectedTeamMembers.length === 0}
                  className="w-full mt-4"
                  data-testid="save-team-assignment"
                >
                  Save Team Assignment
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Schedule Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="schedule" data-testid="schedule-content">
          <ScheduleManagement projects={projects} employees={employees} user={user} />
        </TabsContent>
        )}

        {/* Equipment Management Tab - Company Admin Only */}
        {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
        <TabsContent value="equipment" data-testid="equipment-content">
          {/* Equipment Pool Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-blue-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-700">{equipmentSummary.total_types}</div>
                <div className="text-sm text-blue-600">Equipment Types</div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-purple-700">{equipmentSummary.total_units}</div>
                <div className="text-sm text-purple-600">Total Units</div>
              </CardContent>
            </Card>
            <Card className="bg-amber-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-700">{equipmentSummary.assigned_units}</div>
                <div className="text-sm text-amber-600">Assigned to Projects</div>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-700">{equipmentSummary.available_units}</div>
                <div className="text-sm text-green-600">Available in Pool</div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Equipment Pool</CardTitle>
                <Button onClick={() => {
                  setEquipmentManagementForm({ name: '', description: '', total_quantity: 1, unit: 'units' });
                  setShowEquipmentManagementDialog(true);
                }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Equipment
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-responsive">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Equipment</th>
                      <th>Description</th>
                      <th className="text-center">Total</th>
                      <th className="text-center">Assigned</th>
                      <th className="text-center">Available</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {equipmentList.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-gray-500 py-8">
                          No equipment in pool. Click "Add Equipment" to add some.
                        </td>
                      </tr>
                    ) : (
                      equipmentList.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td 
                            className="font-medium cursor-pointer hover:text-blue-600"
                            onClick={() => handleViewEquipmentAssignments(item)}
                          >
                            {item.name || item.type}
                            {item.assignment_count > 0 && (
                              <span className="ml-2 text-xs text-gray-500">
                                ({item.assignment_count} project{item.assignment_count !== 1 ? 's' : ''})
                              </span>
                            )}
                          </td>
                          <td className="text-gray-600">{item.description || item.custom_description || '-'}</td>
                          <td className="text-center">
                            <Badge variant="outline">{item.total_quantity || item.quantity || 1} {item.unit || 'units'}</Badge>
                          </td>
                          <td className="text-center">
                            <Badge variant={item.assigned_quantity > 0 ? 'default' : 'secondary'} className={item.assigned_quantity > 0 ? 'bg-amber-100 text-amber-800' : ''}>
                              {item.assigned_quantity || 0}
                            </Badge>
                          </td>
                          <td className="text-center">
                            <Badge variant={item.available_quantity > 0 ? 'default' : 'destructive'} className={item.available_quantity > 0 ? 'bg-green-100 text-green-800' : ''}>
                              {item.available_quantity || 0}
                            </Badge>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewEquipmentAssignments(item)}
                                data-testid={`view-assignments-${item.id}`}
                              >
                                View
                              </Button>
                              {item.available_quantity > 0 && (
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={() => handleOpenAssignEquipment(item)}
                                  data-testid={`assign-equipment-${item.id}`}
                                >
                                  Assign
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenEditEquipment(item)}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleDeleteEquipmentItem(item)}
                                disabled={item.assigned_quantity > 0}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Add Equipment Dialog */}
          <Dialog open={showEquipmentManagementDialog} onOpenChange={setShowEquipmentManagementDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Equipment to Pool</DialogTitle>
                <DialogDescription>
                  Add equipment with quantity. You can then assign units to projects as needed.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddEquipmentItem} className="space-y-4">
                <div>
                  <Label>Equipment Name *</Label>
                  <Input
                    value={equipmentManagementForm.name}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, name: e.target.value })}
                    required
                    placeholder="e.g., Ladder, Drill, Safety Harness"
                    data-testid="equipment-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={equipmentManagementForm.total_quantity}
                      onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, total_quantity: e.target.value })}
                      required
                      data-testid="equipment-quantity-input"
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select 
                      value={equipmentManagementForm.unit}
                      onValueChange={(val) => setEquipmentManagementForm({ ...equipmentManagementForm, unit: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="pieces">Pieces</SelectItem>
                        <SelectItem value="sets">Sets</SelectItem>
                        <SelectItem value="pairs">Pairs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={equipmentManagementForm.description}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, description: e.target.value })}
                    placeholder="Additional details or specifications"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEquipmentManagementDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Add to Pool
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Equipment Dialog */}
          <Dialog open={showEditEquipmentDialog} onOpenChange={setShowEditEquipmentDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Edit Equipment</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleEditEquipmentItem} className="space-y-4">
                <div>
                  <Label>Equipment Name *</Label>
                  <Input
                    value={equipmentManagementForm.name}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, name: e.target.value })}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Total Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={equipmentManagementForm.total_quantity}
                      onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, total_quantity: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Unit</Label>
                    <Select 
                      value={equipmentManagementForm.unit}
                      onValueChange={(val) => setEquipmentManagementForm({ ...equipmentManagementForm, unit: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="units">Units</SelectItem>
                        <SelectItem value="pieces">Pieces</SelectItem>
                        <SelectItem value="sets">Sets</SelectItem>
                        <SelectItem value="pairs">Pairs</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Input
                    value={equipmentManagementForm.description}
                    onChange={(e) => setEquipmentManagementForm({ ...equipmentManagementForm, description: e.target.value })}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowEditEquipmentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    Save Changes
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* View Equipment Assignments Dialog */}
          <Dialog open={showEquipmentAssignmentsDialog} onOpenChange={setShowEquipmentAssignmentsDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {selectedEquipmentItem?.name || selectedEquipmentItem?.type} - Project Assignments
                </DialogTitle>
                <DialogDescription>
                  Total: {selectedEquipmentItem?.total_quantity || 0} | 
                  Assigned: {selectedEquipmentItem?.assigned_quantity || 0} | 
                  Available: {selectedEquipmentItem?.available_quantity || 0}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {equipmentAssignments.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">
                    No project assignments yet. This equipment is fully available in the pool.
                  </p>
                ) : (
                  <table className="data-table w-full">
                    <thead>
                      <tr>
                        <th>Project</th>
                        <th>Status</th>
                        <th className="text-center">Qty Assigned</th>
                        <th>Assigned</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentAssignments.map((assignment) => (
                        <tr key={assignment.id}>
                          <td className="font-medium">{assignment.project_name}</td>
                          <td>
                            <Badge variant={assignment.project_status === 'active' ? 'default' : 'secondary'}>
                              {assignment.project_status}
                            </Badge>
                          </td>
                          <td className="text-center">{assignment.quantity}</td>
                          <td className="text-sm text-gray-500">
                            {new Date(assignment.assigned_at).toLocaleDateString()}
                          </td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleUnassignEquipment(assignment.id, assignment.project_id)}
                            >
                              Return to Pool
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Assign Equipment to Project Dialog */}
          <Dialog open={showAssignEquipmentDialog} onOpenChange={setShowAssignEquipmentDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Assign {selectedEquipmentItem?.name || selectedEquipmentItem?.type} to Project</DialogTitle>
                <DialogDescription>
                  Available: {selectedEquipmentItem?.available_quantity || 0} {selectedEquipmentItem?.unit || 'units'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAssignEquipmentToProject} className="space-y-4">
                <div>
                  <Label>Project *</Label>
                  <ProjectCombobox
                    projects={projects.filter(p => p.status === 'active')}
                    value={assignEquipmentForm.project_id}
                    onChange={(val) => setAssignEquipmentForm({ ...assignEquipmentForm, project_id: val })}
                    placeholder="Select a project..."
                    filterActive={false}
                    testId="assign-equipment-project"
                  />
                </div>
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedEquipmentItem?.available_quantity || 1}
                    value={assignEquipmentForm.quantity}
                    onChange={(e) => setAssignEquipmentForm({ ...assignEquipmentForm, quantity: e.target.value })}
                    required
                    data-testid="assign-quantity-input"
                  />
                </div>
                <div>
                  <Label>Notes (Optional)</Label>
                  <Input
                    value={assignEquipmentForm.notes}
                    onChange={(e) => setAssignEquipmentForm({ ...assignEquipmentForm, notes: e.target.value })}
                    placeholder="Any notes about this assignment"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowAssignEquipmentDialog(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading || !assignEquipmentForm.project_id}>
                    Assign Equipment
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        {/* Invoice Management Tab - Behind invoice_ocr feature flag */}
        {(['company_admin', 'accounts', 'admin'].includes(user.role)) && hasFeature(user, 'invoice_ocr') && (
          <TabsContent value="invoices" data-testid="invoices-content">
            <InvoiceManagement projects={projects} onProjectsChanged={fetchAllData} />
          </TabsContent>
        )}

        {/* Stock Material Tab - Visible to all including purchasers */}
        <TabsContent value="stock" data-testid="stock-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Stock Material</CardTitle>
                <Button
                  onClick={() => setShowStockManagementDialog(true)}
                  data-testid="manage-stock-btn"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Manage Stock
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500">
                Click <strong>Manage Stock</strong> to view, adjust, or transfer
                materials between the warehouse and active projects. Equipment
                items live in the dedicated <strong>Equipment</strong> tab.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Material Orders Tab */}
        <TabsContent value="orders" data-testid="orders-content">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Material Orders</CardTitle>
                <Dialog open={showOrderDialog} onOpenChange={setShowOrderDialog}>
                  <DialogTrigger asChild>
                    <Button data-testid="create-order-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Order
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="create-order-dialog">
                    <DialogHeader>
                      <DialogTitle>Create Material Order</DialogTitle>
                    </DialogHeader>
                    <MaterialOrderForm 
                      projects={projects}
                      onSubmit={handleCreateMaterialOrder}
                      onCancel={() => setShowOrderDialog(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="table-responsive" ref={ordersTableRef}>
                <table className="data-table" data-testid="orders-table">
                  <thead>
                    <tr>
                      <th>Order Date</th>
                      <th>Project</th>
                      <th>Order Type</th>
                      <th>Delivery Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialOrders.map((order, index) => {
                      const project = projects.find(p => p.id === order.project_id);
                      return (
                        <tr key={order.id} data-testid={`order-row-${index}`}>
                          <td>{new Date(order.order_date).toLocaleDateString()}</td>
                          <td>{project?.name || 'Unknown'}</td>
                          <td>
                            <Badge variant="outline">{order.order_type}</Badge>
                          </td>
                          <td>{order.delivery_date ? new Date(order.delivery_date).toLocaleDateString() : '-'}</td>
                          <td>
                            <Badge
                              variant={
                                order.status === 'completed'
                                  ? 'success'
                                  : order.status === 'pending'
                                  ? 'warning'
                                  : 'default'
                              }
                            >
                              {order.status}
                            </Badge>
                            {order.source === 'invoice_backdated' && (
                              <Badge
                                className="ml-1 bg-purple-100 text-purple-700 border-purple-200 text-[10px]"
                                title={`Back-dated from invoice ${order.source_invoice_number || ''}`}
                                data-testid={`order-from-invoice-badge-${index}`}
                              >
                                From Invoice
                              </Badge>
                            )}
                          </td>
                          <td>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => handleViewOrderDetails(order)}
                              data-testid={`view-order-${index}`}
                            >
                              View Details
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {ordersScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={ordersScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: ordersTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}

            </CardContent>
          </Card>
        </TabsContent>

        {/* Time Tracking Tab - For Purchasers Only */}
        {['purchaser', 'accounts'].includes(user.role) && (
          <TabsContent value="time-tracking" data-testid="time-tracking-content">
            <Card>
              <CardHeader>
                <CardTitle>Time Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Clock In/Out Controls */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold">Current Status</h3>
                      <p className="text-sm text-gray-600">
                        {timeSummary.is_clocked_in ? (
                          <>
                            <span className="text-green-600 font-semibold">● Clocked In</span>
                            <span className="ml-2 text-gray-500">
                              since {timeSummary.current_shift_start ? new Date(timeSummary.current_shift_start).toLocaleTimeString() : ''}
                            </span>
                          </>
                        ) : (
                          <span className="text-gray-500">● Not Clocked In</span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!timeSummary.is_clocked_in ? (
                        <Button 
                          onClick={handleClockIn} 
                          disabled={clockingIn}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Clock In
                        </Button>
                      ) : (
                        <Button 
                          onClick={handleClockOut} 
                          disabled={clockingIn}
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          Clock Out
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Hours Summary */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">Today</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.today_hours.toFixed(2)}h</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">This Week</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.week_hours.toFixed(2)}h</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg border">
                      <p className="text-sm text-gray-600">Pay Period</p>
                      <p className="text-2xl font-bold text-blue-600">{timeSummary.pay_period_hours.toFixed(2)}h</p>
                    </div>
                  </div>
                </div>

                {/* Time Entries History */}
                <div>
                  <h3 className="text-lg font-semibold mb-3">Time Entries History</h3>
                  <Button 
                    onClick={() => { fetchTimeEntries(); fetchTimeSummary(); }} 
                    className="mb-4"
                    variant="outline"
                    size="sm"
                  >
                    Refresh
                  </Button>
                  
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Date</th>
                          <th className="px-4 py-2 text-left">Clock In</th>
                          <th className="px-4 py-2 text-left">Clock Out</th>
                          <th className="px-4 py-2 text-left">Hours</th>
                          <th className="px-4 py-2 text-left">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {timeEntries.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No time entries found
                            </td>
                          </tr>
                        ) : (
                          timeEntries
                            .sort((a, b) => new Date(b.clock_in) - new Date(a.clock_in))
                            .map((entry) => {
                              const clockIn = new Date(entry.clock_in);
                              const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
                              const hours = clockOut 
                                ? ((clockOut - clockIn) / (1000 * 60 * 60)).toFixed(2)
                                : '-';
                              
                              return (
                                <tr key={entry.id} className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-2">{clockIn.toLocaleDateString()}</td>
                                  <td className="px-4 py-2">{clockIn.toLocaleTimeString()}</td>
                                  <td className="px-4 py-2">
                                    {clockOut ? clockOut.toLocaleTimeString() : '-'}
                                  </td>
                                  <td className="px-4 py-2 font-semibold">{hours}</td>
                                  <td className="px-4 py-2">
                                    {clockOut ? (
                                      <Badge variant="secondary">Complete</Badge>
                                    ) : (
                                      <Badge variant="default" className="bg-green-600">Active</Badge>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* View-Only Tabs for Purchasers */}
        {['purchaser', 'accounts'].includes(user.role) && (
          <>
            {/* View Clients Tab */}
            <TabsContent value="view-clients" data-testid="view-clients-content">
              <Card>
                <CardHeader>
                  <CardTitle>Clients (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto" ref={clientsTableRef}>
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Client Code</th>
                          <th className="px-4 py-2 text-left">Company Name</th>
                          <th className="px-4 py-2 text-left">Contact Person</th>
                          <th className="px-4 py-2 text-left">Phone</th>
                          <th className="px-4 py-2 text-left">Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {clients.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No clients found
                            </td>
                          </tr>
                        ) : (
                          clients.map((client) => (
                            <tr key={client.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-mono text-sm">{client.client_code}</td>
                              <td className="px-4 py-2 font-semibold">{client.company_name}</td>
                              <td className="px-4 py-2">{client.billing_contact?.name || '-'}</td>
                              <td className="px-4 py-2">{client.billing_contact?.phone || '-'}</td>
                              <td className="px-4 py-2 text-sm">{client.billing_contact?.email || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Sticky scrollbar at bottom of viewport */}
                  {clientsScroll.showStickyScroll && (
                    <div className="sticky-scrollbar-container">
                      <div 
                        className="sticky-scrollbar-track" 
                        ref={clientsScroll.stickyScrollRef}
                      >
                        <div 
                          className="sticky-scrollbar-content"
                          style={{ width: clientsTableRef.current?.scrollWidth + 'px' }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* View Projects Tab */}
            <TabsContent value="view-projects" data-testid="view-projects-content">
              <Card>
                <CardHeader>
                  <CardTitle>Projects (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Project Name</th>
                          <th className="px-4 py-2 text-left">Client</th>
                          <th className="px-4 py-2 text-left">Address</th>
                          <th className="px-4 py-2 text-left">Status</th>
                          <th className="px-4 py-2 text-left">Start Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {projects.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                              No projects found
                            </td>
                          </tr>
                        ) : (
                          projects.map((project) => {
                            const client = clients.find(c => c.id === project.client_id);
                            return (
                              <tr key={project.id} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-2 font-semibold">{project.name}</td>
                                <td className="px-4 py-2">{client?.company_name || '-'}</td>
                                <td className="px-4 py-2 text-sm">{project.address}</td>
                                <td className="px-4 py-2">
                                  <Badge variant={
                                    project.status === 'active' ? 'success' :
                                    project.status === 'completed' ? 'secondary' :
                                    'default'
                                  }>
                                    {project.status}
                                  </Badge>
                                </td>
                                <td className="px-4 py-2">
                                  {project.start_date ? new Date(project.start_date).toLocaleDateString() : '-'}
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* View Employees Tab */}
            <TabsContent value="view-employees" data-testid="view-employees-content">
              <Card>
                <CardHeader>
                  <CardTitle>Employees (View Only)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Position</th>
                          <th className="px-4 py-2 text-left">Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                              No employees found
                            </td>
                          </tr>
                        ) : (
                          employees.map((emp) => (
                            <tr key={emp.id} className="border-b hover:bg-gray-50">
                              <td className="px-4 py-2 font-semibold">{emp.user?.name || emp.name}</td>
                              <td className="px-4 py-2 text-sm">{emp.user?.email || emp.email}</td>
                              <td className="px-4 py-2">{emp.position}</td>
                              <td className="px-4 py-2">
                                <Badge variant="secondary">{emp.user?.role || emp.role}</Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </>
        )}

        {/* Project Messages Tab - Available for all roles including purchasers */}
        <TabsContent value="communication" data-testid="communication-content">
          <Card>
            <CardHeader>
              <CardTitle>All Messages & Communication</CardTitle>
              {/* Message Type Filter Chips */}
              <div className="flex gap-2 flex-wrap items-center mt-3" data-testid="message-type-filter">
                <span className="text-sm text-gray-600 font-medium mr-1">Select From:</span>
                {[
                  { key: 'all', label: 'All', testId: 'filter-type-all' },
                  { key: 'announcement', label: 'Announcement', testId: 'filter-type-announcement' },
                  { key: 'project', label: 'Project', testId: 'filter-type-project' },
                  { key: 'work_progress', label: 'Daily Clock Out Work Progress', testId: 'filter-type-work-progress' },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    size="sm"
                    variant={messageTypeFilter === opt.key ? 'default' : 'outline'}
                    onClick={() => setMessageTypeFilter(opt.key)}
                    data-testid={opt.testId}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="mb-4 space-y-3">
                {/* Date Filter */}
                <div className="flex gap-2 items-center flex-wrap">
                  <Label className="min-w-[100px]">Filter by Date:</Label>
                  <div className="flex gap-2 items-center flex-1">
                    <Input
                      type="date"
                      value={messageDateFilter.startDate}
                      onChange={(e) => setMessageDateFilter({ ...messageDateFilter, startDate: e.target.value })}
                      className="max-w-[160px]"
                      placeholder="Start Date"
                    />
                    <span className="text-gray-500">to</span>
                    <Input
                      type="date"
                      value={messageDateFilter.endDate}
                      onChange={(e) => setMessageDateFilter({ ...messageDateFilter, endDate: e.target.value })}
                      className="max-w-[160px]"
                      placeholder="End Date"
                    />
                    <Button 
                      size="sm" 
                      onClick={fetchMessagesWithFilter}
                      variant="outline"
                    >
                      Apply Filter
                    </Button>
                    {(messageDateFilter.startDate || messageDateFilter.endDate) && (
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => {
                          setMessageDateFilter({ startDate: '', endDate: '' });
                          fetchAllData();
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Project Filter */}
                <div className="flex gap-2 items-center">
                  <Label className="min-w-[100px]">Filter by Project:</Label>
                  <select
                    className="border rounded px-3 py-2 flex-1"
                    onChange={(e) => {
                      const projectId = e.target.value;
                      if (projectId === 'all') {
                        setMessages(allMessages);
                      } else {
                        // Filter messages for specific project
                        const filtered = allMessages.filter(m => m.project_id === projectId);
                        setMessages(filtered);
                      }
                    }}
                  >
                    <option value="all">All Projects & Announcements</option>
                    {projects.map(proj => (
                      <option key={proj.id} value={proj.id}>{proj.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="messages-container max-h-96 overflow-y-auto" data-testid="messages-container">
                {messages
                  .filter((msg) => messageTypeFilter === 'all' || msg.message_type === messageTypeFilter)
                  .map((msg, index) => (
                  <div key={msg.id} className="message p-3 mb-3 border rounded bg-white" data-testid={`message-${index}`}>
                    <div className="message-header flex justify-between items-start mb-2">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <strong>{msg.sender_name}</strong>
                          {msg.message_type === 'work_progress' && (
                            <Badge variant="success" className="text-xs">Work Progress</Badge>
                          )}
                          {msg.message_type === 'announcement' && (
                            <Badge variant="secondary" className="text-xs">Announcement</Badge>
                          )}
                          {msg.message_type === 'project' && (
                            <Badge variant="default" className="text-xs">Project</Badge>
                          )}
                        </div>
                        {msg.project_name && (
                          <span className="text-xs text-gray-600">📁 {msg.project_name}</span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{new Date(msg.timestamp).toLocaleString()}</span>
                    </div>
                    <div className="message-body text-sm" style={{whiteSpace: 'pre-wrap'}}>{msg.message}</div>
                    
                    {/* Display images if present */}
                    {msg.image_urls && msg.image_urls.length > 0 && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        {msg.image_urls.map((url, idx) => {
                          // Convert to API endpoint URL
                          const filename = url.split('/').pop();
                          const displayUrl = `${API_URL}/api/image/${filename}`;
                          return (
                            <img 
                              key={idx} 
                              src={displayUrl}
                              alt="Attachment" 
                              className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                              onClick={() => {
                                setViewerImages(msg.image_urls);
                                setViewerIndex(idx);
                                setShowImageViewer(true);
                              }}
                            />
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Display video if present */}
                    {msg.video_url && (
                      <div className="mt-2">
                        <video controls className="w-full max-w-md rounded border">
                          <source src={msg.video_url} type="video/mp4" />
                          Your browser does not support video playback.
                        </video>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm font-semibold mb-2">💡 Tips:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• <strong>To send with media:</strong> Projects tab → Select project → Project Communication → Upload & send</li>
                  <li>• <strong>All messages sync:</strong> Messages sent here or in project view appear in both places</li>
                  <li>• <strong>Refresh to see updates:</strong> Press F5 to load latest messages from all sources</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        {!['purchaser', 'accounts', 'accounts_purchaser'].includes(user.role) && (
        <TabsContent value="tasks" data-testid="tasks-content">
          <TaskManagement employees={employees} />
        </TabsContent>
        )}

        {/* Project Progress Tab */}
        {['company_admin', 'general_manager', 'director', 'project_manager'].includes(user.role) && (
        <TabsContent value="project-progress" data-testid="project-progress-content">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Project Progress Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProjectProgressViewer
                projects={projects}
                canDelete={['company_admin', 'general_manager'].includes(user.role)}
              />
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Subscription Tab Removed */}
        {/* Expenses Tab - Only for Admin/Manager, NOT for purchasers */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="expenses" data-testid="expenses-content">
          <Card>
            <CardHeader>
              <CardTitle>Expense Reimbursements</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <Card className="bg-yellow-50 border-yellow-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-800">
                          {expenses.filter(expense => expense.status === 'pending').length}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Approved</p>
                        <p className="text-2xl font-bold text-green-800">
                          ${expenses.filter(expense => expense.status === 'approved').reduce((sum, expense) => sum + expense.amount, 0).toFixed(2)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50 border-red-200">
                    <CardContent className="pt-6">
                      <div className="text-center">
                        <p className="text-sm text-gray-600">Rejected</p>
                        <p className="text-2xl font-bold text-red-800">
                          {expenses.filter(expense => expense.status === 'rejected').length}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Expenses Table */}
                <div className="table-responsive">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Type</th>
                        <th>Amount</th>
                        <th>Description</th>
                        <th>Pay Period</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {expenses.map((expense, index) => (
                        <tr key={expense.id} data-testid={`expense-row-${index}`}>
                          <td>{expense.employee_name || 'Unknown'}</td>
                          <td className="capitalize">{expense.expense_type.replace('_', ' ')}</td>
                          <td className="font-semibold">${expense.amount.toFixed(2)}</td>
                          <td>{expense.description}</td>
                          <td>
                            <div className="text-sm">
                              {new Date(expense.pay_period_start).toLocaleDateString()} -<br/>
                              {new Date(expense.pay_period_end).toLocaleDateString()}
                            </div>
                          </td>
                          <td>{new Date(expense.submitted_at).toLocaleDateString()}</td>
                          <td>
                            <Badge variant={
                              expense.status === 'approved' ? 'success' :
                              expense.status === 'rejected' ? 'destructive' : 'warning'
                            }>
                              {expense.status}
                            </Badge>
                          </td>
                          <td>
                            {expense.status === 'pending' && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleExpenseStatusUpdate(expense.id, 'approved')}
                                  disabled={loading}
                                  data-testid={`approve-expense-${index}`}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleExpenseStatusUpdate(expense.id, 'rejected')}
                                  disabled={loading}
                                  data-testid={`reject-expense-${index}`}
                                >
                                  Reject
                                </Button>
                              </div>
                            )}
                            {expense.status !== 'pending' && (
                              <span className="text-sm text-gray-500">
                                {expense.status === 'approved' ? 'Approved' : 'Rejected'} by {expense.reviewed_by}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {expenses.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center text-gray-500 py-8">
                            No expenses submitted yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Excel Exports Tab */}
        {(['company_admin', 'accounts', 'admin'].includes(user.role)) && (
        <TabsContent value="exports" data-testid="exports-content">
          <Card>
            <CardHeader>
              <CardTitle>Excel Exports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {hasFeature(user, 'payroll_summary') && (
                <div className="p-4 border rounded bg-blue-50">
                  <h3 className="font-semibold mb-2">📊 Payroll Export</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export employee hours, hourly rates, and total pay for a date range
                  </p>
                  <Button onClick={() => setShowPayrollExportDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                    Export Payroll
                  </Button>
                </div>
                )}

                {hasFeature(user, 'payroll_summary') && (
                <div className="p-4 border rounded bg-purple-50">
                  <h3 className="font-semibold mb-2">📈 Compare Projects</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Pick two or more projects and compare man hours, labour cost, and material spend side-by-side
                  </p>
                  <Button
                    onClick={() => setShowProjectComparisonDialog(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="compare-projects-btn"
                  >
                    Compare Projects
                  </Button>
                </div>
                )}
                
                {hasFeature(user, 'excel_export') && (
                <div className="p-4 border rounded bg-green-50">
                  <h3 className="font-semibold mb-2">💰 Project Expenses Export</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export material costs, labor costs, and equipment costs by project
                  </p>
                  <Button onClick={() => setShowExpenseExportDialog(true)} className="bg-green-600 hover:bg-green-700">
                    Export Project Expenses
                  </Button>
                </div>
                )}
                
                <div className="p-4 border rounded bg-emerald-50">
                  <h3 className="font-semibold mb-2">📦 Material Orders Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export all material orders with status, items, and project details
                  </p>
                  <Button onClick={() => setShowMaterialOrdersExportDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                    Export Material Orders
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-indigo-50">
                  <h3 className="font-semibold mb-2">💬 Project Communications</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export all project messages, announcements, and communications
                  </p>
                  <Button onClick={() => setShowCommunicationsExportDialog(true)} className="bg-indigo-600 hover:bg-indigo-700">
                    Export Communications
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-orange-50">
                  <h3 className="font-semibold mb-2">📥 Stock In/Out Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export stock movements: deliveries, returns, and transfers
                  </p>
                  <Button onClick={() => setShowStockExportDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                    Export Stock Movements
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-amber-50">
                  <h3 className="font-semibold mb-2">🔧 Equipment Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export equipment list with service history and usage logs
                  </p>
                  <Button onClick={() => handleEquipmentExport()} className="bg-amber-600 hover:bg-amber-700">
                    Export Equipment
                  </Button>
                </div>
                
                <div className="p-4 border rounded bg-purple-50">
                  <h3 className="font-semibold mb-2">👥 Client Projects Report</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Export client list with all projects, status, and dates
                  </p>
                  <Button onClick={() => handleClientExport()} className="bg-purple-600 hover:bg-purple-700">
                    Export Clients
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Material Reconciliation Tab - For Admin and Accounts roles */}
        {(['company_admin', 'accounts', 'accounts', 'admin'].includes(user.role)) && (
        <TabsContent value="reconciliation" data-testid="reconciliation-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Material Reconciliation Report</CardTitle>
              <div className="flex items-center gap-2">
                <div className="w-[280px]">
                  <ProjectCombobox
                    projects={projects.filter(p => p.is_active && p.status !== 'completed')}
                    value={selectedReconciliationProject}
                    onChange={(value) => {
                      setSelectedReconciliationProject(value);
                      fetchMaterialReconciliation(value);
                    }}
                    placeholder="Select a project..."
                    filterActive={false}
                    testId="reconciliation-project"
                  />
                </div>
                {selectedReconciliationProject && (
                  <Button 
                    onClick={() => handleExportReconciliation(selectedReconciliationProject)}
                    variant="outline"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Export Excel
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedReconciliationProject ? (
                <div className="text-center py-12 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Select a project to view material reconciliation data</p>
                  <p className="text-sm mt-2">This report shows all materials delivered to the site from completed orders</p>
                </div>
              ) : loadingReconciliation ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                  <p>Loading reconciliation data...</p>
                </div>
              ) : reconciliationData ? (
                <div className="space-y-6">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600 font-medium">Project</p>
                      <p className="text-xl font-bold">{reconciliationData.project_name}</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-green-600 font-medium">Total Deliveries</p>
                      <p className="text-xl font-bold">{reconciliationData.total_deliveries}</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-600 font-medium">Unique Items</p>
                      <p className="text-xl font-bold">{reconciliationData.total_unique_items}</p>
                    </div>
                  </div>

                  {/* Items Summary Table */}
                  {reconciliationData.items && reconciliationData.items.length > 0 ? (
                    <>
                      <div>
                        <h3 className="font-semibold mb-3">Material Summary</h3>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full" data-testid="reconciliation-table">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Item Name</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Total Qty</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Unit</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {reconciliationData.items.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm capitalize">{item.category || 'Misc'}</td>
                                  <td className="px-4 py-3 text-sm font-medium">{item.item_name}</td>
                                  <td className="px-4 py-3 text-sm font-bold text-blue-600">{item.total_quantity}</td>
                                  <td className="px-4 py-3 text-sm">{item.unit}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Delivery History Table */}
                      <div>
                        <h3 className="font-semibold mb-3">Delivery History</h3>
                        <div className="overflow-x-auto border rounded-lg">
                          <table className="w-full" data-testid="delivery-history-table">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Order ID</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Order Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Delivery Date</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Created By</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Delivered By</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Items Count</th>
                                <th className="px-4 py-3 text-left text-sm font-semibold">Supplier</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {reconciliationData.delivery_history.map((delivery, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm font-mono">
                                    {delivery.custom_order_id || delivery.order_id?.substring(0, 8)}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {delivery.order_date ? new Date(delivery.order_date).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {delivery.delivery_date ? new Date(delivery.delivery_date).toLocaleDateString() : 'N/A'}
                                  </td>
                                  <td className="px-4 py-3 text-sm">{delivery.created_by_name || 'N/A'}</td>
                                  <td className="px-4 py-3 text-sm">{delivery.delivered_by_name || 'N/A'}</td>
                                  <td className="px-4 py-3 text-sm">
                                    <Badge>{delivery.total_items_count || delivery.items?.length || 0}</Badge>
                                  </td>
                                  <td className="px-4 py-3 text-sm">{delivery.supplier || 'N/A'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
                      <p>No material deliveries recorded for this project yet</p>
                      <p className="text-sm mt-2">Deliveries appear here when material orders are marked as "Delivered to Site"</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No reconciliation data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Employee Database Tab - Company Admin Only */}
        {['company_admin', 'general_manager', 'manager'].includes(user.role) && (
        <TabsContent value="employee-database" data-testid="employee-database-content">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Employee Database</CardTitle>
              <Button 
                variant="outline"
                onClick={() => {
                  fetchVisibilitySettings();
                  setShowVisibilitySettingsDialog(true);
                }}
              >
                <Settings className="w-4 h-4 mr-2" />
                Visibility Settings
              </Button>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">
                Manage employee credentials, certificates, licenses, and view documents. Click on an employee to view/edit their full profile.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Name</th>
                      <th className="px-4 py-2 text-left">Role</th>
                      <th className="px-4 py-2 text-left">Email</th>
                      <th className="px-4 py-2 text-left">Documents</th>
                      <th className="px-4 py-2 text-left">Certificates</th>
                      <th className="px-4 py-2 text-left">Licenses</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((emp) => {
                      // Collect all document URLs
                      const allDocs = [
                        ...(emp.certificates || []).filter(c => c.document_url).map(c => ({ url: c.document_url, type: 'cert', name: c.name })),
                        ...(emp.licenses || []).filter(l => l.document_url).map(l => ({ url: l.document_url, type: 'license', name: l.license_type })),
                        ...(emp.credentials || []).filter(c => c.document_url).map(c => ({ url: c.document_url, type: 'cred', name: c.credential_name }))
                      ];
                      const docCount = allDocs.length;
                      const missingCount = (emp.certificates?.length || 0) + (emp.licenses?.length || 0) + (emp.credentials?.length || 0) - docCount;
                      
                      return (
                        <tr key={emp.id} className="border-b hover:bg-gray-50 cursor-pointer">
                          <td className="px-4 py-2 font-medium">{emp.user?.name || emp.name || 'N/A'}</td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                              {emp.role || emp.user?.role || 'employee'}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-sm">{emp.user?.email || 'N/A'}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              {/* Document thumbnails */}
                              <div className="flex -space-x-2">
                                {allDocs.slice(0, 3).map((doc, idx) => (
                                  <div 
                                    key={idx}
                                    className="w-8 h-8 rounded border border-gray-300 bg-gray-100 flex items-center justify-center cursor-pointer hover:z-10 hover:scale-110 transition-transform"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`${API_URL}${doc.url}`, '_blank');
                                    }}
                                    title={doc.name}
                                  >
                                    {doc.url?.endsWith('.pdf') ? (
                                      <FileText className="w-4 h-4 text-red-500" />
                                    ) : (
                                      <img 
                                        src={`${API_URL}${doc.url}`} 
                                        alt={doc.name}
                                        className="w-full h-full object-cover rounded"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.innerHTML = '<svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>';
                                        }}
                                      />
                                    )}
                                  </div>
                                ))}
                                {allDocs.length > 3 && (
                                  <div className="w-8 h-8 rounded border border-gray-300 bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                                    +{allDocs.length - 3}
                                  </div>
                                )}
                              </div>
                              {/* Document count badge */}
                              <span className={`px-2 py-1 rounded text-xs ${docCount > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                                {docCount} docs
                              </span>
                              {missingCount > 0 && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs" title="Documents missing uploads">
                                  {missingCount} missing
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              {emp.certificates?.length || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                              {emp.licenses?.length || 0}
                            </span>
                          </td>
                          <td className="px-4 py-2">
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedEmployeeForProfile(emp.id);
                                fetchEmployeeProfile(emp.id);
                              }}
                            >
                              View Profile
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                    {employees.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                          No employees found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Material Returns Tab */}
        {(['purchaser', 'accounts', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
        <TabsContent value="returns" data-testid="returns-content">
          <Card>
            <CardHeader>
              <CardTitle>Material Return Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {!hasFeature(user, 'material_returns') ? (
                <LockedFeatureSection 
                  featureName="material_returns" 
                  title="Material Returns Management"
                />
              ) : (
                <>
              <div className="overflow-x-auto" ref={returnsTableRef}>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-left">Project</th>
                      <th className="px-4 py-2 text-left">Reason</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialReturns.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No return requests
                        </td>
                      </tr>
                    ) : (
                      materialReturns.map((returnReq) => {
                        const employee = employees.find(e => e.id === returnReq.employee_id);
                        const project = projects.find(p => p.id === returnReq.project_id);
                        return (
                          <tr key={returnReq.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">{employee?.user?.name || employee?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{project?.name || 'Unknown'}</td>
                            <td className="px-4 py-2 text-sm">{returnReq.reason.replace(/_/g, ' ')}</td>
                            <td className="px-4 py-2">{new Date(returnReq.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              <Badge variant={
                                returnReq.status === 'approved' ? 'success' :
                                returnReq.status === 'rejected' ? 'destructive' :
                                'default'
                              }>
                                {returnReq.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              {returnReq.status === 'pending' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600"
                                    onClick={() => handleReviewReturn(returnReq.id, 'approved')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => {
                                      const reason = prompt('Rejection reason:');
                                      if (reason) handleReviewReturn(returnReq.id, 'rejected', reason);
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {returnsScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={returnsScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: returnsTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Material Transfers Tab */}
        {(['purchaser', 'accounts', 'admin', 'company_admin', 'project_manager', 'general_manager'].includes(user.role)) && (
        <TabsContent value="transfers" data-testid="transfers-content">
          <Card>
            <CardHeader>
              <CardTitle>Material Transfer Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {!hasFeature(user, 'material_transfers') ? (
                <LockedFeatureSection 
                  featureName="material_transfers" 
                  title="Material Transfers Management"
                />
              ) : (
                <>
              <div className="overflow-x-auto" ref={transfersTableRef}>
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-2 text-left">Employee</th>
                      <th className="px-4 py-2 text-left">From Project</th>
                      <th className="px-4 py-2 text-left">To Project</th>
                      <th className="px-4 py-2 text-left">Date</th>
                      <th className="px-4 py-2 text-left">Status</th>
                      <th className="px-4 py-2 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {materialTransfers.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No transfer requests
                        </td>
                      </tr>
                    ) : (
                      materialTransfers.map((transfer) => {
                        const employee = employees.find(e => e.id === transfer.employee_id);
                        const sourceProj = projects.find(p => p.id === transfer.source_project_id);
                        const destProj = projects.find(p => p.id === transfer.destination_project_id);
                        return (
                          <tr key={transfer.id} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-2">{employee?.user?.name || employee?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{sourceProj?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{destProj?.name || 'Unknown'}</td>
                            <td className="px-4 py-2">{new Date(transfer.created_at).toLocaleDateString()}</td>
                            <td className="px-4 py-2">
                              <Badge variant={
                                transfer.status === 'approved' ? 'success' :
                                transfer.status === 'rejected' ? 'destructive' :
                                'default'
                              }>
                                {transfer.status}
                              </Badge>
                            </td>
                            <td className="px-4 py-2">
                              {transfer.status === 'pending' && (
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600"
                                    onClick={() => handleReviewTransfer(transfer.id, 'approved')}
                                  >
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-600"
                                    onClick={() => {
                                      const reason = prompt('Rejection reason:');
                                      if (reason) handleReviewTransfer(transfer.id, 'rejected', reason);
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Sticky scrollbar at bottom of viewport */}
              {transfersScroll.showStickyScroll && (
                <div className="sticky-scrollbar-container">
                  <div 
                    className="sticky-scrollbar-track" 
                    ref={transfersScroll.stickyScrollRef}
                  >
                    <div 
                      className="sticky-scrollbar-content"
                      style={{ width: transfersTableRef.current?.scrollWidth + 'px' }}
                    />
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* Notification Settings Tab */}
        {!['purchaser', 'accounts'].includes(user.role) && (
        <TabsContent value="notification-settings" data-testid="notification-settings-content">
          <div className="space-y-6">
            <ShiftReminderSettings />
            
            {/* App Update Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <RefreshCw className="w-5 h-5" />
                  App Update
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Current Version</p>
                      <p className="font-semibold">v{getAppVersion()}</p>
                    </div>
                    <Button 
                      onClick={async () => {
                        toast.info('Updating app... Please wait');
                        await forceAppUpdate();
                      }}
                      variant="outline"
                      className="flex items-center gap-2"
                      data-testid="force-update-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Force Update App
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">
                    Click "Force Update App" to clear cache and get the latest version. 
                    Use this if you're experiencing issues or not seeing recent changes.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Reset Time Entries - For Testing Only */}
            {['company_admin', 'accounts', 'accounts'].includes(user.role) && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <Trash2 className="w-5 h-5" />
                  Reset Time Entries (Testing)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    Delete ALL time entries for your company. This resets all hours to zero.
                    <br />
                    <strong className="text-red-600">Warning: This action cannot be undone!</strong>
                  </p>
                  <Button 
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to delete ALL time entries? This action cannot be undone!')) {
                        const token = localStorage.getItem('token');
                        try {
                          const response = await fetch(`${API_URL}/api/admin/time-entries/reset`, {
                            method: 'DELETE',
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          if (response.ok) {
                            const data = await response.json();
                            toast.success(`Successfully reset ${data.deleted_count} time entries`);
                            window.location.reload();
                          } else {
                            toast.error('Failed to reset time entries');
                          }
                        } catch (error) {
                          toast.error('Connection error');
                        }
                      }
                    }}
                    variant="destructive"
                    className="flex items-center gap-2"
                    data-testid="reset-time-entries-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    Reset All Time Entries
                  </Button>
                </div>
              </CardContent>
            </Card>
            )}
          </div>
        </TabsContent>
        )}

      </Tabs>

      {/* Details Dialog - Dashboard Stats Cards */}
      <StatsDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        detailsType={detailsType}
        detailsData={detailsData}
        detailsLoading={detailsLoading}
        projects={projects}
        materialOrders={materialOrders}
        employees={employees}
        onViewOrderDetails={handleViewOrderDetails}
        formatDateDisplay={formatDateDisplay}
      />

      {/* Edit Stock Dialog */}
      <Dialog open={showEditStockDialog} onOpenChange={setShowEditStockDialog}>
        <DialogContent data-testid="edit-stock-dialog">
          <DialogHeader>
            <DialogTitle>Edit Stock - {editingMaterial?.name}</DialogTitle>
          </DialogHeader>
          <div className="edit-stock-content">
            <div className="current-stock">
              <Label>Current Stock</Label>
              <div className="stock-display">{editingMaterial?.stock_quantity} {editingMaterial?.unit}</div>
            </div>

            <div className="stock-adjustment">
              <Label>Adjust Stock</Label>
              <div className="adjustment-controls">
                <Button 
                  variant="outline" 
                  onClick={() => setStockAdjustment(Math.max(-(editingMaterial?.stock_quantity || 0), stockAdjustment - 1))}
                  data-testid="stock-down-btn"
                >
                  -
                </Button>
                <Input 
                  type="number" 
                  value={stockAdjustment}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    setStockAdjustment(Math.max(-(editingMaterial?.stock_quantity || 0), val));
                  }}
                  className="adjustment-input"
                  data-testid="stock-adjustment-input"
                />
                <Button 
                  variant="outline" 
                  onClick={() => setStockAdjustment(stockAdjustment + 1)}
                  data-testid="stock-up-btn"
                >
                  +
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {stockAdjustment > 0 && <>Stock Up: +{stockAdjustment}</>}
                {stockAdjustment < 0 && <>Stock Down: {stockAdjustment}</>}
                {stockAdjustment === 0 && <>No change</>}
              </p>
            </div>

            <div className="new-stock">
              <Label>New Stock</Label>
              <div className="stock-display new">
                {(editingMaterial?.stock_quantity || 0) + stockAdjustment} {editingMaterial?.unit}
              </div>
            </div>

            <div className="dialog-actions">
              <Button variant="outline" onClick={() => setShowEditStockDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleUpdateStock}
                disabled={stockAdjustment === 0 || loading}
                data-testid="save-stock-btn"
              >
                Update Stock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Management Dialog */}
      <Dialog open={showStockManagementDialog} onOpenChange={setShowStockManagementDialog}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Stock Management</DialogTitle>
          </DialogHeader>
          <StockManagementForm
            projects={projects}
            onSubmit={handleSubmitStockMovement}
            onCancel={() => setShowStockManagementDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Export Order Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Export Material Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Copy the text below and paste it into your email or procurement system:
            </p>
            <div className="relative">
              <textarea
                value={exportText}
                readOnly
                className="w-full h-96 p-4 font-mono text-sm border rounded bg-gray-50"
                onClick={(e) => e.target.select()}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowExportDialog(false)}>
                Close
              </Button>
              <Button onClick={handleCopyExport}>
                📋 Copy to Clipboard
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assign Projects & Team - {assigningUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {/* Project Assignment - For Project Managers, Team Leads, Project Coordinators */}
            {assigningUser && ['project_manager', 'team_lead', 'project_coordinator'].includes(assigningUser.role) && (
              <div>
                <Label className="text-lg font-semibold mb-3 block">Assign Projects</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  {projects.map(project => (
                    <div key={project.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`project-${project.id}`}
                        checked={selectedProjectsForAssignment.includes(project.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedProjectsForAssignment([...selectedProjectsForAssignment, project.id]);
                          } else {
                            setSelectedProjectsForAssignment(selectedProjectsForAssignment.filter(id => id !== project.id));
                          }
                        }}
                        className="cursor-pointer"
                      />
                      <label htmlFor={`project-${project.id}`} className="cursor-pointer flex-1">
                        <span className="font-medium">{project.name}</span>
                        <span className="text-sm text-gray-500 ml-2">{project.address}</span>
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedProjectsForAssignment.length} project(s) selected
                </p>
              </div>
            )}

            {/* Team Assignment - Only for Team Leads */}
            {assigningUser && assigningUser.role === 'team_lead' && (
              <div>
                <Label className="text-lg font-semibold mb-3 block">Assign Team Members</Label>
                <div className="space-y-2 max-h-64 overflow-y-auto border rounded p-3">
                  {employees
                    .filter(emp => emp.user?.role === 'employee' || emp.user?.role === 'subcontractor')
                    .map(emp => (
                      <div key={emp.user.id} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id={`team-${emp.user.id}`}
                          checked={selectedTeamMembers.includes(emp.user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTeamMembers([...selectedTeamMembers, emp.user.id]);
                            } else {
                              setSelectedTeamMembers(selectedTeamMembers.filter(id => id !== emp.user.id));
                            }
                          }}
                          className="cursor-pointer"
                        />
                        <label htmlFor={`team-${emp.user.id}`} className="cursor-pointer flex-1">
                          <span className="font-medium">{emp.user.name}</span>
                          <span className="text-sm text-gray-500 ml-2">{emp.position}</span>
                        </label>
                      </div>
                    ))}
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  {selectedTeamMembers.length} team member(s) selected
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveAssignments} disabled={loading}>
                Save Assignments
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Details Dialog */}
      <OrderDetailsDialog
        open={showOrderDetailsDialog}
        onOpenChange={setShowOrderDetailsDialog}
        selectedOrder={selectedOrder}
        projects={projects}
        user={user}
        orderComment={orderComment}
        setOrderComment={setOrderComment}
        onUpdateOrderStatus={handleUpdateOrderStatus}
        onCancelOrder={handleCancelOrder}
        onDeleteOrder={handleDeleteOrder}
        onExportOrder={handleExportOrder}
        onAddOrderComment={handleAddOrderComment}
        onShowDeliveryPhoto={setShowDeliveryPhotoDialog}
        onShowAddCost={setShowAddCostDialog}
        setCostForm={setCostForm}
      />
      {/* Project Details Dialog */}
      <Dialog open={showProjectDetailsDialog} onOpenChange={setShowProjectDetailsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Project Details - {selectedProjectDetails?.name}</DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditProjectForm({
                    name: selectedProjectDetails.name,
                    address: selectedProjectDetails.address,
                    start_date: selectedProjectDetails.start_date || '',
                    client_id: selectedProjectDetails.client_id || null,
                    status: selectedProjectDetails.status || 'active',
                    stage: selectedProjectDetails.stage || 'construction',
                    shift_start: selectedProjectDetails.shift_start || '',
                    shift_end: selectedProjectDetails.shift_end || '',
                    auto_clockout_hours: selectedProjectDetails.auto_clockout_hours || '',
                    latitude: selectedProjectDetails.latitude || '',
                    longitude: selectedProjectDetails.longitude || '',
                    geofence_radius: selectedProjectDetails.geofence_radius || ''
                  });
                  setShowEditProjectDialog(true);
                }}
              >
                ✏️ Edit Project
              </Button>
            </div>
          </DialogHeader>
          {selectedProjectDetails && (
            <div className="project-details-content">
              <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 rounded">
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Project Name</Label>
                  <p className="font-semibold text-lg">{selectedProjectDetails.name}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-600">Address</Label>
                  <p className="text-gray-700">{selectedProjectDetails.address}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Start Date</Label>
                  <p className="font-semibold">
                    {selectedProjectDetails.start_date ? new Date(selectedProjectDetails.start_date).toLocaleDateString() : 'Not set'}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <Badge variant={selectedProjectDetails.is_active ? 'success' : 'default'}>
                    {selectedProjectDetails.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
              </div>

              {/* Team Members */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3">Team Members ({selectedProjectDetails.team_members?.length || 0})</h3>
                <div className="grid grid-cols-2 gap-2">
                  {selectedProjectDetails.team_members?.map((memberId) => {
                    const emp = employees.find(e => e.user_id === memberId);
                    const assignment = selectedProjectDetails.team_assignments?.find(a => a.user_id === memberId);
                    return (
                      <div key={memberId} className="p-3 border rounded bg-white">
                        <p className="font-semibold">{emp?.user?.name || emp?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-600">{emp?.position || 'N/A'}</p>
                        {assignment?.start_time && assignment?.end_time && (
                          <p className="text-xs text-gray-500 mt-1">
                            {assignment.start_time} - {assignment.end_time}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {(!selectedProjectDetails.team_members || selectedProjectDetails.team_members.length === 0) && (
                    <p className="text-gray-500 col-span-2">No team members assigned yet</p>
                  )}
                </div>
              </div>

              {/* Sub-projects Section */}
              {['admin', 'company_admin', 'general_manager', 'project_manager', 'project_coordinator'].includes(user.role) && (
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-lg font-semibold">
                      Sub-projects ({subProjects.length})
                      {!hasFeature(user, 'sub_projects') && <Badge variant="secondary" className="ml-2 text-xs">🔒 Big Business+</Badge>}
                    </h3>
                    {hasFeature(user, 'sub_projects') ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedProjectForSubProject(selectedProjectDetails);
                          setShowSubProjectDialog(true);
                          fetchSubProjects(selectedProjectDetails.id);
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Sub-project
                      </Button>
                    ) : (
                      <LockedFeatureButton featureName="sub_projects" size="sm">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Sub-project
                      </LockedFeatureButton>
                    )}
                  </div>
                  <div className="space-y-2">
                    {subProjects.length === 0 ? (
                      <p className="text-gray-500 text-sm">No sub-projects yet</p>
                    ) : (
                      subProjects.map((subProject) => (
                        <div key={subProject.id} className="p-4 border rounded bg-white">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-base">{subProject.name}</h4>
                              {subProject.description && (
                                <p className="text-sm text-gray-600 mt-1">{subProject.description}</p>
                              )}
                              <div className="flex gap-4 mt-2 text-sm">
                                {subProject.budget && (
                                  <div>
                                    <span className="text-gray-600">Budget:</span>
                                    <span className="font-semibold ml-1">${subProject.budget.toLocaleString()}</span>
                                  </div>
                                )}
                                {subProject.start_date && (
                                  <div>
                                    <span className="text-gray-600">Start:</span>
                                    <span className="ml-1">{new Date(subProject.start_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                                {subProject.end_date && (
                                  <div>
                                    <span className="text-gray-600">End:</span>
                                    <span className="ml-1">{new Date(subProject.end_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                              <div className="mt-2">
                                <Badge variant={subProject.status === 'active' ? 'success' : 'default'}>
                                  {subProject.status}
                                </Badge>
                                {subProject.team_members.length > 0 && (
                                  <span className="text-xs text-gray-600 ml-2">
                                    {subProject.team_members.length} team member(s)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditSubProjectDialog(subProject)}
                              >
                                ✏️
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteSubProject(subProject.id)}
                              >
                                🗑️
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* Equipment */}
              {/* Equipment Section */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">
                    Equipment ({selectedProjectDetails.equipment?.length || 0})
                    {!hasFeature(user, 'equipment_management') && <Badge variant="secondary" className="ml-2 text-xs">🔒 Big Business+</Badge>}
                  </h3>
                  {hasFeature(user, 'equipment_management') ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowEquipmentDialog(true)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Equipment
                    </Button>
                  ) : (
                    <LockedFeatureButton featureName="equipment_management" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Equipment
                    </LockedFeatureButton>
                  )}
                </div>
                {hasFeature(user, 'equipment_management') ? (
                  <div className="grid grid-cols-2 gap-2">
                    {selectedProjectDetails.equipment?.map((equip, idx) => (
                      <div key={idx} className="p-3 border rounded bg-white flex justify-between items-center">
                        <div>
                          <p className="font-semibold">{equip.type}</p>
                          {equip.custom_description && (
                            <p className="text-sm text-gray-600">{equip.custom_description}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEquipmentFromProject(equip.equipment_id)}
                        >
                          ❌
                        </Button>
                      </div>
                    ))}
                    {(!selectedProjectDetails.equipment || selectedProjectDetails.equipment.length === 0) && (
                      <p className="text-gray-500 col-span-2">No equipment assigned yet</p>
                    )}
                  </div>
                ) : (
                  <div className="p-4 border-2 border-dashed rounded text-center">
                    <p className="text-sm text-gray-600">
                      🔒 Equipment management requires Big Business plan or higher
                    </p>
                  </div>
                )}
              </div>

              {/* Project Progress Reports */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <ClipboardList className="w-5 h-5" />
                  Progress Reports
                </h3>
                <ProjectProgressViewer
                  projects={projects}
                  projectId={selectedProjectDetails?.id}
                  canDelete={['company_admin', 'general_manager'].includes(user.role)}
                  embedded
                />
              </div>

              {/* Project Communication */}
              <div className="border-t pt-4">
                <h3 className="text-lg font-semibold mb-3">Project Communication</h3>
                
                {/* Display project messages */}
                <div className="mb-4 max-h-96 overflow-y-auto space-y-2 bg-gray-50 p-3 rounded">
                  {projectMessages.length > 0 ? (
                    projectMessages.map((msg, idx) => (
                      <div key={idx} className="p-3 bg-white rounded border">
                        <div className="flex justify-between items-start mb-1">
                          <p className="font-semibold text-sm">{msg.sender_name}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleString()}
                          </p>
                        </div>
                        
                        {/* Show if replying to someone */}
                        {msg.reply_to_sender && (
                          <div className="mb-2 pl-3 border-l-2 border-blue-400 bg-blue-50 p-2 rounded text-xs">
                            <p className="font-semibold text-blue-700">Replying to {msg.reply_to_sender}</p>
                            <p className="text-gray-600 italic">{msg.reply_to_message}</p>
                          </div>
                        )}
                        
                        <p className="text-sm text-gray-700">{msg.message}</p>
                        
                        {/* Display video if present */}
                        {msg.video_url && (
                          <div className="mt-2">
                            <video 
                              controls
                              className="max-w-md rounded border"
                              src={`${API_URL}${msg.video_url}`}
                            >
                              Your browser does not support the video tag.
                            </video>
                          </div>
                        )}
                        
                        {/* Display images if present */}
                        {msg.image_urls && msg.image_urls.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {msg.image_urls.map((url, imgIdx) => {
                              const filename = url.split('/').pop();
                              const displayUrl = `${API_URL}/api/image/${filename}`;
                              return (
                                <img 
                                  key={imgIdx}
                                  src={displayUrl}
                                  alt="Uploaded"
                                  className="w-32 h-32 object-cover rounded border cursor-pointer hover:opacity-80"
                                  onClick={() => {
                                    setViewerImages(msg.image_urls);
                                    setViewerIndex(imgIdx);
                                    setShowImageViewer(true);
                                  }}
                                />
                              );
                            })}
                          </div>
                        )}
                        
                        {/* Reply Button */}
                        <button
                          onClick={() => {
                            setReplyingTo(msg);
                            setProjectMessageText('');
                            // Scroll to input
                            document.querySelector('#project-message-input')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                        >
                          ↩️ Reply
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-500 py-4">No messages yet</p>
                  )}
                </div>

                {/* Send project message */}
                <form onSubmit={handleSendProjectMessage} className="space-y-2">
                  {/* Show who we're replying to */}
                  {replyingTo && (
                    <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded">
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-blue-700">Replying to {replyingTo.sender_name}</p>
                        <p className="text-xs text-gray-600 italic">{replyingTo.message.substring(0, 60)}...</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setReplyingTo(null)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  
                  <Textarea
                    id="project-message-input"
                    value={projectMessageText}
                    onChange={(e) => setProjectMessageText(e.target.value)}
                    placeholder={replyingTo ? `Reply to ${replyingTo.sender_name}...` : "Type a message to project team..."}
                    rows={3}
                  />
                  
                  {/* Image upload */}
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      id="project-images"
                      multiple
                      accept="image/*"
                      onChange={(e) => setSelectedImages(Array.from(e.target.files))}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('project-images').click()}
                    >
                      📷 Add Images ({selectedImages.length})
                    </Button>
                    
                    {/* Video upload */}
                    <input
                      type="file"
                      id="project-video"
                      accept="video/*"
                      onChange={(e) => setSelectedVideo(e.target.files[0])}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('project-video').click()}
                    >
                      🎥 Add Video {selectedVideo ? '(1)' : ''}
                    </Button>
                    
                    <Button 
                      type="submit"
                      disabled={uploadingImages || uploadingVideo || loading || !projectMessageText.trim()}
                      size="sm"
                    >
                      {uploadingImages || uploadingVideo ? 'Uploading...' : 'Send Message'}
                    </Button>
                  </div>
                  
                  {/* Video preview */}
                  {selectedVideo && (
                    <div className="bg-gray-100 p-2 rounded">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Video: {selectedVideo.name}</span>
                        <button
                          type="button"
                          onClick={() => setSelectedVideo(null)}
                          className="bg-red-500 text-white rounded px-2 py-1 text-xs"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Image preview */}
                  {selectedImages.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {selectedImages.map((file, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={URL.createObjectURL(file)}
                            alt="Preview"
                            className="w-20 h-20 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => setSelectedImages(selectedImages.filter((_, i) => i !== idx))}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </form>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => {
                  setShowProjectDetailsDialog(false);
                  setProjectMessageText('');
                  setSelectedImages([]);
                }}>Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delivery Photo Upload Dialog */}
      <Dialog open={showDeliveryPhotoDialog} onOpenChange={setShowDeliveryPhotoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Received at Site</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Please upload photos to verify delivery:</p>
            
            <div>
              <Label>1. Delivery Slip Photo</Label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    setDeliveryPhotos(prev => [...prev, e.target.files[0]]);
                  }
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            
            <div>
              <Label>2. Received Material Photos</Label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setDeliveryPhotos(prev => [...prev, ...files]);
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
            
            {deliveryPhotos.length > 0 && (
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold mb-2">Selected Photos ({deliveryPhotos.length}):</p>
                <ul className="text-sm text-gray-600">
                  {deliveryPhotos.map((file, idx) => (
                    <li key={idx} className="flex justify-between items-center">
                      <span>{file.name}</span>
                      <button
                        onClick={() => setDeliveryPhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-500 text-xs"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowDeliveryPhotoDialog(false);
                  setDeliveryPhotos([]);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleMarkReceivedAtSite}
                disabled={uploadingDeliveryPhotos}
              >
                {uploadingDeliveryPhotos ? 'Uploading...' : deliveryPhotos.length > 0 ? 'Upload & Mark Received' : 'Mark Received (No Photos)'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={showEditProjectDialog} onOpenChange={setShowEditProjectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Project</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditProject} className="space-y-4">
            <div>
              <Label>Project Name *</Label>
              <Input
                value={editProjectForm.name}
                onChange={(e) => setEditProjectForm({...editProjectForm, name: e.target.value})}
                required
              />
            </div>
            <div>
              <Label>Address *</Label>
              <Input
                value={editProjectForm.address}
                onChange={(e) => setEditProjectForm({...editProjectForm, address: e.target.value})}
                required
              />
            </div>

            {/* GPS Coordinates - for manual override */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Latitude</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g. 43.6532"
                  value={editProjectForm.latitude}
                  onChange={(e) => { setEditProjectForm({...editProjectForm, latitude: e.target.value}); setCoordDiscrepancy(null); }}
                  data-testid="project-latitude"
                />
              </div>
              <div>
                <Label className="text-xs">Longitude</Label>
                <Input
                  type="number"
                  step="any"
                  placeholder="e.g. -79.3832"
                  value={editProjectForm.longitude}
                  onChange={(e) => { setEditProjectForm({...editProjectForm, longitude: e.target.value}); setCoordDiscrepancy(null); }}
                  data-testid="project-longitude"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 -mt-2">
              <p className="text-xs text-gray-400 flex-1">Get coordinates from Google Maps (right-click a spot → copy coordinates).</p>
              {editProjectForm.latitude && editProjectForm.longitude && editProjectForm.address && (
                <Button type="button" variant="outline" size="sm" className="text-xs h-7" onClick={verifyCoordinates} disabled={verifyingCoords}>
                  {verifyingCoords ? 'Checking...' : 'Verify'}
                </Button>
              )}
            </div>
            {coordDiscrepancy && !coordDiscrepancy.match && !coordDiscrepancy.noResult && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 -mt-1" data-testid="coord-discrepancy-warning">
                <p className="text-sm font-medium text-amber-800 mb-1">Location Discrepancy Detected</p>
                <p className="text-xs text-amber-700">
                  Your coordinates are <strong>{(coordDiscrepancy.distance / 1000).toFixed(1)} km</strong> away from the geocoded address.
                </p>
                <p className="text-xs text-amber-600 mt-1">Address resolves to: {coordDiscrepancy.geoDisplay}</p>
                <p className="text-xs text-amber-800 font-medium mt-2">Manually entered coordinates will be used for geofencing (supersedes address).</p>
              </div>
            )}
            {coordDiscrepancy && coordDiscrepancy.match && (
              <div className="bg-green-50 border border-green-300 rounded-lg p-2 -mt-1">
                <p className="text-xs text-green-700">Coordinates match the address ({coordDiscrepancy.distance}m difference).</p>
              </div>
            )}
            {coordDiscrepancy && coordDiscrepancy.noResult && (
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-2 -mt-1">
                <p className="text-xs text-gray-600">Could not geocode the address for comparison. Manually entered coordinates will be used.</p>
              </div>
            )}

            {/* Geofence Radius */}
            <div>
              <Label className="text-xs">Geofence Radius (meters)</Label>
              <Input
                type="number"
                min="50"
                max="5000"
                step="50"
                placeholder="Default: 150m"
                value={editProjectForm.geofence_radius}
                onChange={(e) => setEditProjectForm({...editProjectForm, geofence_radius: e.target.value})}
                data-testid="project-geofence-radius"
              />
              <p className="text-xs text-gray-400 mt-1">Employees must be within this distance to clock in. Default 150m. Increase for large sites (factories, campuses).</p>
            </div>
            
            {/* Client Selection */}
            <div>
              <Label>Client (Optional)</Label>
              <Select
                value={editProjectForm.client_id || "none"}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, client_id: value === "none" ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a client" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.client_name} ({client.client_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={editProjectForm.start_date}
                onChange={(e) => setEditProjectForm({...editProjectForm, start_date: e.target.value})}
              />
            </div>
            
            {/* Project Status */}
            <div>
              <Label>Project Status</Label>
              <Select
                value={editProjectForm.status}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="future">Future</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Project Stage */}
            <div>
              <Label>Project Stage</Label>
              <Select
                value={editProjectForm.stage || 'construction'}
                onValueChange={(value) => setEditProjectForm({ ...editProjectForm, stage: value })}
              >
                <SelectTrigger data-testid="edit-project-stage-select">
                  <SelectValue placeholder="Select stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_construction">
                    <div className="flex items-center gap-2">
                      <span>📋</span>
                      <span>Pre-Construction</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="construction">
                    <div className="flex items-center gap-2">
                      <span>🏗️</span>
                      <span>Construction</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Pre-construction: visible to office staff only
              </p>
            </div>

            {/* Project Shift Override */}
            <div className="border-t pt-3">
              <Label className="text-sm font-semibold mb-1 block">Shift Settings (Override Company Default)</Label>
              <p className="text-xs text-gray-500 mb-2">Leave blank to use company default shift times.</p>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Shift Start</Label>
                  <Input
                    type="time"
                    value={editProjectForm.shift_start}
                    onChange={(e) => setEditProjectForm({...editProjectForm, shift_start: e.target.value})}
                    placeholder="Company default"
                    data-testid="project-shift-start"
                  />
                </div>
                <div>
                  <Label className="text-xs">Shift End</Label>
                  <Input
                    type="time"
                    value={editProjectForm.shift_end}
                    onChange={(e) => setEditProjectForm({...editProjectForm, shift_end: e.target.value})}
                    placeholder="Company default"
                    data-testid="project-shift-end"
                  />
                </div>
                <div>
                  <Label className="text-xs">Auto Clock-Out (hrs)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    step="0.5"
                    value={editProjectForm.auto_clockout_hours}
                    onChange={(e) => setEditProjectForm({...editProjectForm, auto_clockout_hours: e.target.value})}
                    placeholder="Company default"
                    data-testid="project-auto-clockout"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Sub-project Dialog */}
      <Dialog open={showSubProjectDialog} onOpenChange={setShowSubProjectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Sub-project for {selectedProjectForSubProject?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSubProject} className="space-y-4">
            <div>
              <Label>Sub-project Name *</Label>
              <Input
                value={subProjectForm.name}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, name: e.target.value })}
                required
                placeholder="e.g., Phase 1, Building A, Floor 2"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[80px]"
                value={subProjectForm.description}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, description: e.target.value })}
                placeholder="Brief description of this sub-project"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subProjectForm.budget}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, budget: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={subProjectForm.status || 'active'}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.start_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.end_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Assign Team Members</Label>
              <div className="border rounded p-3 max-h-48 overflow-y-auto">
                {employees.filter(emp => 
                  selectedProjectForSubProject?.team_members?.includes(emp.user_id)
                ).map((emp) => (
                  <label key={emp.user_id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={subProjectForm.team_members.includes(emp.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: [...subProjectForm.team_members, emp.user_id]
                          });
                        } else {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: subProjectForm.team_members.filter(id => id !== emp.user_id)
                          });
                        }
                      }}
                    />
                    <span>{emp.user?.name || emp.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowSubProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Create Sub-project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Sub-project Dialog */}
      <Dialog open={showEditSubProjectDialog} onOpenChange={setShowEditSubProjectDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Sub-project: {editingSubProject?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubProject} className="space-y-4">
            <div>
              <Label>Sub-project Name *</Label>
              <Input
                value={subProjectForm.name}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, name: e.target.value })}
                required
                placeholder="e.g., Phase 1, Building A, Floor 2"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <textarea
                className="w-full border rounded px-3 py-2 min-h-[80px]"
                value={subProjectForm.description}
                onChange={(e) => setSubProjectForm({ ...subProjectForm, description: e.target.value })}
                placeholder="Brief description of this sub-project"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Budget ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={subProjectForm.budget}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, budget: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={subProjectForm.status || 'active'}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="on_hold">On Hold</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.start_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={subProjectForm.end_date}
                  onChange={(e) => setSubProjectForm({ ...subProjectForm, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label>Assign Team Members</Label>
              <div className="border rounded p-3 max-h-48 overflow-y-auto">
                {employees.filter(emp => 
                  selectedProjectForSubProject?.team_members?.includes(emp.user_id)
                ).map((emp) => (
                  <label key={emp.user_id} className="flex items-center gap-2 py-1">
                    <input
                      type="checkbox"
                      checked={subProjectForm.team_members.includes(emp.user_id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: [...subProjectForm.team_members, emp.user_id]
                          });
                        } else {
                          setSubProjectForm({
                            ...subProjectForm,
                            team_members: subProjectForm.team_members.filter(id => id !== emp.user_id)
                          });
                        }
                      }}
                    />
                    <span>{emp.user?.name || emp.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowEditSubProjectDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                Update Sub-project
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Equipment to Project Dialog */}
      <Dialog open={showEquipmentDialog} onOpenChange={setShowEquipmentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Equipment to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Select available equipment to add to this project:</p>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {equipment.filter(e => e.status === 'available').map((equip) => (
                <div
                  key={equip.id}
                  className="p-3 border rounded hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                  onClick={() => {
                    handleAddEquipmentToProject(equip.id);
                    setShowEquipmentDialog(false);
                  }}
                >
                  <div>
                    <p className="font-semibold">{equip.type}</p>
                    {equip.custom_description && (
                      <p className="text-sm text-gray-600">{equip.custom_description}</p>
                    )}
                  </div>
                  <Badge variant="success">Available</Badge>
                </div>
              ))}
              {equipment.filter(e => e.status === 'available').length === 0 && (
                <p className="text-gray-500 text-center py-4">No available equipment. All equipment is currently in use.</p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Company Settings Dialog */}
      <CompanySettingsDialog
        open={showCompanySettingsDialog}
        onOpenChange={setShowCompanySettingsDialog}
        companySettings={companySettings}
        setCompanySettings={setCompanySettings}
        user={user}
        onSaved={() => { fetchCompanySettings(); fetchAllData(); }}
      />

      {/* Add Cost Dialog */}
      <Dialog open={showAddCostDialog} onOpenChange={setShowAddCostDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Order Cost Details</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddCost} className="space-y-4 py-4">
            <div>
              <Label htmlFor="order_cost">Order Cost (without HST) *</Label>
              <Input
                id="order_cost"
                type="number"
                step="0.01"
                min="0"
                value={costForm.order_cost}
                onChange={(e) => setCostForm({ ...costForm, order_cost: e.target.value })}
                placeholder="0.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Total cost of all items excluding HST
              </p>
            </div>
            
            <div>
              <Label htmlFor="delivery_charges">Delivery Charges *</Label>
              <Input
                id="delivery_charges"
                type="number"
                step="0.01"
                min="0"
                value={costForm.delivery_charges}
                onChange={(e) => setCostForm({ ...costForm, delivery_charges: e.target.value })}
                placeholder="0.00"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Delivery charges for this order
              </p>
            </div>

            {/* Total Cost Preview */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <Label className="text-sm font-semibold text-blue-800">Total Cost Preview</Label>
              <p className="text-2xl font-bold text-blue-900">
                ${(
                  (parseFloat(costForm.order_cost) || 0) + 
                  (parseFloat(costForm.delivery_charges) || 0)
                ).toFixed(2)}
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddCostDialog(false);
                  setCostForm({ order_cost: '', delivery_charges: '' });
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Cost'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Export Dialogs - Using Extracted Components */}
      <PayrollExportDialog
        open={showPayrollExportDialog}
        onOpenChange={setShowPayrollExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportPayroll}
      />

      <ProjectComparisonDialog
        open={showProjectComparisonDialog}
        onOpenChange={setShowProjectComparisonDialog}
        projects={projects}
      />

      <ExpenseExportDialog
        open={showExpenseExportDialog}
        onOpenChange={setShowExpenseExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        projects={projects}
        onExport={handleExportProjectExpenses}
      />

      <MaterialOrdersExportDialog
        open={showMaterialOrdersExportDialog}
        onOpenChange={setShowMaterialOrdersExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportMaterialOrders}
      />

      <CommunicationsExportDialog
        open={showCommunicationsExportDialog}
        onOpenChange={setShowCommunicationsExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportCommunications}
      />

      <StockExportDialog
        open={showStockExportDialog}
        onOpenChange={setShowStockExportDialog}
        exportForm={exportForm}
        setExportForm={setExportForm}
        onExport={handleExportStockMovements}
      />

      {/* Employee Profile Dialog */}
      <Dialog open={showEmployeeProfileDialog} onOpenChange={setShowEmployeeProfileDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Employee Profile: {employeeProfile?.user?.name || employeeProfile?.name || 'Loading...'}
            </DialogTitle>
          </DialogHeader>
          {employeeProfile && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-gray-500">Email</Label>
                  <p className="font-medium">{employeeProfile.user?.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Phone</Label>
                  <p className="font-medium">{employeeProfile.user?.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Role</Label>
                  <p className="font-medium capitalize">{employeeProfile.role || employeeProfile.user?.role || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hourly Rate</Label>
                  <p className="font-medium">${employeeProfile.hourly_rate || 0}/hr</p>
                </div>
                <div>
                  <Label className="text-gray-500">YTD Hours</Label>
                  <p className="font-medium text-blue-600">{employeeProfile.ytd_hours || 0} hours</p>
                </div>
                <div>
                  <Label className="text-gray-500">Hire Date</Label>
                  <p className="font-medium">{employeeProfile.hire_date || 'N/A'}</p>
                </div>
              </div>

              {/* Certificates Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">📜 Certificates</h3>
                  <Button size="sm" onClick={() => setShowAddCertificateDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Certificate
                  </Button>
                </div>
                {employeeProfile.certificates?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.certificates.map((cert) => (
                      <div key={cert.id} className="flex justify-between items-center p-3 bg-green-50 rounded">
                        <div>
                          <p className="font-medium">{cert.name}</p>
                          <p className="text-sm text-gray-600">
                            {cert.issuing_authority} • #{cert.certificate_number}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {cert.expiry_date || 'No expiry'}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteCertificate(employeeProfile.id, cert.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No certificates added</p>
                )}
              </div>

              {/* Licenses Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">🪪 Licenses</h3>
                  <Button size="sm" onClick={() => setShowAddLicenseDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add License
                  </Button>
                </div>
                {employeeProfile.licenses?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.licenses.map((lic) => (
                      <div key={lic.id} className="flex justify-between items-center p-3 bg-purple-50 rounded">
                        <div>
                          <p className="font-medium">{lic.license_type}</p>
                          <p className="text-sm text-gray-600">
                            #{lic.license_number} • {lic.state || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {lic.expiry_date || 'No expiry'}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteLicense(employeeProfile.id, lic.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No licenses added</p>
                )}
              </div>

              {/* Credentials Section */}
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-lg">🏅 Credentials</h3>
                  <Button size="sm" onClick={() => setShowAddCredentialDialog(true)}>
                    <Plus className="w-4 h-4 mr-1" /> Add Credential
                  </Button>
                </div>
                {employeeProfile.credentials?.length > 0 ? (
                  <div className="space-y-2">
                    {employeeProfile.credentials.map((cred) => (
                      <div key={cred.id} className="flex justify-between items-center p-3 bg-blue-50 rounded">
                        <div>
                          <p className="font-medium">{cred.credential_type}</p>
                          <p className="text-sm text-gray-600">
                            #{cred.credential_number || 'N/A'}
                          </p>
                          <p className="text-xs text-gray-500">
                            Expires: {cred.expiry_date || 'No expiry'} {cred.notes && `• ${cred.notes}`}
                          </p>
                        </div>
                        <Button size="sm" variant="destructive" onClick={() => deleteCredential(employeeProfile.id, cred.id)}>
                          Delete
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No credentials added</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Certificate/License/Credential Dialogs - Using Extracted Components */}
      <AddCertificateDialog
        open={showAddCertificateDialog}
        onOpenChange={setShowAddCertificateDialog}
        newCertificate={newCertificate}
        setNewCertificate={setNewCertificate}
        onSave={addCertificate}
        onFileUpload={(e) => handleDocumentUpload(e, 'certificate')}
        isUploading={isUploadingDocument}
      />

      <AddLicenseDialog
        open={showAddLicenseDialog}
        onOpenChange={setShowAddLicenseDialog}
        newLicense={newLicense}
        setNewLicense={setNewLicense}
        onSave={addLicense}
        onFileUpload={(e) => handleDocumentUpload(e, 'license')}
        isUploading={isUploadingDocument}
      />

      <AddCredentialDialog
        open={showAddCredentialDialog}
        onOpenChange={setShowAddCredentialDialog}
        newCredential={newCredential}
        setNewCredential={setNewCredential}
        onSave={addCredential}
      />

      {/* Visibility Settings Dialog */}
      <Dialog open={showVisibilitySettingsDialog} onOpenChange={setShowVisibilitySettingsDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Employee Data Visibility Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-gray-600">
              Control what employee information managers and accounts roles can see.
            </p>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Certificates to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_certificates_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_certificates_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Licenses to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_licenses_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_licenses_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Credentials to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_credentials_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_credentials_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Hourly Rate to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_hourly_rate_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_hourly_rate_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show YTD Hours to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_ytd_hours_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_ytd_hours_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <Label>Show Contact Info to Managers</Label>
                <input
                  type="checkbox"
                  checked={visibilitySettings.show_contact_info_to_managers}
                  onChange={(e) => setVisibilitySettings({...visibilitySettings, show_contact_info_to_managers: e.target.checked})}
                  className="w-5 h-5"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowVisibilitySettingsDialog(false)}>Cancel</Button>
              <Button onClick={saveVisibilitySettings}>Save Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
        <DialogContent className="max-w-4xl">
          <div className="relative">
            {viewerImages.length > 0 && (
              <>
                <img 
                  src={`${API_URL}/api/image/${viewerImages[viewerIndex].split('/').pop()}`}
                  alt="View" 
                  className="w-full h-auto max-h-[70vh] object-contain"
                  onError={(e) => {
                    console.error('Image failed to load:', viewerImages[viewerIndex]);
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><text x="50%" y="50%" text-anchor="middle">Image not found</text></svg>';
                  }}
                />
                
                {/* Navigation Arrows */}
                {viewerImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setViewerIndex((viewerIndex - 1 + viewerImages.length) % viewerImages.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 text-xl"
                    >
                      ←
                    </button>
                    <button
                      onClick={() => setViewerIndex((viewerIndex + 1) % viewerImages.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-70 text-xl"
                    >
                      →
                    </button>
                  </>
                )}
                
                {/* Image Counter */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded">
                  {viewerIndex + 1} / {viewerImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Driving License Prompt for Admin Users */}
      <DrivingLicensePrompt user={user} onComplete={fetchAllData} />

      {/* Welcome Guide for New Users (after registration) */}
      {showWelcomeGuide && (
        <div className="fixed inset-0 z-[200]">
          <WelcomeGuide 
            companyName={newCompanyName || companySettings.company_name}
            onComplete={() => setShowWelcomeGuide(false)}
          />
        </div>
      )}

      {/* Help Center Dialog */}
      <Dialog open={showHelpCenter} onOpenChange={setShowHelpCenter}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <HelpCenter onClose={() => setShowHelpCenter(false)} />
        </DialogContent>
      </Dialog>

      {/* Onboarding Tour (shows on first login) */}
      <OnboardingTour user={user} />

      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onOpenChange={setShowChangePasswordDialog}
      />

      {/* Mobile: My Clock Status Dialog */}
      <MyClockStatusDialog
        open={showClockStatusDialog}
        onOpenChange={setShowClockStatusDialog}
      />

      {/* Mobile: Clocked In Now (Company Roster) Dialog */}
      <ClockedInNowDialog
        open={showClockedInNowDialog}
        onOpenChange={setShowClockedInNowDialog}
      />

      {/* My Profile Dialog */}
      <Dialog open={showMyProfileDialog} onOpenChange={setShowMyProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="my-profile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              My Profile
            </DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="personal" data-testid="personal-tab">Personal Info</TabsTrigger>
              <TabsTrigger value="earnings" data-testid="earnings-tab">Earnings</TabsTrigger>
            </TabsList>
            <TabsContent value="personal" className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium">{user?.name || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{user?.email || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Role</p>
                  <p className="font-medium">{user?.role?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '-'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{user?.phone || '-'}</p>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="earnings" className="space-y-4">
              <MyEarnings />
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
}

